/* eslint-disable react/no-unescaped-entities, no-empty */

import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import SquareCardForm from '../components/SquareCardForm';

// Admin Components
import AdminInventoryTab from '../components/admin/AdminInventoryTab';
import AdminCoasTab from '../components/admin/AdminCoasTab';
import AdminArticlesTab from '../components/admin/AdminArticlesTab';
import AdminAIContentEngine from '../components/admin/AdminAIContentEngine';
import AdminAccessoriesTab from '../components/admin/AdminAccessoriesTab';
import AdminResourcesTab from '../components/admin/AdminResourcesTab';
import AffiliatesTab from '../components/admin/AffiliatesTab';
import NewsletterBroadcastModal from '../components/admin/NewsletterBroadcastModal';
import CustomersListPanel from '../components/admin/CustomersListPanel';
import BlocklistPanel from '../components/admin/BlocklistPanel';
import AdminCustomOrdersTab from '../components/admin/AdminCustomOrdersTab';
import AdminWholesaleCatalogTab from '../components/admin/AdminWholesaleCatalogTab';
import AdminLifecycleTab from '../components/admin/AdminLifecycleTab';
import AdminBroadcastTab from '../components/admin/AdminBroadcastTab';
import Base64ImageMigrationBanner from '../components/admin/Base64ImageMigrationBanner';
import SiteSettingsCard from '../components/admin/SiteSettingsCard';
import { 
  Lock, 
  Package, 
  LogOut,
  Search,
  CheckCircle2,
  XCircle,
  RefreshCw,
  AlertTriangle,
  Minus,
  Plus,
  Trash2,
  PlusCircle,
  Upload,
  X,
  Image as ImageIcon,
  FileText,
  Clock,
  Download,
  FileSpreadsheet,
  BookOpen,
  Edit,
  Users,
  UserPlus,
  Tag,
  DollarSign,
  Percent,
  Ticket,
  Ban,
  Copy,
  Mail,
  Phone,
  Building2,
  MessageSquare,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Eye,
  ArrowUp,
  Shield,
  ShoppingBag,
  Bell,
  Check,
  TrendingUp,
  TrendingDown,
  CreditCard,
  Banknote,
  BarChart3,
  Settings,
  Key,
  Printer,
  Send,
  Calendar,
  MapPin,
  Truck,
  Sparkles,
  Award,
  AlertCircle,
  Undo2,
  RotateCcw,
  Store,
  UserX,
  Megaphone
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;


const PickupCalendarManager = ({ adminFetch }) => {
  const [blockedDates, setBlockedDates] = React.useState(new Set());
  const [loading, setLoading] = React.useState(true);
  const [currentMonth, setCurrentMonth] = React.useState(new Date());
  const [allowWeekends, setAllowWeekends] = React.useState(false);
  const [weekendSaving, setWeekendSaving] = React.useState(false);
  const [selectedDate, setSelectedDate] = React.useState(null);
  const [blockedSlots, setBlockedSlots] = React.useState(new Set());
  const [slotsLoading, setSlotsLoading] = React.useState(false);

  const ALL_SLOTS = [
    { start: '09:00', end: '10:00', label: '9:00 AM - 10:00 AM' },
    { start: '10:00', end: '11:00', label: '10:00 AM - 11:00 AM' },
    { start: '11:00', end: '12:00', label: '11:00 AM - 12:00 PM' },
    { start: '12:00', end: '13:00', label: '12:00 PM - 1:00 PM' },
    { start: '13:00', end: '14:00', label: '1:00 PM - 2:00 PM' },
    { start: '14:00', end: '15:00', label: '2:00 PM - 3:00 PM' },
    { start: '15:00', end: '16:00', label: '3:00 PM - 4:00 PM' },
    { start: '16:00', end: '17:00', label: '4:00 PM - 5:00 PM' },
  ];

  React.useEffect(() => { fetchBlocked(); fetchPickupSettings(); }, []);

  const fetchBlocked = async () => {
    try {
      const res = await adminFetch(`${API_URL}/api/admin/pickup-blocked-dates`);
      if (res.ok) {
        const data = await res.json();
        setBlockedDates(new Set(data.map(d => d.date)));
      }
    } catch {}
    setLoading(false);
  };

  const fetchPickupSettings = async () => {
    try {
      const res = await adminFetch(`${API_URL}/api/admin/pickup-settings`);
      if (res.ok) {
        const data = await res.json();
        setAllowWeekends(data.allow_weekends || false);
      }
    } catch {}
  };

  const fetchBlockedSlots = async (dateStr) => {
    setSlotsLoading(true);
    try {
      const res = await adminFetch(`${API_URL}/api/admin/pickup-blocked-slots/${dateStr}`);
      if (res.ok) {
        const data = await res.json();
        setBlockedSlots(new Set(data.map(s => s.slot_start)));
      }
    } catch {}
    setSlotsLoading(false);
  };

  const toggleWeekends = async () => {
    setWeekendSaving(true);
    const newValue = !allowWeekends;
    try {
      const res = await adminFetch(`${API_URL}/api/admin/pickup-settings`, {
        method: 'PUT',
        body: JSON.stringify({ allow_weekends: newValue })
      });
      if (res.ok) {
        setAllowWeekends(newValue);
        toast.success(newValue ? 'Weekend pickups enabled' : 'Weekend pickups disabled');
      }
    } catch { toast.error('Failed to update setting'); }
    setWeekendSaving(false);
  };

  const toggleDate = async (dateStr) => {
    const isBlocked = blockedDates.has(dateStr);
    try {
      if (isBlocked) {
        const res = await adminFetch(`${API_URL}/api/admin/pickup-blocked-dates/${dateStr}`, { method: 'DELETE' });
        if (res.ok) {
          setBlockedDates(prev => { const n = new Set(prev); n.delete(dateStr); return n; });
          if (selectedDate === dateStr) setSelectedDate(null);
        }
      } else {
        const res = await adminFetch(`${API_URL}/api/admin/pickup-blocked-dates`, {
          method: 'POST', body: JSON.stringify({ dates: [dateStr] })
        });
        if (res.ok) {
          setBlockedDates(prev => new Set([...prev, dateStr]));
          if (selectedDate === dateStr) setSelectedDate(null);
        }
      }
    } catch {}
  };

  const toggleSlot = async (slotStart) => {
    if (!selectedDate) return;
    const isBlocked = blockedSlots.has(slotStart);
    try {
      if (isBlocked) {
        const res = await adminFetch(`${API_URL}/api/admin/pickup-blocked-slots/${selectedDate}/${slotStart}`, { method: 'DELETE' });
        if (res.ok) setBlockedSlots(prev => { const n = new Set(prev); n.delete(slotStart); return n; });
      } else {
        const res = await adminFetch(`${API_URL}/api/admin/pickup-blocked-slots`, {
          method: 'POST', body: JSON.stringify({ date: selectedDate, slots: [slotStart] })
        });
        if (res.ok) setBlockedSlots(prev => new Set([...prev, slotStart]));
      }
    } catch {}
  };

  const handleDateClick = (dateStr, isBlocked) => {
    if (selectedDate === dateStr) {
      setSelectedDate(null);
      return;
    }
    if (!isBlocked) {
      setSelectedDate(dateStr);
      fetchBlockedSlots(dateStr);
    }
  };

  const handleDateRightClick = (e, dateStr) => {
    e.preventDefault();
    toggleDate(dateStr);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return { firstDay, daysInMonth, year, month };
  };

  const { firstDay, daysInMonth, year, month } = getDaysInMonth(currentMonth);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-blue-600" />
          Pickup Calendar - Manage Availability
        </CardTitle>
        <p className="text-sm text-slate-500">Click a date to manage time slots. Right-click to block/unblock an entire day.</p>
      </CardHeader>
      <CardContent>
        {/* Weekend Toggle */}
        <div className="flex items-center justify-between p-3 mb-4 rounded-lg border border-slate-200 bg-slate-50" data-testid="weekend-toggle-section">
          <div>
            <p className="text-sm font-medium text-slate-700">Allow Weekend Pickups</p>
            <p className="text-xs text-slate-500">Enable Saturday & Sunday for customer pickup scheduling</p>
          </div>
          <button
            onClick={toggleWeekends}
            disabled={weekendSaving}
            data-testid="weekend-toggle-btn"
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              allowWeekends ? 'bg-teal-600' : 'bg-slate-300'
            } ${weekendSaving ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              allowWeekends ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-slate-500">Loading...</p>
        ) : (
          <div className="flex gap-4">
            {/* Calendar */}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-4">
                <button onClick={() => setCurrentMonth(new Date(year, month - 1, 1))} className="p-1.5 hover:bg-slate-100 rounded">
                  <ChevronUp className="h-4 w-4 rotate-[-90deg]" />
                </button>
                <span className="font-heading font-semibold text-slate-900">{monthNames[month]} {year}</span>
                <button onClick={() => setCurrentMonth(new Date(year, month + 1, 1))} className="p-1.5 hover:bg-slate-100 rounded">
                  <ChevronUp className="h-4 w-4 rotate-90" />
                </button>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-slate-500 mb-1">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => <div key={d} className="py-1">{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: firstDay }).map((_, i) => <div key={`empty-${i}`} />)}
                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const day = i + 1;
                  const date = new Date(year, month, day);
                  const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                  const isPast = date < today;
                  const isBlocked = blockedDates.has(dateStr);
                  const isToday = date.toDateString() === today.toDateString();
                  const isWeekendDisabled = isWeekend && !allowWeekends;
                  const isSelected = selectedDate === dateStr;

                  return (
                    <button
                      key={day}
                      onClick={() => !isWeekendDisabled && !isPast && handleDateClick(dateStr, isBlocked)}
                      onContextMenu={(e) => !isWeekendDisabled && !isPast && handleDateRightClick(e, dateStr)}
                      disabled={isWeekendDisabled || isPast}
                      className={`p-2 text-sm rounded-md transition-all ${
                        isWeekendDisabled ? 'bg-slate-50 text-slate-300 cursor-not-allowed' :
                        isPast ? 'text-slate-300 cursor-not-allowed' :
                        isBlocked ? 'bg-red-100 text-red-700 hover:bg-red-200 font-bold ring-1 ring-red-300' :
                        isSelected ? 'bg-blue-600 text-white font-bold ring-2 ring-blue-400' :
                        isToday ? 'bg-teal-100 text-teal-800 hover:bg-teal-200 font-bold' :
                        isWeekend ? 'bg-blue-50 text-blue-700 hover:bg-blue-100' :
                        'hover:bg-slate-100 text-slate-700'
                      }`}
                      data-testid={`calendar-day-${dateStr}`}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
              <div className="flex flex-wrap items-center gap-3 mt-4 text-xs text-slate-500">
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-100 ring-1 ring-red-300" /> Day blocked</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-600" /> Selected</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-teal-100" /> Today</span>
                {allowWeekends ? (
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-50" /> Weekend</span>
                ) : (
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-slate-50" /> Weekend (off)</span>
                )}
              </div>
            </div>

            {/* Time Slot Panel */}
            {selectedDate && !blockedDates.has(selectedDate) && (
              <div className="w-56 border-l border-slate-200 pl-4" data-testid="slot-panel">
                <p className="text-sm font-semibold text-slate-800 mb-1">{selectedDate}</p>
                <p className="text-xs text-slate-500 mb-3">Click slots to block/unblock</p>
                {slotsLoading ? (
                  <p className="text-xs text-slate-400">Loading slots...</p>
                ) : (
                  <div className="space-y-1.5">
                    {ALL_SLOTS.map(slot => {
                      const isSlotBlocked = blockedSlots.has(slot.start);
                      return (
                        <button
                          key={slot.start}
                          onClick={() => toggleSlot(slot.start)}
                          data-testid={`slot-${selectedDate}-${slot.start}`}
                          className={`w-full text-left text-xs px-2.5 py-2 rounded-md transition-all border ${
                            isSlotBlocked
                              ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
                              : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          <span className="flex items-center justify-between">
                            <span>{slot.label}</span>
                            {isSlotBlocked && <span className="text-red-500 font-bold">×</span>}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
                <button
                  onClick={() => setSelectedDate(null)}
                  className="mt-3 text-xs text-slate-500 hover:text-slate-700 underline"
                  data-testid="close-slot-panel"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        )}

        {/* Blocked dates summary */}
        {blockedDates.size > 0 && (
          <div className="mt-4 pt-3 border-t border-slate-100">
            <p className="text-xs font-medium text-slate-500 mb-2">Blocked dates ({blockedDates.size}):</p>
            <div className="flex flex-wrap gap-1">
              {[...blockedDates].sort().map(d => (
                <span key={d} className="inline-flex items-center gap-1 text-xs bg-red-50 text-red-700 px-2 py-1 rounded-full">
                  {d}
                  <button onClick={() => toggleDate(d)} className="hover:text-red-900" data-testid={`unblock-${d}`}>×</button>
                </span>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};


const CalendarConnectionManager = ({ adminFetch }) => {
  const [status, setStatus] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const fetchStatus = async () => {
    try {
      const res = await adminFetch(`${API_URL}/api/admin/calendar/status`);
      const data = await res.json();
      setStatus(data);
    } catch { setStatus({ connected: false }); }
    setLoading(false);
  };

  React.useEffect(() => {
    fetchStatus();
    const handler = (e) => { if (e.data?.type === 'calendar_connected') fetchStatus(); };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const handleConnect = async () => {
    try {
      const res = await adminFetch(`${API_URL}/api/admin/calendar/connect`);
      const data = await res.json();
      if (data.authorization_url) {
        window.open(data.authorization_url, 'google_calendar_auth', 'width=600,height=700,scrollbars=yes');
      }
    } catch (e) { toast.error('Failed to start calendar connection'); }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect your Google Calendar?')) return;
    try {
      await adminFetch(`${API_URL}/api/admin/calendar/disconnect`, { method: 'DELETE' });
      setStatus({ connected: false });
      toast.success('Calendar disconnected');
    } catch { toast.error('Failed to disconnect'); }
  };

  if (loading) return <p className="text-sm text-slate-500">Checking calendar connection...</p>;

  return status?.connected ? (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm">
        <div className="h-2 w-2 rounded-full bg-green-500" />
        <span className="text-green-700 font-medium">Connected</span>
        <span className="text-slate-500">({status.email})</span>
      </div>
      <p className="text-xs text-slate-500">
        Connected {status.connected_at ? new Date(status.connected_at).toLocaleDateString() : ''}
      </p>
      <Button variant="outline" size="sm" onClick={handleDisconnect} data-testid="disconnect-calendar-btn" className="text-red-600 border-red-200 hover:bg-red-50">
        Disconnect Calendar
      </Button>
    </div>
  ) : (
    <div className="space-y-3">
      <p className="text-sm text-slate-500">Connect your Genora Google Calendar to automatically block off unavailable dates for customer pickups.</p>
      <Button onClick={handleConnect} data-testid="connect-calendar-btn" className="bg-teal-600 hover:bg-teal-700">
        <Calendar className="h-4 w-4 mr-2" /> Connect Google Calendar
      </Button>
    </div>
  );
};


const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [updatingVariant, setUpdatingVariant] = useState(null);
  const [addingVariant, setAddingVariant] = useState(null); // product_id when adding
  const [newVariant, setNewVariant] = useState({ size: '', price: '', stock_quantity: 50 });
  const [showAddProduct, setShowAddProduct] = useState(false);
  
  // Admin token state (for Authorization header fallback)
  const [adminToken, setAdminToken] = useState(() => localStorage.getItem('adminToken') || '');
  
  // Admin setup state
  const [adminStatus, setAdminStatus] = useState(null);
  const [setupMode, setSetupMode] = useState(false);
  const [setupForm, setSetupForm] = useState({ email: '', password: '', confirmPassword: '', name: '' });
  const [settingUp, setSettingUp] = useState(false);
  
  // Helper function for authenticated admin fetch requests
  const adminFetch = async (url, options = {}) => {
    const token = localStorage.getItem('adminToken');
    const headers = {
      ...options.headers,
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (options.body && typeof options.body === 'string') {
      headers['Content-Type'] = 'application/json';
    }
    return fetch(url, {
      ...options,
      headers,
      
    });
  };
  
  // Helper function to get auth headers (for FormData uploads)
  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  };
  
  const [newProduct, setNewProduct] = useState({
    name: '',
    category: 'metabolic',
    description: '',
    purity: '≥99%',
    variant_size: '5mg',
    variant_price: '',
    variant_quantity: '50'
  });
  const [productImage, setProductImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  // Edit Product state
  const [showEditProduct, setShowEditProduct] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editProductForm, setEditProductForm] = useState({
    name: '',
    category: '',
    description: '',
    purity: '',
    variants: [],
    // Sale + member-discount eligibility (Apr 23, 2026)
    sale_active: false,
    sale_type: 'percent',
    sale_value: '',
    sale_starts_at: '',
    sale_ends_at: '',
    member_discount_eligible: true,
    allow_preorder: false
  });
  const [editProductImage, setEditProductImage] = useState(null);
  const [editProductImagePreview, setEditProductImagePreview] = useState(null);
  const editProductImageRef = useRef(null);
  const [editUseExternalUrl, setEditUseExternalUrl] = useState(false);
  const [editExternalImageUrl, setEditExternalImageUrl] = useState('');
  
  // Tab state
  const [activeTab, setActiveTab] = useState('inventory'); // 'inventory', 'coas', 'articles', 'customers', 'sales', 'settings'
  
  // Settings state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Sales Dashboard state
  const [salesData, setSalesData] = useState(null);
  const [salesLoading, setSalesLoading] = useState(false);
  const [atRiskOpen, setAtRiskOpen] = useState(false);
  const [remindingId, setRemindingId] = useState(null);
  const [salesDateRange, setSalesDateRange] = useState({ start: '', end: '', preset: 'all' });
  const [salesPage, setSalesPage] = useState(1);
  const [salesSearch, setSalesSearch] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);
  const [statusModal, setStatusModal] = useState(null); // { order_id, order_email, current_status, new_status }
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [affiliateRewardMenu, setAffiliateRewardMenu] = useState(null); // order_id of open menu
  const [affiliateCodes, setAffiliateCodes] = useState([]); // list of affiliate codes for reward assignment
  const [expandedOrder, setExpandedOrder] = useState(null); // order_id of expanded row
  const [editingOrder, setEditingOrder] = useState(null); // order_id being edited
  const [editForm, setEditForm] = useState(null); // { items, discount_amount, discount_label, shipping_cost, adjust_inventory }
  const [savingEdit, setSavingEdit] = useState(false);
  
  // COA management state
  const [coas, setCoas] = useState([]);
  const [showCoaModal, setShowCoaModal] = useState(null); // product_id when editing
  const [coaForm, setCoaForm] = useState({ batch_number: '', purity: '', test_date: '' });
  const [coaPdf, setCoaPdf] = useState(null);
  const [coaUseUrl, setCoaUseUrl] = useState(false);
  const [coaUrl, setCoaUrl] = useState('');
  const coaFileRef = useRef(null);
  
  // CSV import state
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvImportResult, setCsvImportResult] = useState(null);
  const csvFileRef = useRef(null);
  
  // Articles/Blog state
  const [articles, setArticles] = useState([]);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleForm, setArticleForm] = useState({ 
    title: '', 
    excerpt: '', 
    content: '', 
    author: 'Genora Research Team',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    status: 'published',
    scheduled_at: '',
    notify_on_publish: false,
    useImageUrl: false,
    imageUrl: ''
  });
  const [articleImage, setArticleImage] = useState(null);
  const [articleImagePreview, setArticleImagePreview] = useState(null);
  const articleImageRef = useRef(null);
  
  // Staff Management state
  const [staffMembers, setStaffMembers] = useState([]);
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [staffForm, setStaffForm] = useState({ name: '', email: '', password: '', role: 'inventory_manager', fromCustomer: false, customerId: '' });
  
  // Invoice Resend state
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceOrder, setInvoiceOrder] = useState(null);
  const [invoiceEmails, setInvoiceEmails] = useState({ cc: '', bcc: '' });
  const [sendingInvoice, setSendingInvoice] = useState(false);
  
  // Report state
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportDateRange, setReportDateRange] = useState({ start: '', end: '' });
  const [reportData, setReportData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  
  // Customer Management state
  const [customers, setCustomers] = useState([]);
  const [pricingTiers, setPricingTiers] = useState([]);
  const [showVisibilityModal, setShowVisibilityModal] = useState(false);
  const [visibilityProduct, setVisibilityProduct] = useState(null);
  const [selectedTiers, setSelectedTiers] = useState([]);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [customerForm, setCustomerForm] = useState({
    name: '', email: '', phone: '', company: '', pricing_tier_id: '', affiliate_code_id: '', tags: '', notes: '', status: 'active', allow_local_pickup: false, allow_cash_payment: false, allow_consent_send: false, default_cc_emails: '',
    address_line1: '', address_line2: '', city: '', state: '', zip_code: '', country: 'US'
  });
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [customerConsentForms, setCustomerConsentForms] = useState({});
  const [showTierModal, setShowTierModal] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [tierForm, setTierForm] = useState({
    name: '', description: '', discount_type: 'percentage', discount_value: '', is_active: true
  });
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [codeForm, setCodeForm] = useState({
    code: '', description: '', discount_type: 'percentage', discount_value: '', usage_limit: '', expires_at: '', created_for: '', assigned_customer_email: '', assigned_customer_name: ''
  });
  const [customerTab, setCustomerTab] = useState('customers'); // 'customers', 'tiers', 'codes'
  
  // Bulk management (Apr 22, 2026)
  const [selectedCustomerIds, setSelectedCustomerIds] = useState(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Newsletter broadcast (Apr 22, 2026)
  const [showNewsletterModal, setShowNewsletterModal] = useState(false);
  const [newsletterMode, setNewsletterMode] = useState('compose'); // 'compose' | 'article'
  const [newsletterSubject, setNewsletterSubject] = useState('');
  const [newsletterBody, setNewsletterBody] = useState('');
  const [newsletterArticleId, setNewsletterArticleId] = useState('');
  const [newsletterTestEmail, setNewsletterTestEmail] = useState('');
  const [newsletterSubscriberCount, setNewsletterSubscriberCount] = useState(null);
  const [isSendingNewsletter, setIsSendingNewsletter] = useState(false);
  
  // Password Reset state
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [passwordResetCustomer, setPasswordResetCustomer] = useState(null);
  const [newCustomerPassword, setNewCustomerPassword] = useState('');
  const [confirmCustomerPassword, setConfirmCustomerPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // Email-based Password Reset state (Settings tab)
  const [resetEmail, setResetEmail] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [resettingByEmail, setResettingByEmail] = useState(false);

  // Test Email state
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  // Cash Payment state
  const [showCashPaymentModal, setShowCashPaymentModal] = useState(false);
  // Admin "Record Payment" modal supports two payment methods:
  // - 'cash' (legacy)
  // - 'card' (in-person credit card via Square Web Payments SDK)
  const [cashPaymentMethod, setCashPaymentMethod] = useState('cash');
  // Square token captured from the inline <SquareCardForm/> below
  const [adminSquareToken, setAdminSquareToken] = useState(null);
  const [adminSquareVerification, setAdminSquareVerification] = useState(null);
  const [cashPaymentCustomer, setCashPaymentCustomer] = useState(null);
  const [cashPaymentItems, setCashPaymentItems] = useState([]);
  const [cashPaymentNote, setCashPaymentNote] = useState('');
  const [cashPaymentDiscount, setCashPaymentDiscount] = useState(0);
  const [cashPaymentDiscountType, setCashPaymentDiscountType] = useState('percentage'); // 'percentage' or 'fixed'
  // Store-credit amount the admin wants to apply against the cash order. The
  // backend caps this at min(customer.store_credit, post-discount subtotal),
  // so the frontend just needs to feed the admin's chosen value.
  const [cashPaymentStoreCreditUsed, setCashPaymentStoreCreditUsed] = useState(0);
  const [availableProducts, setAvailableProducts] = useState([]);

  // Preorder state
  const [preorders, setPreorders] = useState([]);
  const [stockNotifications, setStockNotifications] = useState([]);
  const [showPreorderModal, setShowPreorderModal] = useState(false);
  const [preorderCustomer, setPreorderCustomer] = useState(null);
  const [preorderItems, setPreorderItems] = useState([]);
  const [preorderNote, setPreorderNote] = useState('');
  const [preorderDeposit, setPreorderDeposit] = useState('');
  const [preorderPhone, setPreorderPhone] = useState('');

  // Multi-select orders state
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [showPreordersOnly, setShowPreordersOnly] = useState(false);
  const [showWholesaleOnly, setShowWholesaleOnly] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Multi-select products state (Inventory tab)
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [bulkProductActionLoading, setBulkProductActionLoading] = useState(false);

  // Check admin status on mount
  useEffect(() => {
    checkAdminStatus();
  }, []);

  const checkAdminStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/status`);
      const text = await response.clone().text();
      const data = text ? JSON.parse(text) : {};
      setAdminStatus(data);
      if (!data.admin_configured) {
        setSetupMode(true);
      }
    } catch (error) {
      console.error('Failed to check admin status:', error);
    }
  };

  const handleAdminSetup = async (e) => {
    e.preventDefault();
    
    if (setupForm.password !== setupForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (setupForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setSettingUp(true);
    try {
      // Use plain fetch for setup - don't send existing (potentially invalid) auth tokens
      const response = await fetch(`${API_URL}/api/admin/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({
          email: setupForm.email,
          password: setupForm.password,
          name: setupForm.name
        })
      });
      
      // Store ok status before reading body (Safari compatibility)
      const isOk = response.ok;
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = {};
      }
      
      if (!isOk) {
        throw new Error(errorData?.detail || 'Setup failed');
      }
      
      toast.success('Admin account created! You can now login.');
      setSetupMode(false);
      setAdminStatus({ admin_configured: true, auth_mode: 'database' });
      setSetupForm({ email: '', password: '', confirmPassword: '', name: '' });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSettingUp(false);
    }
  };

  // Check if already authenticated
  useEffect(() => {
    // Only attempt to fetch products if we have an admin token
    const adminToken = localStorage.getItem('adminToken');
    if (adminToken) {
      // Try to validate the token by fetching products
      // If it fails with 401, we'll clear the token
      const validateAndFetch = async () => {
        try {
          const response = await fetch(`${API_URL}/api/admin/products`, {
            headers: { 'Authorization': `Bearer ${adminToken}` }
          });
          if (response.ok) {
            const data = await response.json();
            setProducts(data);
            setIsAuthenticated(true);
          } else if (response.status === 401) {
            // Token is invalid/expired, clear it
            localStorage.removeItem('adminToken');
            setAdminToken('');
          }
        } catch (error) {
          console.error('Error validating admin session:', error);
        }
      };
      validateAndFetch();
    }
  }, []);
  
  useEffect(() => {
    if (isAuthenticated && activeTab === 'coas') {
      fetchCoas();
    }
    if (isAuthenticated && activeTab === 'articles') {
      fetchArticles();
    }
    if (isAuthenticated && activeTab === 'customers') {
      fetchCustomers();
      fetchPricingTiers();
      fetchDiscountCodes();
      fetchPreorders();
      fetchStockNotifications();
    }
    if (isAuthenticated && activeTab === 'inventory') {
      fetchPricingTiers(); // Need tiers for visibility settings
      fetchStaff(); // Need staff for access management
    }
    if (isAuthenticated && activeTab === 'sales') {
      fetchSalesData();
    }
  }, [isAuthenticated, activeTab]);

  const fetchSalesData = async (dateRange, pageNum, searchQuery, append, wholesaleOnly) => {
    if (append) setLoadingMore(true); else setSalesLoading(true);
    try {
      const range = dateRange || salesDateRange;
      const pg = pageNum || 1;
      const sq = searchQuery !== undefined ? searchQuery : salesSearch;
      const ws = wholesaleOnly !== undefined ? wholesaleOnly : showWholesaleOnly;
      let url = `${API_URL}/api/admin/sales-dashboard`;
      const params = new URLSearchParams();
      if (range.start) params.set('start_date', range.start);
      if (range.end) params.set('end_date', range.end);
      params.set('page', pg);
      params.set('limit', 25);
      if (sq) params.set('search', sq);
      if (ws) params.set('wholesale_only', 'true');
      url += `?${params}`;
      const response = await adminFetch(url, {  });
      if (response.ok) {
        const data = await response.json();
        // Merge adjustment_orders into recent_orders so excluded orders show in the table
        if (data.adjustment_orders && data.adjustment_orders.length > 0) {
          const existingIds = new Set(data.recent_orders.map(o => o.order_id));
          const missingAdjustments = data.adjustment_orders.filter(o => !existingIds.has(o.order_id));
          if (missingAdjustments.length > 0) {
            data.recent_orders = [...data.recent_orders, ...missingAdjustments]
              .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
          }
        }
        if (append && salesData) {
          data.recent_orders = [...salesData.recent_orders, ...data.recent_orders];
        }
        setSalesData(data);
      }
      // Fetch affiliate codes for reward assignment
      const codesRes = await adminFetch(`${API_URL}/api/admin/discount-codes`);
      if (codesRes.ok) {
        const codes = await codesRes.json();
        setAffiliateCodes(codes.filter(c => c.assigned_customer_email));
      }
    } catch (error) {
      console.error('Error fetching sales data:', error);
    } finally {
      setSalesLoading(false);
      setLoadingMore(false);
    }
  };

  const handleSalesPreset = (preset) => {
    const today = new Date();
    let start = '', end = '';
    
    switch (preset) {
      case 'today':
        start = end = today.toISOString().split('T')[0];
        break;
      case 'yesterday': {
        const y = new Date(today); y.setDate(y.getDate() - 1);
        start = end = y.toISOString().split('T')[0];
        break;
      }
      case 'this_week': {
        const d = new Date(today); d.setDate(d.getDate() - d.getDay());
        start = d.toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        break;
      }
      case 'last_7': {
        const d = new Date(today); d.setDate(d.getDate() - 6);
        start = d.toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        break;
      }
      case 'this_month':
        start = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        break;
      case 'last_month': {
        const lm = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lme = new Date(today.getFullYear(), today.getMonth(), 0);
        start = lm.toISOString().split('T')[0];
        end = lme.toISOString().split('T')[0];
        break;
      }
      case 'last_3_months': {
        const d = new Date(today.getFullYear(), today.getMonth() - 3, 1);
        start = d.toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        break;
      }
      case 'last_6_months': {
        const d = new Date(today.getFullYear(), today.getMonth() - 6, 1);
        start = d.toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        break;
      }
      case 'this_year':
        start = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        break;
      case 'last_year': {
        start = new Date(today.getFullYear() - 1, 0, 1).toISOString().split('T')[0];
        end = new Date(today.getFullYear() - 1, 11, 31).toISOString().split('T')[0];
        break;
      }
      default: // 'all'
        start = '';
        end = '';
    }
    
    const newRange = { start, end, preset };
    setSalesDateRange(newRange);
    fetchSalesData(newRange);
  };

  const fetchStaff = async () => {
    try {
      const response = await adminFetch(`${API_URL}/api/admin/staff`, {  });
      if (response.ok) {
        setStaffMembers(await response.json());
      }
    } catch (error) {
      console.error('Error fetching staff:', error);
    }
  };

  const fetchArticles = async () => {
    try {
      const response = await adminFetch(`${API_URL}/api/admin/articles`, {
        
      });
      if (response.ok) {
        setArticles(await response.json());
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
    }
  };

  const fetchCoas = async () => {
    try {
      const response = await fetch(`${API_URL}/api/coas`);
      if (response.ok) {
        setCoas(await response.json());
      }
    } catch (error) {
      console.error('Error fetching COAs:', error);
    }
  };

  // Customer Management Functions
  const fetchCustomers = async () => {
    try {
      const response = await adminFetch(`${API_URL}/api/admin/customers`, {  });
      if (response.ok) {
        setCustomers(await response.json());
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchPricingTiers = async () => {
    try {
      const response = await adminFetch(`${API_URL}/api/admin/pricing-tiers`, {  });
      if (response.ok) {
        setPricingTiers(await response.json());
      }
    } catch (error) {
      console.error('Error fetching pricing tiers:', error);
    }
  };

  const fetchDiscountCodes = async () => {
    try {
      const response = await adminFetch(`${API_URL}/api/admin/discount-codes`, {  });
      if (response.ok) {
        setDiscountCodes(await response.json());
      }
    } catch (error) {
      console.error('Error fetching discount codes:', error);
    }
  };

  const fetchPreorders = async () => {
    try {
      const response = await adminFetch(`${API_URL}/api/admin/preorders`, {  });
      if (response.ok) {
        setPreorders(await response.json());
      }
    } catch (error) {
      console.error('Error fetching preorders:', error);
    }
  };

  const fetchStockNotifications = async () => {
    try {
      const response = await adminFetch(`${API_URL}/api/admin/stock-notifications`, {  });
      if (response.ok) {
        setStockNotifications(await response.json());
      }
    } catch (error) {
      console.error('Error fetching stock notifications:', error);
    }
  };

  const handleSaveCustomer = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const url = editingCustomer 
        ? `${API_URL}/api/admin/customers/${editingCustomer.customer_id}`
        : `${API_URL}/api/admin/customers`;
      const method = editingCustomer ? 'PUT' : 'POST';
      
      const response = await adminFetch(url, {
        method,
        body: JSON.stringify({
          name: customerForm.name,
          email: customerForm.email,
          phone: customerForm.phone,
          company: customerForm.company,
          tags: customerForm.tags ? customerForm.tags.split(',').map(t => t.trim()) : [],
          pricing_tier_id: customerForm.pricing_tier_id || null,
          affiliate_code_id: customerForm.affiliate_code_id || null,
          notes: customerForm.notes,
          status: customerForm.status,
          allow_local_pickup: customerForm.allow_local_pickup || false,
          allow_cash_payment: customerForm.allow_cash_payment || false,
          allow_consent_send: customerForm.allow_consent_send || false,
          default_cc_emails: customerForm.default_cc_emails ? customerForm.default_cc_emails.split(',').map(e => e.trim()) : [],
          address: {
            address_line1: customerForm.address_line1 || '',
            address_line2: customerForm.address_line2 || '',
            city: customerForm.city || '',
            state: customerForm.state || '',
            zip_code: customerForm.zip_code || '',
            country: customerForm.country || 'US'
          }
        })
      });
      
      if (!response.ok) throw new Error((await response.json()).detail);
      
      await fetchCustomers();
      setShowCustomerModal(false);
      setEditingCustomer(null);
      setCustomerForm({ name: '', email: '', phone: '', company: '', pricing_tier_id: '', affiliate_code_id: '', tags: '', notes: '', status: 'active', allow_local_pickup: false, allow_cash_payment: false, default_cc_emails: '', address_line1: '', address_line2: '', city: '', state: '', zip_code: '', country: 'US' });
      toast.success(editingCustomer ? 'Customer updated' : 'Customer created');
    } catch (error) {
      toast.error(error.message || 'Failed to save customer');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!confirm('Delete this customer?')) return;
    try {
      const response = await adminFetch(`${API_URL}/api/admin/customers/${customerId}`, {
        method: 'DELETE',
        
      });
      if (!response.ok) throw new Error((await response.json()).detail);
      await fetchCustomers();
      toast.success('Customer deleted');
    } catch (error) {
      toast.error(error.message || 'Failed to delete customer');
    }
  };

  const handleBanCustomer = async (customer) => {
    const reason = window.prompt(
      `Ban ${customer.name || customer.email}?\n\nThis blocks them from logging in and ordering, and blocks new signups/orders using their email, name, and known addresses.\n\nOptional reason:`,
      'Violated store policy'
    );
    if (reason === null) return; // cancelled
    try {
      const response = await adminFetch(`${API_URL}/api/admin/customers/${customer.customer_id}/ban`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason || 'Banned by admin' }),
      });
      if (!response.ok) throw new Error((await response.json()).detail);
      const data = await response.json();
      await fetchCustomers();
      toast.success(`${data.message} (${data.blocklist_entries} blocklist entries)`);
    } catch (error) {
      toast.error(error.message || 'Failed to ban customer');
    }
  };

  const handleUnbanCustomer = async (customer) => {
    if (!window.confirm(`Unban ${customer.name || customer.email}? They'll be able to log in and order again.`)) return;
    try {
      const response = await adminFetch(`${API_URL}/api/admin/customers/${customer.customer_id}/unban`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!response.ok) throw new Error((await response.json()).detail);
      await fetchCustomers();
      toast.success('Customer unbanned');
    } catch (error) {
      toast.error(error.message || 'Failed to unban customer');
    }
  };


  const handleResetCustomerPassword = async (e) => {
    e.preventDefault();
    
    if (newCustomerPassword !== confirmCustomerPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newCustomerPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setResettingPassword(true);
    try {
      // Try reset first
      const response = await adminFetch(`${API_URL}/api/admin/customers/${passwordResetCustomer.customer_id}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ new_password: newCustomerPassword })
      });
      
      let data;
      try {
        const text = await response.text();
        data = text ? JSON.parse(text) : {};
      } catch {
        throw new Error(`Server error (${response.status}). Please try again.`);
      }
      
      if (!response.ok) {
        // If no user account, try creating one
        if (response.status === 404 && data?.detail?.includes('No user account')) {
          const createRes = await adminFetch(`${API_URL}/api/admin/customers/${passwordResetCustomer.customer_id}/create-account`, {
            method: 'POST',
            body: JSON.stringify({ new_password: newCustomerPassword })
          });
          let createData;
          try {
            const text = await createRes.text();
            createData = text ? JSON.parse(text) : {};
          } catch {
            throw new Error(`Server error (${createRes.status}). Please try again.`);
          }
          if (!createRes.ok) throw new Error(createData?.detail || 'Failed to create account');
          toast.success(`Account created & password set for ${passwordResetCustomer.email}`);
        } else {
          throw new Error(data?.detail || 'Failed to reset password');
        }
      } else {
        toast.success(`Password reset for ${data.user_email}`);
      }
      
      setShowPasswordResetModal(false);
      setPasswordResetCustomer(null);
      setNewCustomerPassword('');
      setConfirmCustomerPassword('');
      await fetchCustomers();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setResettingPassword(false);
    }
  };

  const handleResetPasswordByEmail = async (e) => {
    e.preventDefault();
    
    if (!resetEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    if (resetNewPassword !== resetConfirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (resetNewPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setResettingByEmail(true);
    try {
      const response = await adminFetch(`${API_URL}/api/admin/reset-user-password`, {
        method: 'POST',
        body: JSON.stringify({ 
          email: resetEmail.trim().toLowerCase(),
          new_password: resetNewPassword 
        })
      });
      
      const isOk = response.ok;
      let data;
      try {
        const text = await response.text();
        data = text ? JSON.parse(text) : {};
      } catch {
        throw new Error(`Server error (${response.status}). Please try again.`);
      }
      
      if (!isOk) {
        throw new Error(data?.detail || 'Failed to reset password');
      }
      
      toast.success(`Password reset for ${data.user_name} (${data.user_email})`);
      setResetEmail('');
      setResetNewPassword('');
      setResetConfirmPassword('');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setResettingByEmail(false);
    }
  };

  const handleSendTestEmail = async (e) => {
    e.preventDefault();
    
    if (!testEmailAddress.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    setSendingTestEmail(true);
    try {
      const response = await adminFetch(`${API_URL}/api/admin/test-email`, {
        method: 'POST',
        body: JSON.stringify({ email: testEmailAddress.trim().toLowerCase() })
      });
      
      const isOk = response.ok;
      const data = await response.json();
      
      if (!isOk) {
        throw new Error(data?.detail || 'Failed to send test email');
      }
      
      toast.success(`Test email sent to ${testEmailAddress}! Check your inbox (and spam folder).`);
      setTestEmailAddress('');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSendingTestEmail(false);
    }
  };

  const handleSaveTier = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const url = editingTier 
        ? `${API_URL}/api/admin/pricing-tiers/${editingTier.tier_id}`
        : `${API_URL}/api/admin/pricing-tiers`;
      const method = editingTier ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify(tierForm)
      });
      
      if (!response.ok) throw new Error((await response.json()).detail);
      
      await fetchPricingTiers();
      setShowTierModal(false);
      setEditingTier(null);
      setTierForm({ name: '', description: '', discount_type: 'percentage', discount_value: '', is_active: true });
      toast.success(editingTier ? 'Pricing tier updated' : 'Pricing tier created');
    } catch (error) {
      toast.error(error.message || 'Failed to save pricing tier');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTier = async (tierId) => {
    if (!confirm('Delete this pricing tier? Customers assigned to it will have their tier removed.')) return;
    try {
      const response = await adminFetch(`${API_URL}/api/admin/pricing-tiers/${tierId}`, {
        method: 'DELETE',
        
      });
      if (!response.ok) throw new Error((await response.json()).detail);
      await fetchPricingTiers();
      await fetchCustomers();
      toast.success('Pricing tier deleted');
    } catch (error) {
      toast.error(error.message || 'Failed to delete pricing tier');
    }
  };

  const handleSaveCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await adminFetch(`${API_URL}/api/admin/discount-codes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({
          ...codeForm,
          usage_limit: codeForm.usage_limit ? parseInt(codeForm.usage_limit) : null,
          expires_at: codeForm.expires_at || null
        })
      });
      
      if (!response.ok) throw new Error((await response.json()).detail);
      
      const data = await response.json();
      await fetchDiscountCodes();
      setShowCodeModal(false);
      setCodeForm({ code: '', description: '', discount_type: 'percentage', discount_value: '', usage_limit: '', expires_at: '', created_for: '' });
      toast.success(`Discount code ${data.code.code} created`);
    } catch (error) {
      toast.error(error.message || 'Failed to create discount code');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleOncePerCustomer = async (code) => {
    try {
      const response = await adminFetch(`${API_URL}/api/admin/discount-codes/${code.code_id}`, {
        method: 'PUT',
        body: JSON.stringify({ once_per_customer: !code.once_per_customer }),
      });
      if (!response.ok) throw new Error((await response.json()).detail);
      await fetchDiscountCodes();
      toast.success(!code.once_per_customer ? `${code.code} is now one use per customer` : `${code.code} can be reused`);
    } catch (error) {
      toast.error(error.message || 'Failed to update code');
    }
  };

  const handleDeleteCode = async (codeId) => {
    if (!confirm('Delete this discount code?')) return;
    try {
      const response = await adminFetch(`${API_URL}/api/admin/discount-codes/${codeId}`, {
        method: 'DELETE',
        
      });
      if (!response.ok) throw new Error((await response.json()).detail);
      await fetchDiscountCodes();
      toast.success('Discount code deleted');
    } catch (error) {
      toast.error(error.message || 'Failed to delete discount code');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const openCustomerModal = (customer = null) => {
    if (customer) {
      setEditingCustomer(customer);
      const addr = customer.address || {};
      // Find if any discount code is assigned to this customer
      const assignedCode = discountCodes.find(c => c.assigned_customer_email && c.assigned_customer_email.toLowerCase() === (customer.email || '').toLowerCase());
      setCustomerForm({
        name: customer.name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        company: customer.company || '',
        pricing_tier_id: customer.pricing_tier_id || '',
        affiliate_code_id: assignedCode ? assignedCode.code_id : '',
        tags: (customer.tags || []).join(', '),
        notes: customer.notes || '',
        status: customer.status || 'active',
        allow_local_pickup: customer.allow_local_pickup || false,
        allow_cash_payment: customer.allow_cash_payment || false,
        allow_consent_send: customer.allow_consent_send || false,
        default_cc_emails: (customer.default_cc_emails || []).join(', '),
        address_line1: addr.address_line1 || '',
        address_line2: addr.address_line2 || '',
        city: addr.city || '',
        state: addr.state || '',
        zip_code: addr.zip_code || '',
        country: addr.country || 'US'
      });
    } else {
      setEditingCustomer(null);
      setCustomerForm({ name: '', email: '', phone: '', company: '', pricing_tier_id: '', affiliate_code_id: '', tags: '', notes: '', status: 'active', allow_local_pickup: false, allow_cash_payment: false, default_cc_emails: '', address_line1: '', address_line2: '', city: '', state: '', zip_code: '', country: 'US' });
    }
    setShowCustomerModal(true);
  };

  const openTierModal = (tier = null) => {
    if (tier) {
      setEditingTier(tier);
      setTierForm({
        name: tier.name || '',
        description: tier.description || '',
        discount_type: tier.discount_type || 'percentage',
        discount_value: tier.discount_value || '',
        is_active: tier.is_active !== false
      });
    } else {
      setEditingTier(null);
      setTierForm({ name: '', description: '', discount_type: 'percentage', discount_value: '', is_active: true });
    }
    setShowTierModal(true);
  };

  // Cash Payment Functions
  const openCashPaymentModal = async (customer) => {
    setCashPaymentCustomer(customer);
    setCashPaymentItems([]);
    setCashPaymentNote('');
    setCashPaymentStoreCreditUsed(0);
    
    // Auto-apply tier discount if customer has a pricing tier
    if (customer?.pricing_tier_id) {
      const customerTier = pricingTiers.find(t => t.tier_id === customer.pricing_tier_id);
      if (customerTier && customerTier.discount_value) {
        setCashPaymentDiscount(parseFloat(customerTier.discount_value) || 0);
        setCashPaymentDiscountType(customerTier.discount_type || 'percentage');
      } else {
        setCashPaymentDiscount(0);
        setCashPaymentDiscountType('percentage');
      }
    } else {
      setCashPaymentDiscount(0);
      setCashPaymentDiscountType('percentage');
    }
    
    // Get peptides with available stock
    const peptidesWithStock = products
      .filter(p => p.variants?.some(v => v.in_stock && v.stock_quantity > 0))
      .map(p => ({ ...p, product_type: 'peptide' }));

    // Fetch shop products with available stock and merge
    let shopWithStock = [];
    try {
      const res = await adminFetch(`${API_URL}/api/admin/shop/products`);
      if (res.ok) {
        const shopProducts = await res.json();
        shopWithStock = shopProducts
          .filter(sp => {
            if (sp.has_pack_sizes) {
              return (sp.pack_sizes || []).some(ps => (ps.stock_quantity || 0) > 0);
            }
            if (sp.has_colors) {
              return (sp.colors || []).some(c => (c.stock_quantity || 0) > 0);
            }
            return sp.in_stock && (sp.stock_quantity || 0) > 0;
          })
          .map(sp => ({ ...sp, product_type: 'shop' }));
      }
    } catch (e) {
      // Non-fatal: peptide-only list still works
      console.warn('Could not fetch shop products for cash modal', e);
    }

    setAvailableProducts([...peptidesWithStock, ...shopWithStock]);
    setShowCashPaymentModal(true);
  };

  const addCashPaymentItem = () => {
    setCashPaymentItems([...cashPaymentItems, { 
      product_id: '', 
      variant_index: 0,
      pack_size_index: 0,
      color_index: 0,
      quantity: 1,
      product: null,
      product_type: 'peptide',
    }]);
  };

  const updateCashPaymentItem = (index, field, value) => {
    const updated = [...cashPaymentItems];
    updated[index][field] = value;
    
    // If product changed, reset variant indices AND auto-pick the first
    // IN-STOCK variant/pack/color so the admin never lands on a default
    // selection that's actually out of stock (which previously caused the
    // backend to 400 with "Insufficient stock" on submit).
    if (field === 'product_id') {
      const product = availableProducts.find(p => p.product_id === value);
      updated[index].product = product;
      updated[index].pack_size_index = 0;
      updated[index].color_index = 0;
      updated[index].product_type = product?.product_type || 'peptide';

      const productType = product?.product_type || 'peptide';
      if (productType === 'shop') {
        if (product?.has_pack_sizes) {
          const firstPack = (product.pack_sizes || []).findIndex(
            (ps) => (ps.stock_quantity || 0) > 0,
          );
          updated[index].pack_size_index = firstPack >= 0 ? firstPack : 0;
        } else if (product?.has_colors) {
          const firstColor = (product.colors || []).findIndex(
            (c) => (c.stock_quantity || 0) > 0,
          );
          updated[index].color_index = firstColor >= 0 ? firstColor : 0;
        }
        updated[index].variant_index = 0;
      } else {
        const firstInStock = (product?.variants || []).findIndex(
          (v) => v.in_stock && (v.stock_quantity || 0) > 0,
        );
        updated[index].variant_index = firstInStock >= 0 ? firstInStock : 0;
      }
    }
    
    setCashPaymentItems(updated);
  };

  const removeCashPaymentItem = (index) => {
    setCashPaymentItems(cashPaymentItems.filter((_, i) => i !== index));
  };

  // Unified unit-price resolver: peptide variants, shop simple/pack/color (Apr 22, 2026)
  const getCashItemUnitPrice = (item) => {
    if (!item.product) return 0;
    if (item.product_type === 'shop') {
      const sp = item.product;
      if (sp.has_pack_sizes) {
        return parseFloat(sp.pack_sizes?.[item.pack_size_index]?.price) || 0;
      }
      // Colors and simple both use base_price
      return parseFloat(sp.base_price) || 0;
    }
    return parseFloat(item.product.variants?.[item.variant_index]?.price) || 0;
  };

  const getCashItemVariantLabel = (item) => {
    if (!item.product) return '';
    if (item.product_type === 'shop') {
      const sp = item.product;
      if (sp.has_pack_sizes) return sp.pack_sizes?.[item.pack_size_index]?.name || `Pack ${(item.pack_size_index || 0) + 1}`;
      if (sp.has_colors) return sp.colors?.[item.color_index]?.name || `Color ${(item.color_index || 0) + 1}`;
      return 'Standard';
    }
    return item.product.variants?.[item.variant_index]?.size || '';
  };

  const getCashItemAvailableStock = (item) => {
    if (!item.product) return 0;
    if (item.product_type === 'shop') {
      const sp = item.product;
      if (sp.has_pack_sizes) return parseInt(sp.pack_sizes?.[item.pack_size_index]?.stock_quantity) || 0;
      if (sp.has_colors) return parseInt(sp.colors?.[item.color_index]?.stock_quantity) || 0;
      return parseInt(sp.stock_quantity) || 0;
    }
    return parseInt(item.product.variants?.[item.variant_index]?.stock_quantity) || 0;
  };

  const calculateCashPaymentSubtotal = () => {
    return cashPaymentItems.reduce((total, item) => {
      const unitPrice = getCashItemUnitPrice(item);
      return total + (unitPrice * (item.quantity || 0));
    }, 0);
  };

  const calculateCashPaymentDiscountAmount = () => {
    const subtotal = calculateCashPaymentSubtotal();
    if (cashPaymentDiscountType === 'percentage') {
      return subtotal * (cashPaymentDiscount / 100);
    } else {
      return Math.min(cashPaymentDiscount, subtotal); // Fixed amount, max is subtotal
    }
  };

  const calculateCashPaymentTotal = () => {
    const post = calculateCashPaymentSubtotal() - calculateCashPaymentDiscountAmount();
    // Mirrors the server-side cap so the admin sees the same balance the
    // backend will charge. Floored at 0 — no negative orders.
    const creditCap = Math.max(0, Math.min(
      Number(cashPaymentStoreCreditUsed) || 0,
      Number(cashPaymentCustomer?.store_credit) || 0,
      post,
    ));
    return Math.max(0, post - creditCap);
  };

  // Effective store credit applied to THIS order (post all caps). Reused by
  // both the UI summary and the submit payload so the two never disagree.
  const effectiveCashPaymentStoreCredit = () => {
    const post = calculateCashPaymentSubtotal() - calculateCashPaymentDiscountAmount();
    return Math.max(0, Math.min(
      Number(cashPaymentStoreCreditUsed) || 0,
      Number(cashPaymentCustomer?.store_credit) || 0,
      post,
    ));
  };

  const handleRecordCashPayment = async () => {
    if (cashPaymentItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    // Validate all items have products selected
    const invalidItems = cashPaymentItems.filter(item => !item.product_id);
    if (invalidItems.length > 0) {
      toast.error('Please select a product for all items');
      return;
    }

    // Client-side stock guard. Previously the OOS option was visually
    // disabled in the variant dropdown but still kept as the selected value
    // when the first variant happened to be out of stock — so admins could
    // hit "Record Payment" on an OOS line and only learn about it from the
    // backend's HTTP 400. We now reject with a clear, actionable toast.
    const oosLine = cashPaymentItems.find((item) => {
      if (!item.product) return false;
      const requested = parseInt(item.quantity, 10) || 0;
      const available = getCashItemAvailableStock(item);
      return requested > available;
    });
    if (oosLine) {
      const label = `${oosLine.product?.name || 'Item'} (${getCashItemVariantLabel(oosLine) || 'variant'})`;
      const available = getCashItemAvailableStock(oosLine);
      toast.error(
        available <= 0
          ? `${label} is out of stock — pick a different size or remove this line.`
          : `Only ${available} available for ${label} — adjust the quantity.`,
      );
      return;
    }

    setLoading(true);
    try {
      const orderItems = cashPaymentItems.map(item => {
        const base = {
          product_id: item.product_id,
          product_type: item.product_type || 'peptide',
          quantity: item.quantity,
          price: getCashItemUnitPrice(item),
          size: getCashItemVariantLabel(item),
        };
        if (item.product_type === 'shop') {
          if (item.product?.has_pack_sizes) base.pack_size_index = item.pack_size_index || 0;
          if (item.product?.has_colors) base.color_index = item.color_index || 0;
        } else {
          base.variant_index = item.variant_index || 0;
        }
        return base;
      });

      const subtotal = calculateCashPaymentSubtotal();
      const discountAmount = calculateCashPaymentDiscountAmount();
      const total = calculateCashPaymentTotal();

      // Card flow: tokenize FIRST via SquareCardForm (sets adminSquareToken).
      // If admin selected 'card' but hasn't tapped the Square pay button yet,
      // bail with a clear hint.
      if (cashPaymentMethod === 'card' && total > 0 && !adminSquareToken) {
        toast.error('Please complete the card details and tap "Pay" first.');
        setLoading(false);
        return;
      }

      const response = await adminFetch(`${API_URL}/api/admin/cash-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({
          customer_id: cashPaymentCustomer?.customer_id,
          items: orderItems,
          subtotal: subtotal,
          discount_amount: discountAmount,
          discount_type: cashPaymentDiscountType,
          discount_value: cashPaymentDiscount,
          store_credit_used: effectiveCashPaymentStoreCredit(),
          total: total,
          note: cashPaymentNote,
          payment_method: cashPaymentMethod,
          ...(cashPaymentMethod === 'card' ? {
            source_id: adminSquareToken,
            verification_token: adminSquareVerification,
          } : {})
        })
      });

      // Store ok status before reading body (Safari compatibility)
      const isOk = response.ok;
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = {};
      }

      if (!isOk) {
        // Surface the backend's detail message (with HTTP status as a fallback
        // hint) so the admin can tell *why* the payment was rejected — stock
        // shortage, missing customer, no credit available, etc. Previously
        // this always read "Failed to record payment" which gave us nothing
        // to diagnose with.
        const detail = errorData?.detail
          || (typeof errorData === 'string' ? errorData : null)
          || `Server returned HTTP ${response.status}`;
        throw new Error(detail);
      }

      toast.success(cashPaymentMethod === 'card' ? 'Card payment recorded' : 'Cash payment recorded and inventory updated');
      setShowCashPaymentModal(false);
      setCashPaymentMethod('cash');
      setAdminSquareToken(null);
      setAdminSquareVerification(null);
      fetchProducts(); // Refresh inventory
      fetchCustomers(); // Refresh customer data
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Preorder Functions
  const openPreorderModal = (customer = null) => {
    setPreorderCustomer(customer);
    setPreorderItems([]);
    setPreorderNote('');
    setPreorderDeposit('');
    setPreorderPhone(customer?.phone || '');
    setShowPreorderModal(true);
  };

  const addPreorderItem = () => {
    setPreorderItems([...preorderItems, { 
      product_id: '', 
      variant_index: 0, 
      quantity: 1,
      product: null 
    }]);
  };

  const updatePreorderItem = (index, field, value) => {
    const updated = [...preorderItems];
    updated[index][field] = value;
    
    if (field === 'product_id') {
      const product = products.find(p => p.product_id === value);
      updated[index].product = product;
      updated[index].variant_index = 0;
    }
    
    setPreorderItems(updated);
  };

  const removePreorderItem = (index) => {
    setPreorderItems(preorderItems.filter((_, i) => i !== index));
  };

  const calculatePreorderTotal = () => {
    return preorderItems.reduce((total, item) => {
      if (item.product && item.product.variants?.[item.variant_index]) {
        const variant = item.product.variants[item.variant_index];
        return total + (variant.price * item.quantity);
      }
      return total;
    }, 0);
  };

  const handleCreatePreorder = async () => {
    if (preorderItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    const invalidItems = preorderItems.filter(item => !item.product_id);
    if (invalidItems.length > 0) {
      toast.error('Please select a product for all items');
      return;
    }

    setLoading(true);
    try {
      const orderItems = preorderItems.map(item => ({
        product_id: item.product_id,
        variant_index: item.variant_index,
        quantity: item.quantity
      }));

      const response = await adminFetch(`${API_URL}/api/admin/preorders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({
          customer_id: preorderCustomer?.customer_id,
          customer_name: preorderCustomer?.name || '',
          customer_email: preorderCustomer?.email || '',
          customer_phone: preorderPhone,
          items: orderItems,
          deposit_amount: parseFloat(preorderDeposit) || 0,
          note: preorderNote
        })
      });

      // Store ok status before reading body (Safari compatibility)
      const isOk = response.ok;
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = {};
      }

      if (!isOk) {
        throw new Error(errorData?.detail || 'Failed to create preorder');
      }

      toast.success('Preorder created successfully');
      setShowPreorderModal(false);
      fetchPreorders();
      fetchCustomers();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePreorderStatus = async (preorderId, newStatus) => {
    try {
      const response = await adminFetch(`${API_URL}/api/admin/preorders/${preorderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) throw new Error('Failed to update');
      toast.success('Preorder status updated');
      fetchPreorders();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleFulfillPreorder = async (preorderId) => {
    if (!confirm('This will deduct items from inventory. Continue?')) return;
    
    setLoading(true);
    try {
      const response = await adminFetch(`${API_URL}/api/admin/preorders/${preorderId}/fulfill`, {
        method: 'POST',
        
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to fulfill preorder');
      }

      toast.success('Preorder fulfilled and inventory updated');
      fetchPreorders();
      fetchProducts();
      fetchCustomers();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePreorder = async (preorderId) => {
    if (!confirm('Are you sure you want to delete this preorder?')) return;
    
    try {
      const response = await adminFetch(`${API_URL}/api/admin/preorders/${preorderId}`, {
        method: 'DELETE',
        
      });

      if (!response.ok) throw new Error('Failed to delete');
      toast.success('Preorder deleted');
      fetchPreorders();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      const response = await adminFetch(`${API_URL}/api/admin/stock-notifications/${notificationId}`, {
        method: 'DELETE',
        
      });

      if (!response.ok) throw new Error('Failed to delete');
      toast.success('Notification request deleted');
      fetchStockNotifications();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const filteredCustomers = customers.filter(c => 
    (c.name || '').toLowerCase().includes(customerSearch.toLowerCase()) ||
    (c.email || '').toLowerCase().includes(customerSearch.toLowerCase()) ||
    (c.company || '').toLowerCase().includes(customerSearch.toLowerCase())
  );

  // Bulk customer management handlers (Apr 22, 2026)
  const toggleCustomerSelection = (customerId) => {
    setSelectedCustomerIds(prev => {
      const next = new Set(prev);
      if (next.has(customerId)) next.delete(customerId); else next.add(customerId);
      return next;
    });
  };

  const toggleSelectAllFiltered = () => {
    setSelectedCustomerIds(prev => {
      const allIds = filteredCustomers.map(c => c.customer_id);
      const allSelected = allIds.length > 0 && allIds.every(id => prev.has(id));
      if (allSelected) {
        // Deselect everything currently visible AND drop any stale ids not visible
        return new Set();
      }
      // Reset to only currently-visible customer ids (prune stale selections)
      return new Set(allIds);
    });
  };

  const handleAutoFlagSuspicious = async () => {
    try {
      const res = await adminFetch(`${API_URL}/api/admin/customers-suspicious?days=180`);
      if (!res.ok) throw new Error('Failed to fetch suspicious customers');
      const data = await res.json();
      if (!data.count) {
        toast.info('No suspicious customers detected. Great!');
        return;
      }
      const ids = new Set(data.customers.map(c => c.customer_id));
      setSelectedCustomerIds(ids);
      toast.success(`Flagged ${data.count} suspicious customers (0 orders + inactive/no account). Review selection and click "Delete Selected".`);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleBulkDeleteCustomers = async () => {
    const ids = Array.from(selectedCustomerIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} customer${ids.length > 1 ? 's' : ''}?\n\nCustomers with order history will be blocked (safety guardrail).`)) return;
    setIsBulkDeleting(true);
    try {
      const res = await adminFetch(`${API_URL}/api/admin/customers-bulk-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_ids: ids, force: false }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Bulk delete failed');
      }
      const data = await res.json();
      let msg = `Deleted ${data.deleted_count} customer${data.deleted_count !== 1 ? 's' : ''}`;
      if (data.blocked_count > 0) {
        msg += `. ${data.blocked_count} blocked (had orders)`;
      }
      toast.success(msg);
      setSelectedCustomerIds(new Set());
      fetchCustomers();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleExportCustomers = async (filter) => {
    setIsExporting(true);
    try {
      const res = await adminFetch(`${API_URL}/api/admin/customers-export?filter=${filter}`);
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.download = `genora_customers_${filter}_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filter === 'all' ? 'all customers' : filter === 'with_orders' ? 'paying customers' : 'subscribed customers'} CSV`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setIsExporting(false);
    }
  };

  // Newsletter broadcast handlers (Apr 22, 2026)
  const openNewsletterModal = async () => {
    setNewsletterMode('compose');
    setNewsletterSubject('');
    setNewsletterBody('');
    setNewsletterArticleId('');
    setNewsletterTestEmail('');
    setNewsletterSubscriberCount(null);
    setShowNewsletterModal(true);
    // Fetch live subscriber count
    try {
      const res = await adminFetch(`${API_URL}/api/admin/customers-newsletter-preview`);
      if (res.ok) {
        const d = await res.json();
        setNewsletterSubscriberCount(d.count ?? 0);
      }
    } catch {
      setNewsletterSubscriberCount(0);
    }
  };

  const sendNewsletter = async ({ isTest } = { isTest: false }) => {
    if (isTest && !newsletterTestEmail.trim()) {
      toast.error('Please enter a test email address');
      return;
    }
    if (newsletterMode === 'compose') {
      if (!newsletterSubject.trim()) { toast.error('Subject is required'); return; }
      if (!newsletterBody.trim()) { toast.error('Body is required'); return; }
    } else if (newsletterMode === 'article' && !newsletterArticleId) {
      toast.error('Please select an article'); return;
    }

    if (!isTest) {
      const count = newsletterSubscriberCount ?? 0;
      if (count === 0) { toast.error('No subscribers with marketing_consent = true'); return; }
      if (!window.confirm(`Send this newsletter to ${count} subscriber${count !== 1 ? 's' : ''}?\n\nThis action cannot be undone.`)) return;
    }

    setIsSendingNewsletter(true);
    try {
      const body = {
        mode: newsletterMode,
        subject: newsletterSubject,
        html_body: newsletterBody,
        article_id: newsletterArticleId,
      };
      if (isTest) body.test_email = newsletterTestEmail.trim();

      const res = await adminFetch(`${API_URL}/api/admin/customers-newsletter-send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Send failed');
      }
      const data = await res.json();
      if (isTest) {
        toast.success(`Test email sent to ${newsletterTestEmail}${data.failed_count > 0 ? ' (with errors — check backend logs)' : ''}`);
      } else {
        toast.success(`Sent to ${data.sent_count} of ${data.total_recipients} subscribers${data.failed_count > 0 ? ` (${data.failed_count} failed)` : ''}`);
        setShowNewsletterModal(false);
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setIsSendingNewsletter(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await adminFetch(`${API_URL}/api/admin/products`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
        setIsAuthenticated(true);
      } else if (response.status === 401) {
        // Clear invalid token
        localStorage.removeItem('adminToken');
        setAdminToken('');
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Not authenticated or error fetching products');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const loginData = { password };
      if (adminEmail.trim()) {
        loginData.email = adminEmail.trim().toLowerCase();
      }
      
      // Use plain fetch for login - don't send existing (potentially invalid) auth tokens
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify(loginData)
      });

      // Store ok status before reading body (Safari compatibility)
      const isOk = response.ok;
      const status = response.status;
      
      let data;
      try {
        const text = await response.text();
        data = text ? JSON.parse(text) : {};
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        throw new Error(`Server error (${status}). Please try again.`);
      }

      if (!isOk) {
        throw new Error(data?.detail || `Login failed (${status})`);
      }

      // Store token for Authorization header fallback
      if (data.token) {
        localStorage.setItem('adminToken', data.token);
        setAdminToken(data.token);
      }

      setIsAuthenticated(true);
      toast.success('Logged in successfully');
      fetchProducts();
    } catch (error) {
      toast.error(error.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await adminFetch(`${API_URL}/api/admin/logout`, {
        method: 'POST',
        headers: getAuthHeaders(),
        
      });
    } catch (error) {
      console.error('Logout error');
    }
    // Clear stored token
    localStorage.removeItem('adminToken');
    setAdminToken('');
    setIsAuthenticated(false);
    setProducts([]);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      const response = await adminFetch(`${API_URL}/api/admin/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to change password');
      }
      
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setChangingPassword(false);
    }
  };

  // Invoice & Order Management Handlers
  const handleResendInvoice = async () => {
    if (!invoiceOrder) return;
    
    setSendingInvoice(true);
    try {
      const ccEmails = invoiceEmails.cc ? invoiceEmails.cc.split(',').map(e => e.trim()) : [];
      const bccEmails = invoiceEmails.bcc ? invoiceEmails.bcc.split(',').map(e => e.trim()) : [];
      
      const response = await adminFetch(`${API_URL}/api/admin/orders/${invoiceOrder.order_id}/resend-invoice`, {
        method: 'POST',
        body: JSON.stringify({ cc_emails: ccEmails, bcc_emails: bccEmails })
      });
      
      // Store ok status before reading body (Safari compatibility)
      const isOk = response.ok;
      
      let data;
      try {
        data = await response.json();
      } catch (e) {
        data = {};
      }
      
      if (!isOk) {
        throw new Error(data?.detail || 'Failed to send invoice');
      }
      
      toast.success('Invoice sent successfully!');
      setShowInvoiceModal(false);
      setInvoiceOrder(null);
      setInvoiceEmails({ cc: '', bcc: '' });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSendingInvoice(false);
    }
  };

  const handleSendReminder = async (orderId) => {
    setRemindingId(orderId);
    try {
      const response = await adminFetch(`${API_URL}/api/admin/orders/${orderId}/send-payment-reminder`, { method: 'POST' });
      if (!response.ok) {
        const e = await response.json().catch(() => ({}));
        throw new Error(e.detail || 'Failed to send reminder');
      }
      toast.success('Payment reminder emailed to the customer');
      fetchSalesData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setRemindingId(null);
    }
  };

  const handleSendBalanceReminder = async (orderId) => {
    setRemindingId(orderId);
    try {
      const response = await adminFetch(`${API_URL}/api/admin/orders/${orderId}/send-balance-reminder`, { method: 'POST' });
      if (!response.ok) {
        const e = await response.json().catch(() => ({}));
        throw new Error(e.detail || 'Failed to send balance reminder');
      }
      toast.success('Balance reminder emailed to the customer');
      fetchSalesData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setRemindingId(null);
    }
  };

  const loadAvailableProducts = async () => {
    const peptidesWithStock = products
      .filter(p => p.variants?.some(v => v.in_stock && v.stock_quantity > 0))
      .map(p => ({ ...p, product_type: 'peptide' }));
    let shopWithStock = [];
    try {
      const res = await adminFetch(`${API_URL}/api/admin/shop/products`);
      if (res.ok) {
        const shopProducts = await res.json();
        shopWithStock = shopProducts
          .filter(sp => {
            if (sp.has_pack_sizes) return (sp.pack_sizes || []).some(ps => (ps.stock_quantity || 0) > 0);
            if (sp.has_colors) return (sp.colors || []).some(c => (c.stock_quantity || 0) > 0);
            return sp.in_stock && (sp.stock_quantity || 0) > 0;
          })
          .map(sp => ({ ...sp, product_type: 'shop' }));
      }
    } catch (e) {
      console.warn('Could not fetch shop products', e);
    }
    setAvailableProducts([...peptidesWithStock, ...shopWithStock]);
  };

  const startEditOrder = (order) => {
    loadAvailableProducts();
    setEditForm({
      items: (order.items || []).map((it) => ({
        product_id: it.product_id || '',
        name: it.name || it.product_name || '',
        variant_name: it.variant_name || it.vial_size || '',
        variant_index: it.variant_index != null ? it.variant_index : null,
        price: parseFloat(it.price || 0),
        quantity: it.quantity || 1,
        item_type: it.item_type || 'peptide',
      })),
      discount_amount: parseFloat(order.discount_amount || 0),
      discount_label: order.discount?.code || order.discount_code || '',
      shipping_cost: parseFloat(order.shipping_cost || 0),
      adjust_inventory: false,
    });
    setEditingOrder(order.order_id);
  };

  const cancelEditOrder = () => { setEditingOrder(null); setEditForm(null); };

  const editUpdateLine = (idx, patch) => {
    setEditForm((f) => {
      const items = [...f.items];
      items[idx] = { ...items[idx], ...patch };
      return { ...f, items };
    });
  };

  const editSelectProduct = (idx, productId) => {
    if (!productId) { editUpdateLine(idx, { product_id: '' }); return; }
    const p = availableProducts.find((x) => x.product_id === productId);
    if (!p) return;
    const v = (p.variants && p.variants[0]) || null;
    editUpdateLine(idx, {
      product_id: p.product_id,
      name: p.name,
      variant_index: p.product_type === 'shop' ? null : 0,
      variant_name: v ? v.size : '',
      price: v ? parseFloat(v.price || 0) : (parseFloat(p.price) || 0),
    });
  };

  const editSelectVariant = (idx, vIdx) => {
    setEditForm((f) => {
      const items = [...f.items];
      const line = items[idx];
      const p = availableProducts.find((x) => x.product_id === line.product_id);
      const v = p && p.variants ? p.variants[vIdx] : null;
      items[idx] = { ...line, variant_index: vIdx, variant_name: v ? v.size : line.variant_name, price: v ? parseFloat(v.price || 0) : line.price };
      return { ...f, items };
    });
  };

  const editAddLine = () => setEditForm((f) => ({ ...f, items: [...f.items, { product_id: '', name: '', variant_name: '', variant_index: null, price: 0, quantity: 1, item_type: 'peptide' }] }));
  const editRemoveLine = (idx) => setEditForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const editComputed = () => {
    if (!editForm) return { subtotal: 0, disc: 0, total: 0 };
    const subtotal = editForm.items.reduce((s, i) => s + (parseFloat(i.price) || 0) * (parseInt(i.quantity) || 0), 0);
    const disc = Math.max(0, Math.min(parseFloat(editForm.discount_amount) || 0, subtotal));
    const total = Math.max(0, subtotal - disc + (parseFloat(editForm.shipping_cost) || 0));
    return { subtotal, disc, total };
  };

  const saveEditOrder = async () => {
    if (!editForm || editForm.items.length === 0) { toast.error('Order needs at least one item'); return; }
    for (const it of editForm.items) {
      if (!(it.name || '').trim()) { toast.error('Every line needs a product'); return; }
    }
    setSavingEdit(true);
    try {
      const r = await adminFetch(`${API_URL}/api/admin/orders/${editingOrder}/edit`, {
        method: 'PUT',
        body: JSON.stringify({
          items: editForm.items.map((i) => ({
            product_id: i.product_id || undefined,
            name: i.name,
            variant_name: i.variant_name,
            variant_index: i.variant_index,
            price: parseFloat(i.price) || 0,
            quantity: parseInt(i.quantity) || 1,
            item_type: i.item_type,
          })),
          discount_amount: parseFloat(editForm.discount_amount) || 0,
          discount_label: editForm.discount_label,
          shipping_cost: parseFloat(editForm.shipping_cost) || 0,
          adjust_inventory: editForm.adjust_inventory,
        }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Save failed'); }
      toast.success('Order updated');
      cancelEditOrder();
      fetchSalesData();
    } catch (e) { toast.error(e.message); } finally { setSavingEdit(false); }
  };


  const handleMarkAsAdjustment = async (orderId) => {
    if (!window.confirm('Mark this order as inventory adjustment? It will be excluded from sales metrics.')) return;
    
    try {
      const response = await adminFetch(`${API_URL}/api/admin/orders/${orderId}/mark-adjustment`, {
        method: 'PATCH',
        body: JSON.stringify({ reason: 'Inventory adjustment' })
      });
      
      if (!response.ok) throw new Error('Failed to mark order');
      
      toast.success('Order marked as inventory adjustment');
      fetchSalesData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUnmarkAdjustment = async (orderId) => {
    if (!window.confirm('Include this order back in sales metrics?')) return;
    
    try {
      const response = await adminFetch(`${API_URL}/api/admin/orders/${orderId}/unmark-adjustment`, {
        method: 'PATCH'
      });
      
      if (!response.ok) throw new Error('Failed to restore order');
      
      toast.success('Order restored to sales metrics');
      fetchSalesData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleBulkUnmarkAdjustment = async () => {
    if (selectedOrders.length === 0) {
      toast.error('No orders selected');
      return;
    }
    
    if (!window.confirm(`Include ${selectedOrders.length} order(s) back in sales metrics?`)) return;
    
    setBulkActionLoading(true);
    try {
      let successCount = 0;
      let errorCount = 0;
      
      for (const orderId of selectedOrders) {
        try {
          const response = await adminFetch(`${API_URL}/api/admin/orders/${orderId}/unmark-adjustment`, {
            method: 'PATCH'
          });
          if (response.ok) successCount++;
          else errorCount++;
        } catch {
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} order(s) restored to sales metrics`);
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} order(s) failed to update`);
      }
      
      setSelectedOrders([]);
      fetchSalesData();
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleAssignAffiliateReward = async (orderId, affiliateEmail) => {
    try {
      const response = await adminFetch(`${API_URL}/api/admin/orders/${orderId}/affiliate-reward`, {
        method: 'PATCH',
        body: JSON.stringify({ affiliate_email: affiliateEmail || '' })
      });
      if (!response.ok) {
        const d = await response.json();
        throw new Error(d.detail || 'Failed');
      }
      toast.success(affiliateEmail ? `Order assigned to ${affiliateEmail}'s rewards` : 'Affiliate reward removed');
      setAffiliateRewardMenu(null);
      fetchSalesData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEmailOrderToAdmin = async (order) => {
    try {
      const response = await adminFetch(`${API_URL}/api/admin/orders/${order.order_id}/email-copy`, {
        method: 'POST'
      });
      if (!response.ok) {
        const d = await response.json();
        throw new Error(d.detail || 'Failed to send');
      }
      toast.success('Order details emailed to admin');
    } catch (error) {
      toast.error(error.message);
    }
  };


  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Permanently delete this order? This action cannot be undone.')) return;
    
    try {
      const response = await adminFetch(`${API_URL}/api/admin/orders/${orderId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) throw new Error('Failed to delete order');
      
      toast.success('Order deleted');
      fetchSalesData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Cancel an order — restores inventory, refunds store credit, reverses points,
  // marks order_status='cancelled' + exclude_from_sales (so the affiliate dashboard
  // recomputes without this order). Optionally emails the customer.
  const handleConfirmReservation = async (order) => {
    const pname = order.payment_method === 'cash_app' ? 'Cash App'
      : order.payment_method === 'zelle' ? 'Zelle'
      : 'manual';
    const ok = window.confirm(
      `Mark ${pname} payment received for ${order.order_id}?\n\n` +
      `Only do this AFTER you've verified the deposit in your ${order.payment_method === 'zelle' ? 'bank app' : pname === 'manual' ? 'records' : pname + ' account'}.\n\n` +
      `This will:\n` +
      `  • Deduct inventory for ${order.items?.length || 0} line item(s)\n` +
      `  • Mark the order as PAID / ready to ship\n` +
      `  • Email the customer: "Payment received — order confirmed"\n` +
      `  • Send an audit copy to the team mailbox\n\n` +
      `Amount paid: $${(order.total || 0).toFixed(2)}`
    );
    if (!ok) return;
    try {
      const token = localStorage.getItem('adminToken');
      const r = await fetch(`${API_URL}/api/admin/orders/${order.order_id}/confirm-reservation`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const data = await r.json().catch(() => ({}));
      if (data?.shipstation_synced) {
        toast.success('Payment confirmed — inventory deducted, customer notified, order sent to ShipStation.');
      } else {
        toast.warning('Payment confirmed (inventory deducted, customer notified), but the order did NOT reach ShipStation. Check your ShipStation account/billing, then re-confirm or re-sync.', { duration: 8000 });
      }
      fetchSalesData();
    } catch (e) {
      toast.error(`Confirm failed: ${e.message}`);
    }
  };

  // "Undo" an accidental green-checkmark confirmation on a Cash App / Zelle /
  // Reserve order. Restocks inventory, sends the order back to "Reserved /
  // awaiting payment", removes it from ShipStation, and emails the customer
  // that the confirmation was reversed.
  const handleRevertConfirmation = async (order) => {
    const pname = order.payment_method === 'cash_app' ? 'Cash App'
      : order.payment_method === 'zelle' ? 'Zelle'
      : 'manual';
    const ok = window.confirm(
      `Undo the payment confirmation for ${order.order_id}?\n\n` +
      `Use this if you clicked the green checkmark by mistake.\n\n` +
      `This will:\n` +
      `  • Restock inventory for ${order.items?.length || 0} line item(s)\n` +
      `  • Send the order back to "Reserved / awaiting payment"\n` +
      `  • Remove the order from ShipStation\n` +
      `  • Email the customer that the confirmation was reversed\n\n` +
      `You can re-confirm later once the ${pname} payment is truly verified.`
    );
    if (!ok) return;
    try {
      const token = localStorage.getItem('adminToken');
      const r = await fetch(`${API_URL}/api/admin/orders/${order.order_id}/revert-confirmation`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      toast.success('Confirmation reverted — inventory restocked, order back to Reserved, customer notified.');
      fetchSalesData();
    } catch (e) {
      toast.error(`Revert failed: ${e.message}`);
    }
  };


  // Manually (re)push an order to ShipStation. Useful when the automatic
  // push was skipped — e.g. a ShipStation outage / billing "402 Payment
  // Required". ShipStation upserts by orderNumber so this is safe to repeat.
  const handleResyncShipStation = async (order) => {
    try {
      const token = localStorage.getItem('adminToken');
      const r = await fetch(`${API_URL}/api/admin/orders/${order.order_id}/sync-shipstation`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        throw new Error(data.detail || `HTTP ${r.status}`);
      }
      toast.success(`Order ${order.order_id} sent to ShipStation${data.shipstation_order_id ? ` (SS #${data.shipstation_order_id})` : ''}.`);
      fetchSalesData();
    } catch (e) {
      toast.error(`ShipStation sync failed: ${e.message}`, { duration: 8000 });
    }
  };

  // "Customer flaked" — cancel a Cash App / Zelle reservation that never had
  // its payment confirmed. Refunds store credit, releases the affiliate code,
  // deletes the ShipStation queue entry, and emails the customer a friendly
  // "didn't see your payment, want to try again?" with a back-to-cart link.
  const handleCancelReservation = async (order) => {
    const pname = order.payment_method === 'cash_app' ? 'Cash App'
      : order.payment_method === 'zelle' ? 'Zelle'
      : 'reservation';
    const reason = window.prompt(
      `Cancel ${pname} reservation ${order.order_id}?\n\n` +
      `Use this when a customer clicked "I've Sent the Payment" but the funds never showed up in your ${order.payment_method === 'zelle' ? 'bank' : 'Cash App'} account.\n\n` +
      `This will:\n` +
      `  • Refund $${(order.store_credit_used || 0).toFixed(2)} store credit (if used)\n` +
      `  • Release the affiliate code "${order.discount_code || '—'}" back to available uses\n` +
      `  • Delete the ShipStation queue entry\n` +
      `  • Mark the order cancelled (excluded from sales reports)\n` +
      `  • Email the customer: "We didn't see your payment — want to try again?" with a link back to their cart\n\n` +
      `Optional reason (shown in audit log):`,
      'Customer did not send payment'
    );
    if (reason === null) return;
    try {
      const token = localStorage.getItem('adminToken');
      const r = await fetch(`${API_URL}/api/admin/orders/${order.order_id}/cancel-reservation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ reason: (reason || '').trim(), notify_customer: true }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.detail || `HTTP ${r.status}`);
      const parts = [];
      if (data.store_credit_refunded > 0) parts.push(`refunded $${data.store_credit_refunded.toFixed(2)}`);
      if (data.discount_code_released) parts.push(`code "${data.discount_code_released}" released`);
      if (data.shipstation_deleted) parts.push('ShipStation cleared');
      toast.success(`Reservation cancelled${parts.length ? ' — ' + parts.join(', ') : ''}.`);
      fetchSalesData();
    } catch (e) {
      toast.error(`Cancel failed: ${e.message}`);
    }
  };

  const handleCancelOrder = async (order) => {
    const reason = window.prompt(
      `Cancel order ${order.order_id}?\n\n` +
      `This will:\n• Restore stock for ${order.items?.length || 0} item(s)\n` +
      `• Refund $${(order.store_credit_used || 0).toFixed(2)} store credit if used\n` +
      `• Reverse purchase points / affiliate revenue\n` +
      `• Email the customer\n\n` +
      `Optional cancellation reason:`,
      ''
    );
    if (reason === null) return; // user pressed Cancel on prompt
    try {
      const response = await adminFetch(`${API_URL}/api/admin/orders/${order.order_id}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason.trim(), notify_customer: true })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.detail || 'Failed to cancel order');
      const errs = (data.inventory_errors || []).length;
      toast.success(
        `Order cancelled • ${(data.inventory_restored || []).length} item(s) restocked` +
        (data.store_credit_refunded ? ` • $${data.store_credit_refunded.toFixed(2)} credit refunded` : '') +
        (errs ? ` • ${errs} inventory warning(s)` : '')
      );
      fetchSalesData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Multi-select order handlers
  const toggleOrderSelection = (orderId) => {
    setSelectedOrders(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const toggleSelectAllOrders = () => {
    if (!salesData?.recent_orders) return;
    const allOrderIds = salesData.recent_orders.slice(0, 10).map(o => o.order_id);
    if (selectedOrders.length === allOrderIds.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(allOrderIds);
    }
  };

  const handleBulkMarkAsAdjustment = async () => {
    if (selectedOrders.length === 0) {
      toast.error('No orders selected');
      return;
    }
    
    if (!window.confirm(`Mark ${selectedOrders.length} order(s) as inventory adjustments? They will be excluded from sales metrics.`)) return;
    
    setBulkActionLoading(true);
    try {
      let successCount = 0;
      let errorCount = 0;
      
      for (const orderId of selectedOrders) {
        try {
          const response = await adminFetch(`${API_URL}/api/admin/orders/${orderId}/mark-adjustment`, {
            method: 'PATCH',
            body: JSON.stringify({ reason: 'Bulk inventory adjustment' })
          });
          if (response.ok) successCount++;
          else errorCount++;
        } catch {
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} order(s) marked as inventory adjustments`);
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} order(s) failed to update`);
      }
      
      setSelectedOrders([]);
      fetchSalesData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDeleteOrders = async () => {
    if (selectedOrders.length === 0) {
      toast.error('No orders selected');
      return;
    }
    
    if (!window.confirm(`Permanently delete ${selectedOrders.length} order(s)? This action cannot be undone.`)) return;
    
    setBulkActionLoading(true);
    try {
      let successCount = 0;
      let errorCount = 0;
      
      for (const orderId of selectedOrders) {
        try {
          const response = await adminFetch(`${API_URL}/api/admin/orders/${orderId}`, {
            method: 'DELETE'
          });
          if (response.ok) successCount++;
          else errorCount++;
        } catch {
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} order(s) deleted`);
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} order(s) failed to delete`);
      }
      
      setSelectedOrders([]);
      fetchSalesData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Multi-select product handlers (Inventory tab)
  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const toggleSelectAllProducts = () => {
    if (selectedProducts.length === filteredProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredProducts.map(p => p.product_id));
    }
  };

  const handleBulkSetOutOfStock = async () => {
    if (selectedProducts.length === 0) {
      toast.error('No products selected');
      return;
    }
    
    if (!window.confirm(`Mark all variants of ${selectedProducts.length} product(s) as OUT OF STOCK?`)) return;
    
    setBulkProductActionLoading(true);
    try {
      let successCount = 0;
      let errorCount = 0;
      
      for (const productId of selectedProducts) {
        const product = products.find(p => p.product_id === productId);
        if (!product || !product.variants) continue;
        
        // Update all variants to out of stock
        const updatedVariants = product.variants.map(v => ({ ...v, in_stock: false }));
        
        try {
          const response = await adminFetch(`${API_URL}/api/admin/products/${productId}`, {
            method: 'PUT',
            body: JSON.stringify({ variants: updatedVariants })
          });
          if (response.ok) successCount++;
          else errorCount++;
        } catch {
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} product(s) marked as out of stock`);
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} product(s) failed to update`);
      }
      
      setSelectedProducts([]);
      fetchProducts();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBulkProductActionLoading(false);
    }
  };

  const handleBulkSetInStock = async () => {
    if (selectedProducts.length === 0) {
      toast.error('No products selected');
      return;
    }
    
    if (!window.confirm(`Mark all variants of ${selectedProducts.length} product(s) as IN STOCK?`)) return;
    
    setBulkProductActionLoading(true);
    try {
      let successCount = 0;
      let errorCount = 0;
      
      for (const productId of selectedProducts) {
        const product = products.find(p => p.product_id === productId);
        if (!product || !product.variants) continue;
        
        // Update all variants to in stock
        const updatedVariants = product.variants.map(v => ({ ...v, in_stock: true }));
        
        try {
          const response = await adminFetch(`${API_URL}/api/admin/products/${productId}`, {
            method: 'PUT',
            body: JSON.stringify({ variants: updatedVariants })
          });
          if (response.ok) successCount++;
          else errorCount++;
        } catch {
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} product(s) marked as in stock`);
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} product(s) failed to update`);
      }
      
      setSelectedProducts([]);
      fetchProducts();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBulkProductActionLoading(false);
    }
  };

  const handleBulkDeleteProducts = async () => {
    if (selectedProducts.length === 0) {
      toast.error('No products selected');
      return;
    }
    
    if (!window.confirm(`Permanently DELETE ${selectedProducts.length} product(s)? This cannot be undone.`)) return;
    
    setBulkProductActionLoading(true);
    try {
      let successCount = 0;
      let errorCount = 0;
      
      for (const productId of selectedProducts) {
        try {
          const response = await adminFetch(`${API_URL}/api/admin/products/${productId}`, {
            method: 'DELETE'
          });
          if (response.ok) successCount++;
          else errorCount++;
        } catch {
          errorCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} product(s) deleted`);
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} product(s) failed to delete`);
      }
      
      setSelectedProducts([]);
      fetchProducts();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBulkProductActionLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    setLoadingReport(true);
    try {
      let url = `${API_URL}/api/admin/sales-report?include_details=true`;
      if (reportDateRange.start) url += `&start_date=${reportDateRange.start}`;
      if (reportDateRange.end) url += `&end_date=${reportDateRange.end}`;
      
      const response = await adminFetch(url);
      if (!response.ok) throw new Error('Failed to generate report');
      
      const data = await response.json();
      setReportData(data);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoadingReport(false);
    }
  };

  const handlePrintReport = () => {
    window.print();
  };

  const handleLoadMore = () => {
    const nextPage = salesPage + 1;
    setSalesPage(nextPage);
    fetchSalesData(null, nextPage, undefined, true);
  };

  const handleSalesSearch = (query) => {
    setSalesSearch(query);
    setSalesPage(1);
    fetchSalesData(null, 1, query, false);
  };

  const handleStatusChange = async () => {
    if (!statusModal) return;
    setUpdatingStatus(true);
    try {
      const res = await adminFetch(`${API_URL}/api/admin/orders/${statusModal.order_id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: statusModal.new_status, notify_customer: true })
      });
      let data;
      try { const text = await res.text(); data = text ? JSON.parse(text) : {}; } catch { data = {}; }
      if (!res.ok) throw new Error(data?.detail || 'Failed to update status');
      // When the admin set status to "shipped" the backend pushes the order
      // to ShipStation. Surface that result so they know whether they can go
      // print the label or whether the push failed and they need to retry.
      if (statusModal.new_status === 'shipped') {
        if (data?.shipstation_synced) {
          toast.success('Status updated & order sent to ShipStation — go print the label');
        } else if (data?.shipstation_error) {
          toast.error(`Status updated but ShipStation sync failed: ${data.shipstation_error}`);
        } else {
          toast.success('Status updated & customer notified (ShipStation: no key configured)');
        }
      } else {
        toast.success(`Status updated & customer notified`);
      }
      setStatusModal(null);
      fetchSalesData(null, 1, salesSearch, false);
      setSalesPage(1);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const STATUS_CONFIG = {
    order_placed: { label: 'Order Placed', color: 'bg-amber-100 text-amber-700' },
    confirmed: { label: 'Confirmed', color: 'bg-blue-100 text-blue-700' },
    delayed: { label: 'Delayed', color: 'bg-red-100 text-red-700' },
    shipped: { label: 'Shipped', color: 'bg-green-100 text-green-700' },
    ready_for_pickup: { label: 'Ready for Pickup', color: 'bg-purple-100 text-purple-700' },
    pending: { label: 'Order Placed', color: 'bg-amber-100 text-amber-700' },
    paid: { label: 'Order Placed', color: 'bg-amber-100 text-amber-700' },
  };

  const handleSendMonthlyReport = async () => {
    // Get current month and year for the previous month
    const now = new Date();
    let month = now.getMonth(); // 0-indexed, so this is actually previous month
    let year = now.getFullYear();
    if (month === 0) {
      month = 12;
      year -= 1;
    }
    
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'];
    const monthName = monthNames[month - 1];
    
    if (!window.confirm(`Send the ${monthName} ${year} sales report to research@genorapeptides.com?`)) return;
    
    try {
      const response = await adminFetch(`${API_URL}/api/admin/sales-report/send-monthly`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ year, month })
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to send report');
      }
      
      const data = await response.json();
      toast.success(`📧 Monthly report sent! Revenue: $${data.summary.total_revenue.toLocaleString()}, Orders: ${data.summary.total_orders}`);
    } catch (error) {
      toast.error(error.message || 'Failed to send monthly report');
    }
  };

  const handleToggleStock = async (productId, variantIndex, currentStatus) => {
    setUpdatingVariant(`${productId}_${variantIndex}`);
    try {
      const response = await adminFetch(`${API_URL}/api/admin/products/${productId}/variants/${variantIndex}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({ in_stock: !currentStatus })
      });

      if (!response.ok) {
        throw new Error('Failed to update');
      }

      // Update local state
      setProducts(prev => prev.map(product => {
        if (product.product_id === productId) {
          const updatedVariants = [...product.variants];
          updatedVariants[variantIndex] = {
            ...updatedVariants[variantIndex],
            in_stock: !currentStatus
          };
          return { ...product, variants: updatedVariants };
        }
        return product;
      }));

      toast.success(`Stock status updated`);
    } catch (error) {
      toast.error('Failed to update stock status');
    } finally {
      setUpdatingVariant(null);
    }
  };

  const handleUpdateQuantity = async (productId, variantIndex, newQuantity) => {
    if (newQuantity < 0) return;
    setUpdatingVariant(`${productId}_${variantIndex}`);
    try {
      const response = await adminFetch(`${API_URL}/api/admin/products/${productId}/variants/${variantIndex}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({ stock_quantity: newQuantity })
      });

      if (!response.ok) {
        throw new Error('Failed to update');
      }

      // Update local state
      setProducts(prev => prev.map(product => {
        if (product.product_id === productId) {
          const updatedVariants = [...product.variants];
          updatedVariants[variantIndex] = {
            ...updatedVariants[variantIndex],
            stock_quantity: newQuantity,
            in_stock: newQuantity > 0
          };
          return { ...product, variants: updatedVariants };
        }
        return product;
      }));

      toast.success(`Quantity updated to ${newQuantity}`);
    } catch (error) {
      toast.error('Failed to update quantity');
    } finally {
      setUpdatingVariant(null);
    }
  };

  const handleAddVariant = async (productId) => {
    if (!newVariant.size || !newVariant.price) {
      toast.error('Please enter size and price');
      return;
    }
    
    setUpdatingVariant(`${productId}_add`);
    try {
      const response = await adminFetch(`${API_URL}/api/admin/products/${productId}/variants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({
          size: newVariant.size,
          price: parseFloat(newVariant.price),
          stock_quantity: parseInt(newVariant.stock_quantity) || 50
        })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to add variant');
      }

      // Refresh products to get updated data
      await fetchProducts();
      setAddingVariant(null);
      setNewVariant({ size: '', price: '', stock_quantity: 50 });
      toast.success(`New size "${newVariant.size}" added`);
    } catch (error) {
      toast.error(error.message || 'Failed to add variant');
    } finally {
      setUpdatingVariant(null);
    }
  };

  const handleDeleteVariant = async (productId, variantIndex, variantSize) => {
    if (!window.confirm(`Are you sure you want to remove the "${variantSize}" size option?`)) {
      return;
    }
    
    setUpdatingVariant(`${productId}_${variantIndex}`);
    try {
      const response = await adminFetch(`${API_URL}/api/admin/products/${productId}/variants/${variantIndex}`, {
        method: 'DELETE',
        
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to remove variant');
      }

      // Update local state
      setProducts(prev => prev.map(product => {
        if (product.product_id === productId) {
          const updatedVariants = product.variants.filter((_, i) => i !== variantIndex);
          return { ...product, variants: updatedVariants };
        }
        return product;
      }));

      toast.success(`Size "${variantSize}" removed`);
    } catch (error) {
      toast.error(error.message || 'Failed to remove variant');
    } finally {
      setUpdatingVariant(null);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProductImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    
    if (!newProduct.name || !newProduct.category || !newProduct.variant_price) {
      toast.error('Please fill in name, category, and price');
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('name', newProduct.name);
      formData.append('category', newProduct.category);
      formData.append('description', newProduct.description);
      formData.append('purity', newProduct.purity);
      formData.append('variant_size', newProduct.variant_size);
      formData.append('variant_price', newProduct.variant_price);
      formData.append('variant_quantity', newProduct.variant_quantity);
      
      // Handle image - either file upload or URL
      if (newProduct.useImageUrl && newProduct.imageUrl) {
        formData.append('image_url', newProduct.imageUrl);
      } else if (productImage) {
        formData.append('image', productImage);
      }
      
      const response = await adminFetch(`${API_URL}/api/admin/products`, {
        method: 'POST',
        
        body: formData
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to create product');
      }

      await fetchProducts();
      setShowAddProduct(false);
      setNewProduct({
        name: '',
        category: 'metabolic',
        description: '',
        purity: '≥99%',
        variant_size: '5mg',
        variant_price: '',
        variant_quantity: '50',
        useImageUrl: false,
        imageUrl: ''
      });
      setProductImage(null);
      setImagePreview(null);
      toast.success(`Product "${newProduct.name}" created successfully`);
    } catch (error) {
      toast.error(error.message || 'Failed to create product');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (productId, productName) => {
    if (!window.confirm(`Are you sure you want to delete "${productName}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      const response = await adminFetch(`${API_URL}/api/admin/products/${productId}`, {
        method: 'DELETE',
        
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to delete product');
      }

      setProducts(prev => prev.filter(p => p.product_id !== productId));
      toast.success(`Product "${productName}" deleted`);
    } catch (error) {
      toast.error(error.message || 'Failed to delete product');
    }
  };

  const handleNotifyCustomersAboutProduct = async (productId, productName) => {
    if (!window.confirm(`Send "Back in Stock" email to ALL customers about "${productName}"?\n\nThis will notify every registered customer about this product's availability.`)) {
      return;
    }
    
    try {
      const response = await adminFetch(`${API_URL}/api/admin/products/${productId}/notify-customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_url: window.location.origin })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send notifications');
      }

      let msg = `Sent to ${data.sent_count} customers`;
      if (data.failed_count > 0) msg += ` (${data.failed_count} failed)`;
      msg += ` via ${data.sender_used || 'email'}`;
      toast.success(msg);
      if (data.failed_count > 0 && data.failed_emails?.length) {
        console.warn('Failed emails:', data.failed_emails);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Edit Product Functions
  const openEditProductModal = (product) => {
    setEditingProduct(product);
    // Strip date-time portion if sale_starts_at/ends_at are full ISO strings — date input expects YYYY-MM-DD
    const toDateInput = (val) => {
      if (!val) return '';
      const s = String(val);
      return s.includes('T') ? s.split('T')[0] : s;
    };
    setEditProductForm({
      name: product.name || '',
      category: product.category || 'metabolic',
      description: product.description || '',
      purity: product.purity || '≥99%',
      variants: product.variants?.map(v => ({
        ...v,
        // Mirror per-variant sale fields so the form can edit them. Undefined => no override.
        sale_active: v.sale_active ?? undefined,
        sale_type: v.sale_type ?? 'percent',
        sale_value: v.sale_value ?? '',
        sale_starts_at: toDateInput(v.sale_starts_at),
        sale_ends_at: toDateInput(v.sale_ends_at),
        // Override is considered "on" only when an explicit variant-level sale_active flag is present.
        // Avoids false positives from a stale sale_value:0 or empty start/end date.
        _has_variant_sale_override: typeof v.sale_active === 'boolean'
      })) || [],
      sale_active: !!product.sale_active,
      sale_type: product.sale_type || 'percent',
      sale_value: product.sale_value ?? '',
      sale_starts_at: toDateInput(product.sale_starts_at),
      sale_ends_at: toDateInput(product.sale_ends_at),
      member_discount_eligible: product.member_discount_eligible !== false,
      allow_preorder: product.allow_preorder === true
    });
    const imgUrl = product.image_url;
    setEditProductImagePreview(
      (imgUrl?.startsWith('/uploads') || imgUrl?.startsWith('/api/uploads')) 
        ? `${API_URL}${imgUrl}` 
        : imgUrl
    );
    setEditProductImage(null);
    setShowEditProduct(true);
  };

  const handleEditProductImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditProductImage(file);
      setEditProductImagePreview(URL.createObjectURL(file));
    }
  };

  const handleUpdateVariantInEdit = (index, field, value) => {
    setEditProductForm(prev => ({
      ...prev,
      variants: prev.variants.map((v, i) => 
        i === index ? { ...v, [field]: field === 'price' || field === 'stock_quantity' ? parseFloat(value) || 0 : value } : v
      )
    }));
  };

  const handleAddVariantInEdit = () => {
    setEditProductForm(prev => ({
      ...prev,
      variants: [...prev.variants, { size: '', price: 0, stock_quantity: 50, in_stock: true }]
    }));
  };

  const handleRemoveVariantInEdit = (index) => {
    if (editProductForm.variants.length <= 1) {
      toast.error('Product must have at least one variant');
      return;
    }
    setEditProductForm(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
  };

  const handleSaveEditProduct = async () => {
    if (!editProductForm.name) {
      toast.error('Product name is required');
      return;
    }
    if (editProductForm.variants.length === 0 || editProductForm.variants.some(v => !v.size || !v.price)) {
      toast.error('All variants must have size and price');
      return;
    }

    setLoading(true);
    try {
      // Handle image upload first if new image selected
      let newImageUrl = null;
      if (editUseExternalUrl && editExternalImageUrl.trim()) {
        // Use external URL directly
        let url = editExternalImageUrl.trim();
        if (url.includes('drive.google.com/file/d/')) {
          const match = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
          if (match) {
            url = `https://lh3.googleusercontent.com/d/${match[1]}`;
          }
        } else if (url.includes('drive.google.com') && url.includes('id=')) {
          const match = url.match(/id=([a-zA-Z0-9_-]+)/);
          if (match) {
            url = `https://lh3.googleusercontent.com/d/${match[1]}`;
          }
        }
        newImageUrl = url;
      } else if (editProductImage) {
        const formData = new FormData();
        formData.append('image', editProductImage);
        
        const imgResponse = await adminFetch(`${API_URL}/api/admin/products/${editingProduct.product_id}/image`, {
          method: 'PUT',
          
          body: formData
        });
        
        if (imgResponse.ok) {
          const imgData = await imgResponse.json();
          newImageUrl = imgData.image_url;
        }
      }

      // Update product details
      const updateData = {
        name: editProductForm.name,
        category: editProductForm.category,
        description: editProductForm.description,
        purity: editProductForm.purity,
        // Sale + member-eligibility (Apr 23, 2026)
        sale_active: !!editProductForm.sale_active,
        sale_type: editProductForm.sale_type || 'percent',
        sale_value: editProductForm.sale_value === '' ? 0 : parseFloat(editProductForm.sale_value) || 0,
        sale_starts_at: editProductForm.sale_starts_at || null,
        sale_ends_at: editProductForm.sale_ends_at || null,
        member_discount_eligible: editProductForm.member_discount_eligible !== false,
        allow_preorder: !!editProductForm.allow_preorder
      };
      
      if (newImageUrl) {
        updateData.image_url = newImageUrl;
      }

      const response = await adminFetch(`${API_URL}/api/admin/products/${editingProduct.product_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        throw new Error('Failed to update product details');
      }

      // Update existing variants
      for (let i = 0; i < editProductForm.variants.length; i++) {
        const variant = editProductForm.variants[i];
        if (i < editingProduct.variants.length) {
          // Update existing variant using correct endpoint
          await adminFetch(`${API_URL}/api/admin/products/${editingProduct.product_id}/variants/${i}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            
            body: JSON.stringify({
              in_stock: variant.in_stock,
              stock_quantity: variant.stock_quantity
            })
          });
          
          // Update variant price/size via full update endpoint
          const variantPayload = {
            size: variant.size,
            price: variant.price,
            stock_quantity: variant.stock_quantity,
            in_stock: variant.in_stock
          };
          // Per-variant sale override (Apr 23, 2026):
          // - When the override checkbox is ON, send the variant-level sale fields (these take precedence over product-level).
          // - When OFF, instruct backend to $unset the variant-level sale fields so the variant inherits the product-level sale.
          //   Otherwise stale `sale_active=false` on the variant would suppress the parent product-level sale.
          if (variant._has_variant_sale_override) {
            variantPayload.sale_active = !!variant.sale_active;
            variantPayload.sale_type = variant.sale_type || 'percent';
            variantPayload.sale_value = variant.sale_value === '' || variant.sale_value === null || variant.sale_value === undefined
              ? 0
              : parseFloat(variant.sale_value) || 0;
            variantPayload.sale_starts_at = variant.sale_starts_at || null;
            variantPayload.sale_ends_at = variant.sale_ends_at || null;
          } else {
            variantPayload.clear_sale_override = true;
          }
          await adminFetch(`${API_URL}/api/admin/products/${editingProduct.product_id}/variants/${i}/full`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            
            body: JSON.stringify(variantPayload)
          });
        } else {
          // Add new variant using correct endpoint
          await adminFetch(`${API_URL}/api/admin/products/${editingProduct.product_id}/variants`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            
            body: JSON.stringify({
              size: variant.size,
              price: variant.price,
              stock_quantity: variant.stock_quantity || 50,
              in_stock: variant.in_stock !== false
            })
          });
        }
      }

      toast.success('Product updated successfully');
      setShowEditProduct(false);
      fetchProducts();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Product Visibility Functions
  const openVisibilityModal = (product) => {
    setVisibilityProduct(product);
    setSelectedTiers(product.visible_to_tiers || []);
    setShowVisibilityModal(true);
  };

  const handleToggleTierVisibility = (tierId) => {
    setSelectedTiers(prev => 
      prev.includes(tierId) 
        ? prev.filter(id => id !== tierId)
        : [...prev, tierId]
    );
  };

  const handleSaveVisibility = async () => {
    if (!visibilityProduct) return;
    
    setLoading(true);
    try {
      const response = await adminFetch(`${API_URL}/api/admin/products/${visibilityProduct.product_id}/visibility`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({ visible_to_tiers: selectedTiers })
      });

      if (!response.ok) {
        throw new Error('Failed to update visibility');
      }

      // Update local state
      setProducts(prev => prev.map(p => 
        p.product_id === visibilityProduct.product_id 
          ? { ...p, visible_to_tiers: selectedTiers }
          : p
      ));

      toast.success(selectedTiers.length 
        ? 'Product visibility restricted to selected tiers' 
        : 'Product visible to all customers'
      );
      setShowVisibilityModal(false);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getTierNamesForProduct = (product) => {
    if (!product.visible_to_tiers || product.visible_to_tiers.length === 0) {
      return null;
    }
    return pricingTiers
      .filter(t => product.visible_to_tiers.includes(t.tier_id))
      .map(t => t.name);
  };

  // Staff Management Functions
  const openStaffModal = (staff = null) => {
    // Ensure customers are loaded for the selector
    if (customers.length === 0) {
      fetchCustomers();
    }
    
    if (staff) {
      setEditingStaff(staff);
      setStaffForm({
        name: staff.name,
        email: staff.email,
        password: '', // Don't show existing password
        role: staff.role,
        fromCustomer: false,
        customerId: ''
      });
    } else {
      setEditingStaff(null);
      setStaffForm({ name: '', email: '', password: '', role: 'inventory_manager', fromCustomer: false, customerId: '' });
    }
    setShowStaffModal(true);
  };

  const handleSelectCustomerForStaff = (customer) => {
    setStaffForm(prev => ({
      ...prev,
      name: customer.name || '',
      email: customer.email || '',
      fromCustomer: true,
      customerId: customer.customer_id
    }));
  };

  const handleSaveStaff = async () => {
    if (!staffForm.name || !staffForm.email) {
      toast.error('Name and email are required');
      return;
    }
    if (!editingStaff && !staffForm.password) {
      toast.error('Password is required for new staff');
      return;
    }

    setLoading(true);
    try {
      const url = editingStaff 
        ? `${API_URL}/api/admin/staff/${editingStaff.staff_id}`
        : `${API_URL}/api/admin/staff`;
      
      const body = editingStaff
        ? { name: staffForm.name, role: staffForm.role, ...(staffForm.password && { password: staffForm.password }) }
        : { 
            name: staffForm.name, 
            email: staffForm.email, 
            password: staffForm.password, 
            role: staffForm.role,
            ...(staffForm.customerId && { customer_id: staffForm.customerId })
          };

      const response = await adminFetch(url, {
        method: editingStaff ? 'PUT' : 'POST',
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to save staff');
      }

      toast.success(editingStaff ? 'Staff member updated' : 'Staff member created');
      setShowStaffModal(false);
      fetchStaff();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStaff = async (staffId, staffName) => {
    // Use window.confirm explicitly to ensure cross-browser compatibility
    const confirmed = window.confirm(`Are you sure you want to remove ${staffName} from staff?`);
    if (!confirmed) return;

    try {
      const response = await adminFetch(`${API_URL}/api/admin/staff/${staffId}`, {
        method: 'DELETE',
        
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete staff member');
      }
      
      toast.success(`${staffName} has been removed from staff`);
      fetchStaff();
    } catch (error) {
      console.error('Delete staff error:', error);
      toast.error(error.message || 'Failed to delete staff member');
    }
  };

  const handleToggleStaffActive = async (staff) => {
    try {
      const response = await adminFetch(`${API_URL}/api/admin/staff/${staff.staff_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({ is_active: !staff.is_active })
      });

      if (!response.ok) throw new Error('Failed to update');
      toast.success(`Staff member ${staff.is_active ? 'deactivated' : 'activated'}`);
      fetchStaff();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // COA Functions
  const openCoaModal = (product) => {
    const existingCoa = coas.find(c => c.product_id === product.product_id);
    if (existingCoa && existingCoa.has_coa) {
      setCoaForm({
        batch_number: existingCoa.batch_number || '',
        purity: existingCoa.purity || product.purity,
        test_date: existingCoa.test_date || ''
      });
    } else {
      setCoaForm({
        batch_number: '',
        purity: product.purity || '≥99%',
        test_date: new Date().toISOString().split('T')[0]
      });
    }
    setCoaPdf(null);
    setCoaUseUrl(false);
    setCoaUrl('');
    setShowCoaModal(product);
  };

  const handleUploadCoa = async (e) => {
    e.preventDefault();
    if (!showCoaModal) return;
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('batch_number', coaForm.batch_number);
      formData.append('purity', coaForm.purity);
      formData.append('test_date', coaForm.test_date);
      if (coaUseUrl && coaUrl.trim()) {
        formData.append('external_file_url', coaUrl.trim());
      } else if (coaPdf) {
        formData.append('coa_file', coaPdf);
      }
      
      const response = await adminFetch(`${API_URL}/api/admin/coas/${showCoaModal.product_id}`, {
        method: 'POST',
        
        body: formData
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to upload COA');
      }

      await fetchCoas();
      setShowCoaModal(null);
      setCoaForm({ batch_number: '', purity: '', test_date: '' });
      setCoaPdf(null);
      toast.success(`COA uploaded for ${showCoaModal.name}`);
    } catch (error) {
      toast.error(error.message || 'Failed to upload COA');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCoa = async (productId, productName) => {
    if (!window.confirm(`Delete COA for "${productName}"?`)) return;
    
    try {
      const response = await adminFetch(`${API_URL}/api/admin/coas/${productId}`, {
        method: 'DELETE',
        
      });

      if (!response.ok) {
        throw new Error('Failed to delete COA');
      }

      await fetchCoas();
      toast.success('COA deleted');
    } catch (error) {
      toast.error(error.message);
    }
  };

  // CSV Functions
  const handleExportCsv = async () => {
    try {
      const response = await adminFetch(`${API_URL}/api/admin/inventory/export`, {
        
      });
      
      if (!response.ok) {
        throw new Error('Failed to export');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `genora_inventory_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Inventory exported to CSV');
    } catch (error) {
      toast.error('Failed to export inventory');
    }
  };

  const handleImportCsv = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setLoading(true);
    setCsvImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      
      const response = await adminFetch(`${API_URL}/api/admin/inventory/import`, {
        method: 'POST',
        
        body: formData
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.detail || 'Import failed');
      }
      
      setCsvImportResult(result);
      await fetchProducts();
      
      if (result.errors?.length === 0) {
        toast.success(result.message);
      } else {
        toast.warning(`${result.message} (${result.errors?.length} errors)`);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to import CSV');
    } finally {
      setLoading(false);
    }
  };

  // Article Functions
  const handleArticleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setArticleImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setArticleImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const openArticleModal = (article = null) => {
    if (article) {
      setEditingArticle(article);
      setArticleForm({
        title: article.title || '',
        excerpt: article.excerpt || '',
        content: article.content || '',
        author: article.author || 'Genora Research Team',
        meta_title: article.meta_title || '',
        meta_description: article.meta_description || '',
        meta_keywords: article.meta_keywords || '',
        status: article.status || 'published',
        scheduled_at: article.scheduled_at ? article.scheduled_at.slice(0, 16) : '',
        notify_on_publish: article.notify_on_publish || false
      });
      setArticleImagePreview((article.image_url?.startsWith('/uploads') || article.image_url?.startsWith('/api/uploads')) ? `${API_URL}${article.image_url}` : article.image_url);
    } else {
      setEditingArticle(null);
      setArticleForm({ 
        title: '', 
        excerpt: '', 
        content: '', 
        author: 'Genora Research Team',
        meta_title: '',
        meta_description: '',
        meta_keywords: '',
        status: 'published',
        scheduled_at: '',
        notify_on_publish: false
      });
      setArticleImagePreview(null);
    }
    setArticleImage(null);
    setShowArticleModal(true);
  };

  const handleSaveArticle = async (e) => {
    e.preventDefault();
    
    if (!articleForm.title || !articleForm.content) {
      toast.error('Title and content are required');
      return;
    }

    if (articleForm.status === 'scheduled' && !articleForm.scheduled_at) {
      toast.error('Please select a scheduled date and time');
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('title', articleForm.title);
      formData.append('excerpt', articleForm.excerpt);
      formData.append('content', articleForm.content);
      formData.append('author', articleForm.author);
      formData.append('meta_title', articleForm.meta_title);
      formData.append('meta_description', articleForm.meta_description);
      formData.append('meta_keywords', articleForm.meta_keywords);
      formData.append('status', articleForm.status);
      formData.append('notify_on_publish', articleForm.notify_on_publish.toString());
      if (articleForm.status === 'scheduled' && articleForm.scheduled_at) {
        formData.append('scheduled_at', new Date(articleForm.scheduled_at).toISOString());
      }
      
      // Handle image - either file upload or URL
      if (articleForm.useImageUrl && articleForm.imageUrl) {
        formData.append('image_url', articleForm.imageUrl);
      } else if (articleImage) {
        formData.append('image', articleImage);
      }
      
      const url = editingArticle 
        ? `${API_URL}/api/admin/articles/${editingArticle.post_id}`
        : `${API_URL}/api/admin/articles`;
      
      const response = await fetch(url, {
        method: editingArticle ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: formData
      });

      if (!response.ok) {
        const errText = await response.text();
        let errMsg = 'Failed to save article';
        try { errMsg = JSON.parse(errText).detail || errMsg; } catch {}
        throw new Error(errMsg);
      }

      await fetchArticles();
      setShowArticleModal(false);
      setEditingArticle(null);
      setArticleForm({ 
        title: '', 
        excerpt: '', 
        content: '', 
        author: 'Genora Research Team',
        meta_title: '',
        meta_description: '',
        meta_keywords: '',
        status: 'published',
        scheduled_at: '',
        notify_on_publish: false,
        useImageUrl: false,
        imageUrl: ''
      });
      setArticleImage(null);
      setArticleImagePreview(null);
      toast.success(editingArticle ? 'Article updated' : 'Article created');
    } catch (error) {
      toast.error(error.message || 'Failed to save article');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteArticle = async (postId, title) => {
    if (!window.confirm(`Delete article "${title}"?`)) return;
    
    try {
      const response = await adminFetch(`${API_URL}/api/admin/articles/${postId}`, {
        method: 'DELETE',
        
      });

      if (!response.ok) {
        throw new Error('Failed to delete');
      }

      setArticles(prev => prev.filter(a => a.post_id !== postId));
      toast.success('Article deleted');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleNotifyCustomersAboutArticle = async (postId, title) => {
    if (!window.confirm(`Send email notification about "${title}" to ALL customers?\n\nThis will send an email to every registered customer and user.`)) return;
    
    try {
      const response = await adminFetch(`${API_URL}/api/admin/articles/${postId}/notify-customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ site_url: window.location.origin })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send notifications');
      }

      toast.success(`📧 Email sent to ${data.sent_count} customers!`);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const filteredCoas = coas.filter(coa =>
    coa.product_name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const filteredArticles = articles.filter(article =>
    article.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Login/Setup Screen
  if (!isAuthenticated) {
    // Show setup form for first-time setup
    if (setupMode && adminStatus && !adminStatus.admin_configured) {
      return (
        <div className="min-h-screen bg-slate-900 flex items-center justify-center px-6 py-12">
          <Card className="w-full max-w-md border-slate-700 bg-slate-800">
            <CardHeader className="text-center">
              <div className="w-16 h-16 rounded-full bg-teal-900/50 flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-teal-400" />
              </div>
              <CardTitle className="text-white text-xl">Admin Setup</CardTitle>
              <p className="text-slate-400 text-sm">Create your admin account to get started</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAdminSetup} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="setup-name" className="text-slate-300">Your Name</Label>
                  <Input
                    id="setup-name"
                    type="text"
                    placeholder="John Doe"
                    value={setupForm.name}
                    onChange={(e) => setSetupForm({...setupForm, name: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white"
                    required
                    data-testid="setup-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="setup-email" className="text-slate-300">Gmail Address</Label>
                  <Input
                    id="setup-email"
                    type="email"
                    placeholder="your.email@gmail.com"
                    value={setupForm.email}
                    onChange={(e) => setSetupForm({...setupForm, email: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white"
                    required
                    data-testid="setup-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="setup-password" className="text-slate-300">Password</Label>
                  <Input
                    id="setup-password"
                    type="password"
                    placeholder="Min 6 characters"
                    value={setupForm.password}
                    onChange={(e) => setSetupForm({...setupForm, password: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white"
                    required
                    minLength={6}
                    data-testid="setup-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="setup-confirm" className="text-slate-300">Confirm Password</Label>
                  <Input
                    id="setup-confirm"
                    type="password"
                    placeholder="Confirm password"
                    value={setupForm.confirmPassword}
                    onChange={(e) => setSetupForm({...setupForm, confirmPassword: e.target.value})}
                    className="bg-slate-700 border-slate-600 text-white"
                    required
                    data-testid="setup-confirm"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white"
                  disabled={settingUp}
                  data-testid="setup-submit-btn"
                >
                  {settingUp ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Creating Account...
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Create Admin Account
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Show login form
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-6">
        <Card className="w-full max-w-md border-slate-700 bg-slate-800">
          <CardHeader className="text-center">
            <div className="w-16 h-16 rounded-full bg-teal-900/50 flex items-center justify-center mx-auto mb-4">
              <Lock className="h-8 w-8 text-teal-400" />
            </div>
            <CardTitle className="text-white">Admin Login</CardTitle>
            <p className="text-slate-400 text-sm">
              Enter your credentials to access inventory management
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="admin-email" className="text-slate-300">Email</Label>
                <Input
                  id="admin-email"
                  type="email"
                  placeholder="admin@genora.com"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white"
                  data-testid="admin-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter admin password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white"
                  data-testid="admin-password"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-teal-600 hover:bg-teal-700 text-white"
                disabled={loading}
                data-testid="admin-login-btn"
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
            <div className="mt-5 pt-4 border-t border-slate-700 text-center">
              <p className="text-slate-400 text-sm">
                Staff member?{' '}
                <a href="/staff" className="text-teal-400 hover:text-teal-300 font-medium underline" data-testid="staff-portal-link">
                  Sign in at the Staff Portal →
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Add Product Modal */}
      {showAddProduct && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold text-slate-900">Add New Product</h2>
              <button
                onClick={() => {
                  setShowAddProduct(false);
                  setProductImage(null);
                  setImagePreview(null);
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleCreateProduct} className="p-6 space-y-4">
              {/* Image Upload or URL */}
              <div className="space-y-2">
                <Label>Product Image</Label>
                <div className="flex gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => setNewProduct(prev => ({ ...prev, useImageUrl: false }))}
                    className={`px-3 py-1 text-sm rounded ${!newProduct.useImageUrl ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600'}`}
                  >
                    Upload File
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewProduct(prev => ({ ...prev, useImageUrl: true }))}
                    className={`px-3 py-1 text-sm rounded ${newProduct.useImageUrl ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600'}`}
                  >
                    Paste URL
                  </button>
                </div>
                
                {newProduct.useImageUrl ? (
                  <div className="space-y-2">
                    <Input
                      placeholder="https://lh3.googleusercontent.com/d/FILE_ID"
                      value={newProduct.imageUrl || ''}
                      onChange={(e) => {
                        let url = e.target.value;
                        // Auto-convert Google Drive share links to direct links
                        if (url.includes('drive.google.com/file/d/')) {
                          const match = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
                          if (match) {
                            url = `https://lh3.googleusercontent.com/d/${match[1]}`;
                          }
                        }
                        setNewProduct(prev => ({ ...prev, imageUrl: url }));
                        setImagePreview(url);
                      }}
                      data-testid="product-image-url"
                    />
                    <p className="text-xs text-slate-500">
                      Paste Google Drive link or any public image URL.<br/>
                      For Google Drive: Share file → "Anyone with link" → Copy link
                    </p>
                    {imagePreview && newProduct.useImageUrl && (
                      <div className="mt-2 p-2 border rounded">
                        <img src={imagePreview} alt="Preview" className="max-h-32 mx-auto rounded" onError={(e) => e.target.style.display='none'} />
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-teal-400 hover:bg-teal-50/30 transition-colors"
                    >
                      {imagePreview && !newProduct.useImageUrl ? (
                        <div className="relative">
                          <img src={imagePreview} alt="Preview" className="max-h-40 mx-auto rounded" />
                          <p className="text-sm text-slate-500 mt-2">Click to change image</p>
                        </div>
                      ) : (
                        <>
                          <Upload className="h-10 w-10 text-slate-400 mx-auto mb-2" />
                          <p className="text-sm text-slate-600">Click to upload product image</p>
                          <p className="text-xs text-slate-400">PNG, JPG, WebP up to 5MB</p>
                        </>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                      data-testid="product-image-input"
                    />
                  </>
                )}
              </div>
              
              {/* Product Name */}
              <div className="space-y-2">
                <Label htmlFor="product-name">Product Name *</Label>
                <Input
                  id="product-name"
                  placeholder="e.g., BPC-157"
                  value={newProduct.name}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, name: e.target.value }))}
                  required
                  data-testid="new-product-name"
                />
              </div>
              
              {/* Category */}
              <div className="space-y-2">
                <Label htmlFor="product-category">Category *</Label>
                <select
                  id="product-category"
                  value={newProduct.category}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                  data-testid="new-product-category"
                >
                  <option value="metabolic">Metabolic Research</option>
                  <option value="recovery-repair">General Research Peptides</option>
                  <option value="cognitive">Cognitive Research</option>
                  <option value="longevity">Cellular Research</option>
                </select>
              </div>
              
              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="product-description">Description</Label>
                <textarea
                  id="product-description"
                  placeholder="Product description..."
                  value={newProduct.description}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm resize-none"
                  data-testid="new-product-description"
                />
              </div>
              
              {/* Purity */}
              <div className="space-y-2">
                <Label htmlFor="product-purity">Purity</Label>
                <Input
                  id="product-purity"
                  placeholder="≥99%"
                  value={newProduct.purity}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, purity: e.target.value }))}
                  data-testid="new-product-purity"
                />
              </div>
              
              {/* Initial Variant */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <p className="font-medium text-slate-700 text-sm">Initial Size Option</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Size *</Label>
                    <Input
                      placeholder="5mg"
                      value={newProduct.variant_size}
                      onChange={(e) => setNewProduct(prev => ({ ...prev, variant_size: e.target.value }))}
                      required
                      data-testid="new-product-variant-size"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Price ($) *</Label>
                    <Input
                      type="number"
                      placeholder="50.00"
                      step="0.01"
                      min="0"
                      value={newProduct.variant_price}
                      onChange={(e) => setNewProduct(prev => ({ ...prev, variant_price: e.target.value }))}
                      required
                      data-testid="new-product-variant-price"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Stock Qty</Label>
                    <Input
                      type="number"
                      placeholder="50"
                      min="0"
                      value={newProduct.variant_quantity}
                      onChange={(e) => setNewProduct(prev => ({ ...prev, variant_quantity: e.target.value }))}
                      data-testid="new-product-variant-qty"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowAddProduct(false);
                    setProductImage(null);
                    setImagePreview(null);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
                  data-testid="create-product-btn"
                >
                  {loading ? 'Creating...' : 'Create Product'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Product Modal */}
      {showEditProduct && editingProduct && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <h2 className="font-heading text-xl font-bold text-slate-900">Edit Product</h2>
                <p className="text-sm text-slate-500">Update {editingProduct.name}</p>
              </div>
              <button
                onClick={() => {
                  setShowEditProduct(false);
                  setEditingProduct(null);
                  setEditProductImage(null);
                  setEditProductImagePreview(null);
                  setEditUseExternalUrl(false);
                  setEditExternalImageUrl('');
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Image Upload or URL */}
              <div className="space-y-2">
                <Label>Product Image</Label>
                <div className="flex gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => setEditUseExternalUrl(false)}
                    className={`px-3 py-1 text-sm rounded ${!editUseExternalUrl ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600'}`}
                    data-testid="edit-product-upload-tab"
                  >
                    Upload File
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditUseExternalUrl(true)}
                    className={`px-3 py-1 text-sm rounded ${editUseExternalUrl ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600'}`}
                    data-testid="edit-product-url-tab"
                  >
                    Paste URL
                  </button>
                </div>
                
                {editUseExternalUrl ? (
                  <div className="space-y-2">
                    <Input
                      placeholder="https://drive.google.com/... or any public image URL"
                      value={editExternalImageUrl}
                      onChange={(e) => {
                        setEditExternalImageUrl(e.target.value);
                        if (e.target.value.trim()) setEditProductImagePreview(e.target.value.trim());
                      }}
                      data-testid="edit-product-external-url"
                    />
                    <p className="text-xs text-slate-500">
                      Paste Google Drive link or any public image URL. Google Drive share links are auto-converted.
                    </p>
                    {editProductImagePreview && editUseExternalUrl && (
                      <div className="mt-2 p-2 border rounded">
                        <img src={editProductImagePreview} alt="Preview" className="max-h-32 mx-auto rounded" onError={(e) => e.target.style.display='none'} />
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div 
                      onClick={() => editProductImageRef.current?.click()}
                      className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-teal-400 hover:bg-teal-50/30 transition-colors"
                    >
                      {editProductImagePreview && !editUseExternalUrl ? (
                        <div className="relative">
                          <img src={editProductImagePreview} alt="Preview" className="max-h-40 mx-auto rounded" />
                          <p className="text-sm text-slate-500 mt-2">Click to change image</p>
                        </div>
                      ) : (
                        <>
                          <ImageIcon className="h-10 w-10 text-slate-400 mx-auto mb-2" />
                          <p className="text-sm text-slate-600">Click to upload product image</p>
                          <p className="text-xs text-slate-400">PNG, JPG, WebP up to 5MB</p>
                        </>
                      )}
                    </div>
                    <input
                      ref={editProductImageRef}
                      type="file"
                      accept="image/*"
                      onChange={handleEditProductImageChange}
                      className="hidden"
                      data-testid="edit-product-image-input"
                    />
                  </>
                )}
              </div>
              
              {/* Product Name */}
              <div className="space-y-2">
                <Label htmlFor="edit-product-name">Product Name *</Label>
                <Input
                  id="edit-product-name"
                  value={editProductForm.name}
                  onChange={(e) => setEditProductForm(prev => ({ ...prev, name: e.target.value }))}
                  data-testid="edit-product-name"
                />
              </div>
              
              {/* Category */}
              <div className="space-y-2">
                <Label htmlFor="edit-product-category">Category</Label>
                <select
                  id="edit-product-category"
                  value={editProductForm.category}
                  onChange={(e) => setEditProductForm(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                  data-testid="edit-product-category"
                >
                  <option value="metabolic">Metabolic Research</option>
                  <option value="recovery-repair">General Research Peptides</option>
                  <option value="cognitive">Cognitive Research</option>
                  <option value="longevity">Cellular Research</option>
                </select>
              </div>
              
              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="edit-product-description">Description</Label>
                <textarea
                  id="edit-product-description"
                  value={editProductForm.description}
                  onChange={(e) => setEditProductForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={4}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm resize-none"
                  data-testid="edit-product-description"
                />
              </div>
              
              {/* Purity */}
              <div className="space-y-2">
                <Label htmlFor="edit-product-purity">Purity</Label>
                <Input
                  id="edit-product-purity"
                  value={editProductForm.purity}
                  onChange={(e) => setEditProductForm(prev => ({ ...prev, purity: e.target.value }))}
                  data-testid="edit-product-purity"
                />
              </div>

              {/* Member Discount Eligibility (Apr 23, 2026) */}
              <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4 flex items-start justify-between gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-900">Member Discount Eligible</Label>
                  <p className="text-xs text-slate-500 mt-1">
                    When OFF, this product is excluded from member tier discounts and discount codes (no double-dipping).
                  </p>
                </div>
                <Switch
                  checked={editProductForm.member_discount_eligible !== false}
                  onCheckedChange={(checked) => setEditProductForm(prev => ({ ...prev, member_discount_eligible: checked }))}
                  data-testid="edit-product-member-discount-eligible"
                />
              </div>

              {/* Allow Pre-Orders when out of stock (Jun 1, 2026) */}
              <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4 flex items-start justify-between gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-900">Allow Pre-Orders</Label>
                  <p className="text-xs text-slate-500 mt-1">
                    When OFF (default), customers cannot add this product once it's out of stock — they'll see a disabled "Out of Stock" button. Turn ON to let customers pre-order while out of stock.
                  </p>
                </div>
                <Switch
                  checked={!!editProductForm.allow_preorder}
                  onCheckedChange={(checked) => setEditProductForm(prev => ({ ...prev, allow_preorder: checked }))}
                  data-testid="edit-product-allow-preorder"
                />
              </div>

              {/* Temporary Sale (product-level) (Apr 23, 2026) */}
              <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-4 space-y-3">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-900">Temporary Sale</Label>
                    <p className="text-xs text-slate-500 mt-1">
                      Apply a discount to all variants. Per-variant overrides take precedence below.
                    </p>
                  </div>
                  <Switch
                    checked={!!editProductForm.sale_active}
                    onCheckedChange={(checked) => setEditProductForm(prev => ({ ...prev, sale_active: checked }))}
                    data-testid="edit-product-sale-active"
                  />
                </div>
                {editProductForm.sale_active && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-amber-200">
                    <div>
                      <Label className="text-xs text-slate-500">Discount Type</Label>
                      <select
                        value={editProductForm.sale_type}
                        onChange={(e) => setEditProductForm(prev => ({ ...prev, sale_type: e.target.value }))}
                        className="w-full mt-1 border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
                        data-testid="edit-product-sale-type"
                      >
                        <option value="percent">Percent off (%)</option>
                        <option value="fixed">Fixed sale price ($)</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">
                        {editProductForm.sale_type === 'fixed' ? 'Sale Price ($)' : 'Discount %'}
                      </Label>
                      <Input
                        type="number"
                        value={editProductForm.sale_value}
                        onChange={(e) => setEditProductForm(prev => ({ ...prev, sale_value: e.target.value }))}
                        placeholder={editProductForm.sale_type === 'fixed' ? '39.99' : '15'}
                        step="0.01"
                        min="0"
                        className="mt-1"
                        data-testid="edit-product-sale-value"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Sale Starts (optional)</Label>
                      <Input
                        type="date"
                        value={editProductForm.sale_starts_at || ''}
                        onChange={(e) => setEditProductForm(prev => ({ ...prev, sale_starts_at: e.target.value }))}
                        className="mt-1"
                        data-testid="edit-product-sale-starts-at"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Sale Ends (optional)</Label>
                      <Input
                        type="date"
                        value={editProductForm.sale_ends_at || ''}
                        onChange={(e) => setEditProductForm(prev => ({ ...prev, sale_ends_at: e.target.value }))}
                        className="mt-1"
                        data-testid="edit-product-sale-ends-at"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Variants */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Size Variants</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddVariantInEdit}
                    className="text-teal-600"
                  >
                    <PlusCircle className="h-4 w-4 mr-1" />
                    Add Variant
                  </Button>
                </div>
                
                <div className="space-y-3">
                  {editProductForm.variants.map((variant, index) => (
                    <div key={index} className="flex flex-col gap-3 p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 grid grid-cols-4 gap-3">
                        <div>
                          <Label className="text-xs text-slate-500">Size</Label>
                          <Input
                            value={variant.size}
                            onChange={(e) => handleUpdateVariantInEdit(index, 'size', e.target.value)}
                            placeholder="5mg"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Price ($)</Label>
                          <Input
                            type="number"
                            value={variant.price}
                            onChange={(e) => handleUpdateVariantInEdit(index, 'price', e.target.value)}
                            placeholder="50.00"
                            step="0.01"
                            min="0"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Stock Qty</Label>
                          <Input
                            type="number"
                            value={variant.stock_quantity}
                            onChange={(e) => handleUpdateVariantInEdit(index, 'stock_quantity', e.target.value)}
                            min="0"
                            className="mt-1"
                          />
                        </div>
                        <div className="flex items-end justify-between">
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={variant.in_stock}
                              onCheckedChange={(checked) => handleUpdateVariantInEdit(index, 'in_stock', checked)}
                            />
                            <span className="text-xs text-slate-500">In Stock</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveVariantInEdit(index)}
                            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-30"
                            disabled={editProductForm.variants.length <= 1}
                            title={editProductForm.variants.length <= 1 ? "Cannot remove last variant" : "Remove variant"}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        </div>
                      </div>
                      {/* Per-variant sale override (Apr 23, 2026) */}
                      <div className="border-t border-slate-200 pt-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!variant._has_variant_sale_override}
                            onChange={(e) => handleUpdateVariantInEdit(index, '_has_variant_sale_override', e.target.checked)}
                            className="h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                            data-testid={`variant-${index}-sale-override`}
                          />
                          <span className="text-xs font-medium text-slate-700">Override sale for this size</span>
                        </label>
                        {variant._has_variant_sale_override && (
                          <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={!!variant.sale_active}
                                onCheckedChange={(checked) => handleUpdateVariantInEdit(index, 'sale_active', checked)}
                              />
                              <span className="text-xs text-slate-600">On Sale</span>
                            </div>
                            <select
                              value={variant.sale_type || 'percent'}
                              onChange={(e) => handleUpdateVariantInEdit(index, 'sale_type', e.target.value)}
                              className="border border-slate-200 rounded px-2 py-1 text-xs bg-white"
                            >
                              <option value="percent">% off</option>
                              <option value="fixed">$ fixed</option>
                            </select>
                            <Input
                              type="number"
                              value={variant.sale_value ?? ''}
                              onChange={(e) => handleUpdateVariantInEdit(index, 'sale_value', e.target.value)}
                              placeholder={variant.sale_type === 'fixed' ? '39.99' : '15'}
                              step="0.01"
                              min="0"
                              className="text-xs"
                            />
                            <div className="flex gap-1">
                              <Input
                                type="date"
                                value={variant.sale_starts_at || ''}
                                onChange={(e) => handleUpdateVariantInEdit(index, 'sale_starts_at', e.target.value)}
                                className="text-xs"
                                title="Sale starts"
                              />
                              <Input
                                type="date"
                                value={variant.sale_ends_at || ''}
                                onChange={(e) => handleUpdateVariantInEdit(index, 'sale_ends_at', e.target.value)}
                                className="text-xs"
                                title="Sale ends"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-slate-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowEditProduct(false);
                    setEditingProduct(null);
                    setEditProductImage(null);
                    setEditProductImagePreview(null);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveEditProduct}
                  disabled={loading}
                  className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
                  data-testid="save-edit-product-btn"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      {showCsvModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="font-heading text-lg font-bold text-slate-900">Import Inventory from CSV</h2>
                <p className="text-sm text-slate-500">Bulk update stock quantities and prices</p>
              </div>
              <button
                onClick={() => {
                  setShowCsvModal(false);
                  setCsvFile(null);
                  setCsvImportResult(null);
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 mb-2">CSV Format</h4>
                <p className="text-sm text-blue-700 mb-2">Required columns:</p>
                <ul className="text-xs text-blue-600 space-y-1 font-mono">
                  <li>• product_slug (e.g., "aod-9604")</li>
                  <li>• variant_size (e.g., "5mg")</li>
                  <li>• stock_quantity (number)</li>
                  <li>• in_stock (TRUE/FALSE)</li>
                  <li>• variant_price (optional, e.g., "45.00")</li>
                </ul>
                <p className="text-sm text-blue-700 mt-3">
                  <strong>Tip:</strong> Export your current inventory first, edit in Excel, then import.
                </p>
              </div>
              
              {/* File Upload */}
              <div 
                onClick={() => csvFileRef.current?.click()}
                className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-teal-400 hover:bg-teal-50/30 transition-colors"
              >
                {csvFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileSpreadsheet className="h-6 w-6 text-teal-600" />
                    <span className="text-sm text-slate-700">{csvFile.name}</span>
                  </div>
                ) : (
                  <>
                    <FileSpreadsheet className="h-10 w-10 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-600">Click to select CSV file</p>
                    <p className="text-xs text-slate-400">.csv files from Excel</p>
                  </>
                )}
              </div>
              <input
                ref={csvFileRef}
                type="file"
                accept=".csv"
                onChange={(e) => setCsvFile(e.target.files[0])}
                className="hidden"
              />
              
              {/* Import Result */}
              {csvImportResult && (
                <div className={`rounded-lg p-4 ${csvImportResult.errors?.length > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'}`}>
                  <p className={`font-medium mb-2 ${csvImportResult.errors?.length > 0 ? 'text-amber-800' : 'text-green-800'}`}>
                    {csvImportResult.message}
                  </p>
                  {csvImportResult.updated?.length > 0 && (
                    <div className="mb-2">
                      <p className="text-xs font-medium text-green-700">Updated:</p>
                      <p className="text-xs text-green-600">{csvImportResult.updated.join(', ')}</p>
                    </div>
                  )}
                  {csvImportResult.errors?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-amber-700">Errors:</p>
                      <ul className="text-xs text-amber-600 max-h-32 overflow-y-auto">
                        {csvImportResult.errors.map((err, i) => (
                          <li key={i}>• {err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={handleExportCsv}
                  className="flex-1"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export Current
                </Button>
                <Button
                  onClick={handleImportCsv}
                  disabled={!csvFile || loading}
                  className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
                >
                  {loading ? 'Importing...' : 'Import CSV'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <Package className="h-6 w-6 sm:h-8 sm:w-8 text-teal-400" />
              <div>
                <h1 className="font-heading text-lg sm:text-xl font-bold">Admin Panel</h1>
                <p className="text-slate-400 text-xs sm:text-sm hidden sm:block">Manage products, stock & COAs</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              {activeTab === 'inventory' && (
                <>
                  <Button 
                    size="sm"
                    variant="outline"
                    onClick={() => setShowCsvModal(true)}
                    className="border-slate-600 text-slate-300 hover:bg-slate-800 text-xs sm:text-sm"
                    data-testid="csv-btn"
                  >
                    <FileSpreadsheet className="h-4 w-4 sm:mr-2" />
                    <span className="hidden sm:inline">CSV Import/Export</span>
                  </Button>
                  <Button 
                    size="sm"
                    onClick={() => setShowAddProduct(true)}
                    className="bg-teal-600 hover:bg-teal-700 text-white text-xs sm:text-sm"
                    data-testid="add-product-btn"
                  >
                    <PlusCircle className="h-4 w-4 sm:mr-2" />
                    <span className="hidden sm:inline">Add Product</span>
                  </Button>
                </>
              )}
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  fetchProducts();
                  if (activeTab === 'coas') fetchCoas();
                }}
                className="border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleLogout}
                className="border-slate-600 text-slate-300 hover:bg-slate-800"
                data-testid="admin-logout"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {/* Tabs: dropdown on mobile, wrapping pills on desktop (no horizontal scroll) */}
          <div className="sm:hidden mt-4">
            <label htmlFor="admin-tab-select" className="sr-only">Select admin section</label>
            <select
              id="admin-tab-select"
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
              className="w-full bg-slate-800 text-white border border-slate-700 rounded-lg px-3 py-3 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-teal-500"
              data-testid="admin-tab-select"
            >
              <option value="sales">Sales</option>
              <option value="inventory">Inventory</option>
              <option value="accessories">Accessories</option>
              <option value="coas">COAs</option>
              <option value="articles">Articles</option>
              <option value="ai-content">AI Content</option>
              <option value="affiliates">Affiliates</option>
              <option value="customers">Customers</option>
              <option value="custom-orders">Custom Orders</option>
              <option value="wholesale-catalog">Wholesale</option>
              <option value="lifecycle">Re-engagement</option>
              <option value="broadcast">Broadcast</option>
              <option value="resources">Resources</option>
              <option value="settings">Settings</option>
            </select>
          </div>
          <div className="hidden sm:flex flex-wrap gap-1.5 mt-6 pb-2">
            <button
              onClick={() => setActiveTab('sales')}
              className={`px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${
                activeTab === 'sales' 
                  ? 'bg-white text-slate-900 shadow-sm' 
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
              }`}
              data-testid="tab-sales"
            >
              <TrendingUp className="h-4 w-4 inline mr-1 sm:mr-2" />
              Sales
            </button>
            <button
              onClick={() => setActiveTab('inventory')}
              className={`px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${
                activeTab === 'inventory' 
                  ? 'bg-white text-slate-900 shadow-sm' 
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
              }`}
              data-testid="tab-inventory"
            >
              <Package className="h-4 w-4 inline mr-1 sm:mr-2" />
              Inventory
            </button>
            <button
              onClick={() => setActiveTab('accessories')}
              className={`px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${
                activeTab === 'accessories' 
                  ? 'bg-white text-slate-900 shadow-sm' 
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
              }`}
              data-testid="tab-accessories"
            >
              <Tag className="h-4 w-4 inline mr-1 sm:mr-2" />
              Accessories
            </button>
            <button
              onClick={() => setActiveTab('coas')}
              className={`px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${
                activeTab === 'coas' 
                  ? 'bg-white text-slate-900 shadow-sm' 
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
              }`}
              data-testid="tab-coas"
            >
              <FileText className="h-4 w-4 inline mr-1 sm:mr-2" />
              COAs
            </button>
            <button
              onClick={() => setActiveTab('articles')}
              className={`px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${
                activeTab === 'articles' 
                  ? 'bg-white text-slate-900 shadow-sm' 
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
              }`}
              data-testid="tab-articles"
            >
              <BookOpen className="h-4 w-4 inline mr-1 sm:mr-2" />
              Articles
            </button>
            <button
              onClick={() => setActiveTab('ai-content')}
              className={`px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${
                activeTab === 'ai-content' 
                  ? 'bg-white text-slate-900 shadow-sm' 
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
              }`}
              data-testid="tab-ai-content"
            >
              <Sparkles className="h-4 w-4 inline mr-1 sm:mr-2" />
              AI Content
            </button>
            <button
              onClick={() => setActiveTab('affiliates')}
              className={`px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${
                activeTab === 'affiliates' 
                  ? 'bg-white text-slate-900 shadow-sm' 
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
              }`}
              data-testid="tab-affiliates"
            >
              <Award className="h-4 w-4 inline mr-1 sm:mr-2" />
              Affiliates
            </button>
            <button
              onClick={() => setActiveTab('customers')}
              className={`px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${
                activeTab === 'customers' 
                  ? 'bg-white text-slate-900 shadow-sm' 
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
              }`}
              data-testid="tab-customers"
            >
              <Users className="h-4 w-4 inline mr-1 sm:mr-2" />
              Customers
            </button>
            <button
              onClick={() => setActiveTab('custom-orders')}
              className={`px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${
                activeTab === 'custom-orders'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
              }`}
              data-testid="tab-custom-orders"
            >
              <DollarSign className="h-4 w-4 inline mr-1 sm:mr-2" />
              Custom Orders
            </button>
            <button
              onClick={() => setActiveTab('wholesale-catalog')}
              className={`px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${
                activeTab === 'wholesale-catalog'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
              }`}
              data-testid="tab-wholesale-catalog"
            >
              <ShoppingBag className="h-4 w-4 inline mr-1 sm:mr-2" />
              Wholesale
            </button>
            <button
              onClick={() => setActiveTab('lifecycle')}
              className={`px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${
                activeTab === 'lifecycle'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
              }`}
              data-testid="tab-lifecycle"
            >
              <UserX className="h-4 w-4 inline mr-1 sm:mr-2" />
              Re-engagement
            </button>
            <button
              onClick={() => setActiveTab('broadcast')}
              className={`px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${
                activeTab === 'broadcast'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
              }`}
              data-testid="tab-broadcast"
            >
              <Megaphone className="h-4 w-4 inline mr-1 sm:mr-2" />
              Broadcast
            </button>
            <button
              onClick={() => setActiveTab('resources')}
              className={`px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${
                activeTab === 'resources' 
                  ? 'bg-white text-slate-900 shadow-sm' 
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
              }`}
              data-testid="tab-resources"
            >
              <BookOpen className="h-4 w-4 inline mr-1 sm:mr-2" />
              Resources
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-3 sm:px-4 py-2 rounded-lg font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${
                activeTab === 'settings' 
                  ? 'bg-white text-slate-900 shadow-sm' 
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
              }`}
              data-testid="tab-settings"
            >
              <Settings className="h-4 w-4 inline mr-1 sm:mr-2" />
              Settings
            </button>
          </div>
        </div>
      </div>

      {/* Article Modal */}
      {showArticleModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="font-heading text-lg font-bold text-slate-900">
                {editingArticle ? 'Edit Article' : 'New Article'}
              </h2>
              <button
                onClick={() => {
                  setShowArticleModal(false);
                  setEditingArticle(null);
                  setArticleImage(null);
                  setArticleImagePreview(null);
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSaveArticle} className="p-6 space-y-4">
              {/* Featured Image */}
              <div className="space-y-2">
                <Label>Featured Image</Label>
                <div className="flex gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => setArticleForm(prev => ({ ...prev, useImageUrl: false }))}
                    className={`px-3 py-1 text-sm rounded ${!articleForm.useImageUrl ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600'}`}
                  >
                    Upload File
                  </button>
                  <button
                    type="button"
                    onClick={() => setArticleForm(prev => ({ ...prev, useImageUrl: true }))}
                    className={`px-3 py-1 text-sm rounded ${articleForm.useImageUrl ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-600'}`}
                  >
                    Paste URL
                  </button>
                </div>
                
                {articleForm.useImageUrl ? (
                  <div className="space-y-2">
                    <Input
                      placeholder="https://lh3.googleusercontent.com/d/FILE_ID"
                      value={articleForm.imageUrl || ''}
                      onChange={(e) => {
                        let url = e.target.value;
                        // Auto-convert Google Drive share links to direct links
                        if (url.includes('drive.google.com/file/d/')) {
                          const match = url.match(/\/d\/([a-zA-Z0-9_-]+)/);
                          if (match) {
                            url = `https://lh3.googleusercontent.com/d/${match[1]}`;
                          }
                        }
                        setArticleForm(prev => ({ ...prev, imageUrl: url }));
                        setArticleImagePreview(url);
                      }}
                      data-testid="article-image-url"
                    />
                    <p className="text-xs text-slate-500">
                      Paste Google Drive link or any public image URL.<br/>
                      For Google Drive: Share file → "Anyone with link" → Copy link
                    </p>
                    {articleImagePreview && articleForm.useImageUrl && (
                      <div className="mt-2 p-2 border rounded">
                        <img src={articleImagePreview} alt="Preview" className="max-h-32 mx-auto rounded" onError={(e) => e.target.style.display='none'} />
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div 
                      onClick={() => articleImageRef.current?.click()}
                      className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center cursor-pointer hover:border-teal-400 hover:bg-teal-50/30 transition-colors"
                    >
                      {articleImagePreview && !articleForm.useImageUrl ? (
                        <img src={articleImagePreview} alt="Preview" className="max-h-40 mx-auto rounded" />
                      ) : (
                        <>
                          <Upload className="h-8 w-8 text-slate-400 mx-auto mb-1" />
                          <p className="text-sm text-slate-500">Click to upload image</p>
                        </>
                      )}
                    </div>
                    <div className="text-xs text-slate-400 bg-slate-50 rounded p-2">
                      <strong>Recommended:</strong> 1200 x 630px (16:9 aspect ratio) • JPEG/PNG • Under 500KB
                      <br />
                      <span className="text-teal-600">This size works best for both page headers and social media sharing.</span>
                    </div>
                    <input
                      ref={articleImageRef}
                      type="file"
                      accept="image/*"
                      onChange={handleArticleImageChange}
                      className="hidden"
                    />
                  </>
                )}
              </div>
              
              {/* Title */}
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  placeholder="Article title"
                  value={articleForm.title}
                  onChange={(e) => setArticleForm(prev => ({ ...prev, title: e.target.value }))}
                  required
                  data-testid="article-title"
                />
              </div>
              
              {/* Author */}
              <div className="space-y-2">
                <Label>Author</Label>
                <Input
                  placeholder="Author name"
                  value={articleForm.author}
                  onChange={(e) => setArticleForm(prev => ({ ...prev, author: e.target.value }))}
                  data-testid="article-author"
                />
              </div>
              
              {/* Excerpt */}
              <div className="space-y-2">
                <Label>Excerpt (Short summary)</Label>
                <textarea
                  placeholder="Brief summary for listing page..."
                  value={articleForm.excerpt}
                  onChange={(e) => setArticleForm(prev => ({ ...prev, excerpt: e.target.value }))}
                  rows={2}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm resize-none"
                  data-testid="article-excerpt"
                />
              </div>
              
              {/* Content */}
              <div className="space-y-2">
                <Label>Content *</Label>
                <textarea
                  placeholder="Full article content... (supports basic formatting)"
                  value={articleForm.content}
                  onChange={(e) => setArticleForm(prev => ({ ...prev, content: e.target.value }))}
                  rows={10}
                  className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm resize-none font-mono"
                  required
                  data-testid="article-content"
                />
              </div>
              
              {/* SEO Settings Section */}
              <div className="border-t border-slate-200 pt-4 mt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Search className="h-4 w-4 text-teal-600" />
                  <Label className="text-sm font-semibold text-slate-700">SEO & Meta Data</Label>
                </div>
                <p className="text-xs text-slate-500 mb-4">Customize how this article appears in search results and social media shares.</p>
                
                <div className="space-y-3">
                  {/* Meta Title */}
                  <div className="space-y-1">
                    <Label className="text-xs">SEO Title</Label>
                    <Input
                      placeholder="Custom title for search engines (defaults to article title)"
                      value={articleForm.meta_title}
                      onChange={(e) => setArticleForm(prev => ({ ...prev, meta_title: e.target.value }))}
                      data-testid="article-meta-title"
                    />
                    <p className="text-xs text-slate-400">{articleForm.meta_title?.length || 0}/60 characters recommended</p>
                  </div>
                  
                  {/* Meta Description */}
                  <div className="space-y-1">
                    <Label className="text-xs">SEO Description</Label>
                    <textarea
                      placeholder="Brief description for search results (defaults to excerpt)"
                      value={articleForm.meta_description}
                      onChange={(e) => setArticleForm(prev => ({ ...prev, meta_description: e.target.value }))}
                      rows={2}
                      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm resize-none"
                      data-testid="article-meta-description"
                    />
                    <p className="text-xs text-slate-400">{articleForm.meta_description?.length || 0}/160 characters recommended</p>
                  </div>
                  
                  {/* Meta Keywords */}
                  <div className="space-y-1">
                    <Label className="text-xs">Keywords</Label>
                    <Input
                      placeholder="peptides, research, BPC-157 (comma-separated)"
                      value={articleForm.meta_keywords}
                      onChange={(e) => setArticleForm(prev => ({ ...prev, meta_keywords: e.target.value }))}
                      data-testid="article-meta-keywords"
                    />
                  </div>
                </div>
              </div>

              {/* Publishing Status */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-4">
                <Label className="font-semibold text-slate-900">Publishing Options</Label>
                
                <div className="flex flex-wrap gap-3">
                  <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                    articleForm.status === 'published' ? 'border-green-300 bg-green-50 text-green-700' : 'border-slate-200'
                  }`}>
                    <input
                      type="radio"
                      name="status"
                      value="published"
                      checked={articleForm.status === 'published'}
                      onChange={(e) => setArticleForm(prev => ({ ...prev, status: e.target.value, scheduled_at: '' }))}
                      className="text-green-600"
                    />
                    <span className="text-sm font-medium">Publish Now</span>
                  </label>
                  
                  <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                    articleForm.status === 'scheduled' ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-slate-200'
                  }`}>
                    <input
                      type="radio"
                      name="status"
                      value="scheduled"
                      checked={articleForm.status === 'scheduled'}
                      onChange={(e) => setArticleForm(prev => ({ ...prev, status: e.target.value }))}
                      className="text-blue-600"
                    />
                    <Clock className="h-4 w-4" />
                    <span className="text-sm font-medium">Schedule</span>
                  </label>
                  
                  <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                    articleForm.status === 'draft' ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-slate-200'
                  }`}>
                    <input
                      type="radio"
                      name="status"
                      value="draft"
                      checked={articleForm.status === 'draft'}
                      onChange={(e) => setArticleForm(prev => ({ ...prev, status: e.target.value, scheduled_at: '' }))}
                      className="text-amber-600"
                    />
                    <span className="text-sm font-medium">Save as Draft</span>
                  </label>
                </div>

                {/* Schedule Date/Time Picker */}
                {articleForm.status === 'scheduled' && (
                  <div className="space-y-2">
                    <Label className="text-sm text-slate-600">Schedule Date & Time</Label>
                    <Input
                      type="datetime-local"
                      value={articleForm.scheduled_at}
                      onChange={(e) => setArticleForm(prev => ({ ...prev, scheduled_at: e.target.value }))}
                      min={new Date().toISOString().slice(0, 16)}
                      className="max-w-xs"
                      data-testid="article-scheduled-at"
                    />
                    <p className="text-xs text-slate-500">
                      Article will be automatically published at this time
                    </p>
                  </div>
                )}

                {/* Notify Customers Checkbox - shown for published or scheduled status */}
                {(articleForm.status === 'published' || articleForm.status === 'scheduled') && (
                  <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <input
                      type="checkbox"
                      id="notify-on-publish"
                      checked={articleForm.notify_on_publish}
                      onChange={(e) => setArticleForm(prev => ({ ...prev, notify_on_publish: e.target.checked }))}
                      className="mt-1 h-4 w-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                      data-testid="notify-on-publish-checkbox"
                    />
                    <div className="flex-1">
                      <label htmlFor="notify-on-publish" className="text-sm font-medium text-slate-700 cursor-pointer">
                        📧 Notify all customers when published
                      </label>
                      <p className="text-xs text-slate-500 mt-1">
                        {articleForm.status === 'scheduled' 
                          ? 'An email will be sent to all customers automatically when the article is published at the scheduled time.'
                          : 'An email will be sent to all customers immediately upon publishing.'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowArticleModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className={`flex-1 text-white ${
                    articleForm.status === 'draft' ? 'bg-amber-600 hover:bg-amber-700' :
                    articleForm.status === 'scheduled' ? 'bg-blue-600 hover:bg-blue-700' :
                    'bg-teal-600 hover:bg-teal-700'
                  }`}
                  data-testid="save-article-btn"
                >
                  {loading ? 'Saving...' : (
                    editingArticle ? 'Update Article' :
                    articleForm.status === 'draft' ? 'Save Draft' :
                    articleForm.status === 'scheduled' ? 'Schedule Article' :
                    'Publish Article'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* COA Upload Modal */}
      {showCoaModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="font-heading text-lg font-bold text-slate-900">Upload COA</h2>
                <p className="text-sm text-slate-500">{showCoaModal.name}</p>
              </div>
              <button
                onClick={() => {
                  setShowCoaModal(null);
                  setCoaPdf(null);
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleUploadCoa} className="p-6 space-y-4">
              <div className="space-y-2">
                <Label>Batch Number *</Label>
                <Input
                  placeholder="e.g., AOD-2025-001"
                  value={coaForm.batch_number}
                  onChange={(e) => setCoaForm(prev => ({ ...prev, batch_number: e.target.value }))}
                  required
                  data-testid="coa-batch-number"
                />
              </div>
              <div className="space-y-2">
                <Label>Purity</Label>
                <Input
                  placeholder="e.g., 99.5%"
                  value={coaForm.purity}
                  onChange={(e) => setCoaForm(prev => ({ ...prev, purity: e.target.value }))}
                  data-testid="coa-purity"
                />
              </div>
              <div className="space-y-2">
                <Label>Test Date *</Label>
                <Input
                  type="date"
                  value={coaForm.test_date}
                  onChange={(e) => setCoaForm(prev => ({ ...prev, test_date: e.target.value }))}
                  required
                  data-testid="coa-test-date"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Certificate File</Label>
                  <button
                    type="button"
                    onClick={() => { setCoaUseUrl(!coaUseUrl); setCoaPdf(null); setCoaUrl(''); }}
                    className="text-xs text-teal-600 hover:text-teal-800 font-medium"
                    data-testid="coa-toggle-url"
                  >
                    {coaUseUrl ? 'Upload file instead' : 'Use URL / Google Drive link'}
                  </button>
                </div>
                {coaUseUrl ? (
                  <div className="space-y-1">
                    <Input
                      placeholder="https://drive.google.com/file/d/... or any public URL"
                      value={coaUrl}
                      onChange={(e) => setCoaUrl(e.target.value)}
                      data-testid="coa-url-input"
                    />
                    <p className="text-xs text-slate-400">Paste a Google Drive share link or any public file URL</p>
                  </div>
                ) : (
                  <>
                    <div 
                      onClick={() => coaFileRef.current?.click()}
                      className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center cursor-pointer hover:border-teal-400 hover:bg-teal-50/30 transition-colors"
                    >
                      {coaPdf ? (
                        <div className="flex items-center justify-center gap-2">
                          <FileText className="h-5 w-5 text-teal-600" />
                          <span className="text-sm text-slate-700">{coaPdf.name}</span>
                        </div>
                      ) : (
                        <>
                          <Upload className="h-8 w-8 text-slate-400 mx-auto mb-1" />
                          <p className="text-sm text-slate-500">Click to upload PDF or image</p>
                          <p className="text-xs text-slate-400">PDF, JPG, PNG, WebP</p>
                        </>
                      )}
                    </div>
                    <input
                      ref={coaFileRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.webp,.gif"
                      onChange={(e) => setCoaPdf(e.target.files[0])}
                      className="hidden"
                    />
                  </>
                )}
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCoaModal(null)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
                  data-testid="upload-coa-btn"
                >
                  {loading ? 'Uploading...' : 'Upload COA'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6 sm:py-8">
        {/* Search - hidden on customers tab which has its own search */}
        {activeTab !== 'customers' && (
          <div className="relative max-w-md mb-6 sm:mb-8">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              type="text"
              placeholder={
                activeTab === 'inventory' ? "Search products..." : 
                activeTab === 'coas' ? "Search COAs..." : 
                activeTab === 'articles' ? "Search articles..." : 
                "Search..."
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="admin-search"
            />
          </div>
        )}

        {activeTab === 'sales' ? (
          <>
            {/* Sales Dashboard */}
            {salesLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-8 w-8 text-teal-600 animate-spin" />
              </div>
            ) : salesData ? (
              <div className="space-y-8">
                {/* Date Range Selector */}
                <Card className="border-slate-100">
                  <CardContent className="p-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <p className="text-sm font-medium text-slate-600 mr-1">Date Range:</p>
                      {[
                        { key: 'all', label: 'All Time' },
                        { key: 'today', label: 'Today' },
                        { key: 'yesterday', label: 'Yesterday' },
                        { key: 'this_week', label: 'This Week' },
                        { key: 'last_7', label: 'Last 7 Days' },
                        { key: 'this_month', label: 'This Month' },
                        { key: 'last_month', label: 'Last Month' },
                        { key: 'last_3_months', label: 'Last 3 Months' },
                        { key: 'last_6_months', label: 'Last 6 Months' },
                        { key: 'this_year', label: 'This Year' },
                        { key: 'last_year', label: 'Last Year' },
                      ].map(p => (
                        <button
                          key={p.key}
                          data-testid={`sales-preset-${p.key}`}
                          onClick={() => handleSalesPreset(p.key)}
                          className={`px-3 py-1.5 text-xs rounded-full border font-medium transition-all ${
                            salesDateRange.preset === p.key
                              ? 'bg-teal-600 text-white border-teal-600'
                              : 'bg-white text-slate-600 border-slate-200 hover:border-teal-400 hover:bg-teal-50'
                          }`}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center gap-3 mt-3 pt-3 border-t border-slate-100">
                      <p className="text-sm text-slate-500">Custom:</p>
                      <Input
                        type="date"
                        value={salesDateRange.start}
                        onChange={(e) => {
                          const newRange = { ...salesDateRange, start: e.target.value, preset: 'custom' };
                          setSalesDateRange(newRange);
                        }}
                        className="w-40 text-sm"
                        data-testid="sales-start-date"
                      />
                      <span className="text-slate-400">to</span>
                      <Input
                        type="date"
                        value={salesDateRange.end}
                        onChange={(e) => {
                          const newRange = { ...salesDateRange, end: e.target.value, preset: 'custom' };
                          setSalesDateRange(newRange);
                        }}
                        className="w-40 text-sm"
                        data-testid="sales-end-date"
                      />
                      <Button
                        size="sm"
                        onClick={() => fetchSalesData()}
                        data-testid="sales-apply-range"
                        className="bg-teal-600 hover:bg-teal-700"
                      >
                        Apply
                      </Button>
                    </div>
                    {salesData.range_summary?.is_filtered && (
                      <div className="mt-3 p-3 bg-teal-50 rounded-lg border border-teal-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-teal-800">
                              Filtered: {salesData.range_summary.start_date} to {salesData.range_summary.end_date}
                            </p>
                            <p className="text-xs text-teal-600">
                              {salesData.range_summary.orders} orders | ${salesData.range_summary.revenue.toLocaleString()} revenue | ${salesData.range_summary.average_order_value} avg
                            </p>
                          </div>
                          <button
                            onClick={() => handleSalesPreset('all')}
                            className="text-xs text-teal-700 hover:text-teal-900 underline"
                          >
                            Clear filter
                          </button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3">
                  <Button 
                    variant="outline" 
                    onClick={handleSendMonthlyReport}
                    data-testid="send-monthly-report-btn"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Email Monthly Report
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowReportModal(true)}
                    data-testid="generate-report-btn"
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    Generate Report
                  </Button>
                </div>

                {/* Cash App pending banner — only renders when there's at
                    least one cash_app order in the 'reserved' state awaiting
                    admin confirmation. Click → smooth-scroll to the Recent
                    Orders table on this same page (NOT a tab switch — there
                    is no separate 'orders' tab; Orders lives inside Sales). */}
                {(() => {
                  const cashAppPending = (salesData.recent_orders || []).filter(
                    o => o.payment_method === 'cash_app' && o.order_status === 'reserved'
                  );
                  if (cashAppPending.length === 0) return null;
                  const totalPending = cashAppPending.reduce((s, o) => s + (Number(o.total) || 0), 0);
                  return (
                    <button
                      onClick={() => {
                        const el = document.getElementById('recent-orders-card');
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                      className="w-full text-left rounded-xl border-2 border-green-400 bg-green-50 hover:bg-green-100 transition-colors px-4 py-3 flex items-center justify-between gap-4"
                      data-testid="cashapp-pending-tile"
                    >
                      <div className="flex items-center gap-3">
                        <div className="bg-green-500 text-white rounded-full h-9 w-9 flex items-center justify-center font-bold text-sm">
                          {cashAppPending.length}
                        </div>
                        <div>
                          <p className="font-semibold text-green-900">
                            Cash App {cashAppPending.length === 1 ? 'payment' : 'payments'} awaiting confirmation
                          </p>
                          <p className="text-xs text-green-700">
                            ${totalPending.toFixed(2)} total — check Cash App, then confirm in the Orders table below
                          </p>
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-green-700" />
                    </button>
                  );
                })()}

                {/* Zelle pending banner — same shape as Cash App. */}
                {(() => {
                  const zellePending = (salesData.recent_orders || []).filter(
                    o => o.payment_method === 'zelle' && o.order_status === 'reserved'
                  );
                  if (zellePending.length === 0) return null;
                  const totalPending = zellePending.reduce((s, o) => s + (Number(o.total) || 0), 0);
                  return (
                    <button
                      onClick={() => {
                        const el = document.getElementById('recent-orders-card');
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                      className="w-full text-left rounded-xl border-2 border-purple-400 bg-purple-50 hover:bg-purple-100 transition-colors px-4 py-3 flex items-center justify-between gap-4"
                      data-testid="zelle-pending-tile"
                    >
                      <div className="flex items-center gap-3">
                        <div className="bg-purple-500 text-white rounded-full h-9 w-9 flex items-center justify-center font-bold text-sm">
                          {zellePending.length}
                        </div>
                        <div>
                          <p className="font-semibold text-purple-900">
                            Zelle {zellePending.length === 1 ? 'payment' : 'payments'} awaiting confirmation
                          </p>
                          <p className="text-xs text-purple-700">
                            ${totalPending.toFixed(2)} total — check your bank app, then confirm in the Orders table below
                          </p>
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-purple-700" />
                    </button>
                  );
                })()}

                {/* At-risk reservations tile — unpaid Cash App / Zelle / Reserve
                    orders that the hourly cron will auto-cancel once they age
                    past the configured threshold. Expands to a list with a
                    one-click "Send reminder" per reservation. */}
                {(() => {
                  const ar = salesData.at_risk_reservations;
                  if (!ar || !ar.enabled || ar.count === 0) return null;
                  const soonest = ar.reservations[0];
                  const fmtRemaining = (h) => h <= 0 ? 'overdue — cancels next sweep' : (h < 1 ? `${Math.round(h * 60)} min left` : `${h.toFixed(1)}h left`);
                  return (
                    <div className="rounded-xl border-2 border-amber-400 bg-amber-50 overflow-hidden" data-testid="at-risk-reservations-tile">
                      <button
                        onClick={() => setAtRiskOpen(!atRiskOpen)}
                        className="w-full text-left hover:bg-amber-100 transition-colors px-4 py-3 flex items-center justify-between gap-4"
                        data-testid="at-risk-reservations-toggle"
                      >
                        <div className="flex items-center gap-3">
                          <div className="bg-amber-500 text-white rounded-full h-9 w-9 flex items-center justify-center font-bold text-sm shrink-0">
                            {ar.count}
                          </div>
                          <div>
                            <p className="font-semibold text-amber-900 flex items-center gap-1.5">
                              <AlertCircle className="h-4 w-4" />
                              Unpaid {ar.count === 1 ? 'reservation' : 'reservations'} at risk of auto-cancel
                            </p>
                            <p className="text-xs text-amber-700">
                              ${ar.total_amount.toFixed(2)} total · {ar.urgent_count} within {ar.risk_window_hours}h of the {ar.threshold_hours}h cutoff
                              {soonest ? ` · soonest: ${soonest.order_id} (${fmtRemaining(soonest.hours_remaining)})` : ''}
                            </p>
                          </div>
                        </div>
                        {atRiskOpen ? <ChevronUp className="h-5 w-5 text-amber-700 shrink-0" /> : <ChevronDown className="h-5 w-5 text-amber-700 shrink-0" />}
                      </button>
                      {atRiskOpen && (
                        <div className="border-t border-amber-200 divide-y divide-amber-200 bg-white/60" data-testid="at-risk-reservations-list">
                          {ar.reservations.map((r) => (
                            <div key={r.order_id} className="px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap" data-testid={`at-risk-row-${r.order_id}`}>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-slate-800 truncate">
                                  {r.customer_name} · <span className="font-mono text-xs">{r.order_id}</span>
                                </p>
                                <p className="text-xs text-slate-500">
                                  {r.payment_method === 'cash_app' ? 'Cash App' : r.payment_method === 'zelle' ? 'Zelle' : 'Reserve'} · ${r.total.toFixed(2)} ·{' '}
                                  <span className={r.is_urgent ? 'text-red-600 font-semibold' : 'text-amber-700'}>{fmtRemaining(r.hours_remaining)}</span>
                                </p>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-amber-400 text-amber-800 hover:bg-amber-100"
                                disabled={remindingId === r.order_id}
                                onClick={() => handleSendReminder(r.order_id)}
                                data-testid={`send-reminder-${r.order_id}`}
                              >
                                <Bell className="h-4 w-4 mr-1" />
                                {remindingId === r.order_id ? 'Sending…' : 'Send reminder'}
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Summary Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="border-slate-100">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm text-slate-500">
                          {salesData.range_summary?.is_filtered ? 'Range Revenue' : 'Total Revenue'}
                        </p>
                        <DollarSign className="h-5 w-5 text-teal-600" />
                      </div>
                      <p className="text-2xl font-heading font-bold text-slate-900">
                        ${salesData.range_summary?.is_filtered 
                          ? salesData.range_summary.revenue.toLocaleString()
                          : salesData.summary.total_revenue.toLocaleString()}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {salesData.range_summary?.is_filtered 
                          ? `${salesData.range_summary.orders} orders`
                          : `${salesData.summary.total_orders} orders`}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-100">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm text-slate-500">This Month</p>
                        {salesData.summary.month_growth_percent >= 0 ? (
                          <TrendingUp className="h-5 w-5 text-green-600" />
                        ) : (
                          <TrendingDown className="h-5 w-5 text-red-600" />
                        )}
                      </div>
                      <p className="text-2xl font-heading font-bold text-slate-900">
                        ${salesData.summary.month_revenue.toLocaleString()}
                      </p>
                      <p className={`text-xs mt-1 ${salesData.summary.month_growth_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {salesData.summary.month_growth_percent >= 0 ? '+' : ''}{salesData.summary.month_growth_percent}% vs last month
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-100">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm text-slate-500">Today</p>
                        <BarChart3 className="h-5 w-5 text-blue-600" />
                      </div>
                      <p className="text-2xl font-heading font-bold text-slate-900">
                        ${salesData.summary.today_revenue.toLocaleString()}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {salesData.summary.today_orders} orders
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-100">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm text-slate-500">Avg Order Value</p>
                        <Tag className="h-5 w-5 text-purple-600" />
                      </div>
                      <p className="text-2xl font-heading font-bold text-slate-900">
                        ${salesData.summary.average_order_value.toFixed(2)}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        Per order average
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Payment Methods Split */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card className="border-slate-100">
                    <CardContent className="p-6">
                      <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">Payment Methods</h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid="payment-method-square">
                          <div className="flex items-center gap-3">
                            <CreditCard className="h-5 w-5 text-teal-600" />
                            <div>
                              <p className="font-medium text-slate-900">Square</p>
                              <p className="text-sm text-slate-500">{salesData.payment_methods.square?.orders || 0} orders</p>
                            </div>
                          </div>
                          <p className="font-bold text-slate-900">${(salesData.payment_methods.square?.revenue || 0).toLocaleString()}</p>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid="payment-method-paypal">
                          <div className="flex items-center gap-3">
                            <DollarSign className="h-5 w-5 text-amber-600" />
                            <div>
                              <p className="font-medium text-slate-900">PayPal</p>
                              <p className="text-sm text-slate-500">{salesData.payment_methods.paypal?.orders || 0} orders</p>
                            </div>
                          </div>
                          <p className="font-bold text-slate-900">${(salesData.payment_methods.paypal?.revenue || 0).toLocaleString()}</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg" data-testid="payment-method-cash">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <Banknote className="h-5 w-5 text-green-600" />
                              <div>
                                <p className="font-medium text-slate-900">Cash (all manual methods)</p>
                                <p className="text-sm text-slate-500">{salesData.payment_methods.cash?.orders || 0} orders</p>
                              </div>
                            </div>
                            <p className="font-bold text-slate-900">${(salesData.payment_methods.cash?.revenue || 0).toLocaleString()}</p>
                          </div>
                          {/* Drill-down: separate Cash App / Zelle / In-person
                              so you can see which channel is actually pulling
                              its weight. Only renders when there's at least
                              one manual-payment order. */}
                          {salesData.payment_methods_detail && (salesData.payment_methods.cash?.orders > 0) && (
                            <div className="mt-2 pt-2 border-t border-slate-200 grid grid-cols-3 gap-2 text-xs">
                              <div className="rounded-md bg-green-50 px-2 py-1.5" data-testid="cash-detail-cashapp">
                                <p className="font-semibold text-green-800">Cash App</p>
                                <p className="text-green-700">${(salesData.payment_methods_detail.cash_app?.revenue || 0).toLocaleString()}</p>
                                <p className="text-green-600/70">{salesData.payment_methods_detail.cash_app?.orders || 0} ord.</p>
                              </div>
                              <div className="rounded-md bg-purple-50 px-2 py-1.5" data-testid="cash-detail-zelle">
                                <p className="font-semibold text-purple-800">Zelle</p>
                                <p className="text-purple-700">${(salesData.payment_methods_detail.zelle?.revenue || 0).toLocaleString()}</p>
                                <p className="text-purple-600/70">{salesData.payment_methods_detail.zelle?.orders || 0} ord.</p>
                              </div>
                              <div className="rounded-md bg-slate-100 px-2 py-1.5" data-testid="cash-detail-inperson">
                                <p className="font-semibold text-slate-800">In-person</p>
                                <p className="text-slate-700">${(salesData.payment_methods_detail.cash_in_person?.revenue || 0).toLocaleString()}</p>
                                <p className="text-slate-600/70">{salesData.payment_methods_detail.cash_in_person?.orders || 0} ord.</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Drill-down chart — visualizes the share of each
                          payment channel so admin can see at a glance
                          whether Zelle is gaining traction vs Cash App.
                          Only renders when there's ANY revenue. */}
                      {salesData.payment_methods_detail && (() => {
                        const detail = salesData.payment_methods_detail;
                        const rows = [
                          { key: 'square', label: 'Square (card)', revenue: detail.square?.revenue || 0, color: 'bg-teal-500', orders: detail.square?.orders || 0 },
                          { key: 'cash_app', label: 'Cash App', revenue: detail.cash_app?.revenue || 0, color: 'bg-green-500', orders: detail.cash_app?.orders || 0 },
                          { key: 'zelle', label: 'Zelle', revenue: detail.zelle?.revenue || 0, color: 'bg-purple-500', orders: detail.zelle?.orders || 0 },
                          { key: 'cash_in_person', label: 'Cash (in-person)', revenue: detail.cash_in_person?.revenue || 0, color: 'bg-slate-500', orders: detail.cash_in_person?.orders || 0 },
                          { key: 'paypal', label: 'PayPal', revenue: detail.paypal?.revenue || 0, color: 'bg-amber-500', orders: detail.paypal?.orders || 0 },
                        ].filter(r => r.revenue > 0).sort((a, b) => b.revenue - a.revenue);
                        const totalRev = rows.reduce((s, r) => s + r.revenue, 0);
                        if (totalRev === 0) return null;
                        return (
                          <div className="mt-5 pt-4 border-t border-slate-100" data-testid="payment-method-chart">
                            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-3">Share by channel</p>
                            <div className="space-y-2">
                              {rows.map(r => {
                                const pct = (r.revenue / totalRev) * 100;
                                return (
                                  <div key={r.key} className="space-y-1" data-testid={`chart-row-${r.key}`}>
                                    <div className="flex justify-between text-xs">
                                      <span className="font-medium text-slate-700">{r.label}</span>
                                      <span className="text-slate-500">
                                        <span className="font-semibold text-slate-700">${r.revenue.toLocaleString()}</span>
                                        <span className="ml-2 text-slate-400">{pct.toFixed(1)}% · {r.orders} ord.</span>
                                      </span>
                                    </div>
                                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                                      <div className={`${r.color} h-full rounded-full transition-all`} style={{ width: `${pct}%` }} />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}
                    </CardContent>
                  </Card>

                  {/* Top Products */}
                  <Card className="border-slate-100">
                    <CardContent className="p-6">
                      <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">Top Products</h3>
                      <div className="space-y-3 max-h-64 overflow-y-auto">
                        {salesData.top_products.length > 0 ? (
                          salesData.top_products.map((product, index) => (
                            <div key={product.product_id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                              <div className="flex items-center gap-3">
                                <span className="w-6 h-6 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center text-xs font-bold">
                                  {index + 1}
                                </span>
                                <div>
                                  <p className="font-medium text-slate-900 text-sm">{product.product_name}</p>
                                  <p className="text-xs text-slate-500">{product.units_sold} units sold</p>
                                </div>
                              </div>
                              <p className="font-semibold text-teal-700">${product.revenue.toFixed(2)}</p>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-slate-500 text-center py-4">No sales data yet</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Recent Orders */}
                <Card className="border-slate-100" id="recent-orders-card">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-4">
                        <h3 className="font-heading font-semibold text-lg text-slate-900">
                          Orders
                          {salesData.pagination && (
                            <span className="text-sm font-normal text-slate-400 ml-2">
                              ({salesData.pagination.total_orders} total)
                            </span>
                          )}
                        </h3>
                        <button
                          onClick={() => setShowPreordersOnly(!showPreordersOnly)}
                          className={`text-xs px-3 py-1 rounded-full border font-medium transition-colors ${
                            showPreordersOnly 
                              ? 'bg-amber-100 text-amber-700 border-amber-300' 
                              : 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-amber-50'
                          }`}
                          data-testid="toggle-preorders-filter"
                        >
                          {showPreordersOnly ? 'Showing Pre-Orders' : 'Show Pre-Orders Only'}
                        </button>
                        <button
                          onClick={() => { const nv = !showWholesaleOnly; setShowWholesaleOnly(nv); fetchSalesData(null, 1, undefined, false, nv); }}
                          className={`text-xs px-3 py-1 rounded-full border font-medium transition-colors ${
                            showWholesaleOnly
                              ? 'bg-teal-100 text-teal-700 border-teal-300'
                              : 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-teal-50'
                          }`}
                          data-testid="toggle-wholesale-filter"
                        >
                          {showWholesaleOnly ? 'Showing Wholesale Requests' : 'Show Wholesale Requests'}
                        </button>
                        {salesData.summary.inventory_adjustments > 0 && (
                          <Badge className="bg-amber-100 text-amber-700">
                            {salesData.summary.inventory_adjustments} adjustments excluded
                          </Badge>
                        )}
                      </div>
                      {/* Bulk Actions */}
                      {selectedOrders.length > 0 && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-600">{selectedOrders.length} selected</span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleBulkMarkAsAdjustment}
                            disabled={bulkActionLoading}
                            className="text-amber-600 border-amber-300 hover:bg-amber-50"
                            data-testid="bulk-mark-adjustment"
                          >
                            <AlertTriangle className="h-4 w-4 mr-1" />
                            Exclude from Sales
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleBulkUnmarkAdjustment}
                            disabled={bulkActionLoading}
                            className="text-teal-600 border-teal-300 hover:bg-teal-50"
                            data-testid="bulk-include-in-sales"
                          >
                            <Undo2 className="h-4 w-4 mr-1" />
                            Include in Sales
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleBulkDeleteOrders}
                            disabled={bulkActionLoading}
                            className="text-red-600 border-red-300 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      )}
                    </div>
                    {/* Customer Search */}
                    <div className="mb-4" data-testid="sales-search-section">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          placeholder="Search by customer name, email, or order ID..."
                          value={salesSearch}
                          onChange={(e) => handleSalesSearch(e.target.value)}
                          className="pl-9 h-9 text-sm"
                          data-testid="sales-search-input"
                        />
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="py-3 px-4 w-10">
                              <input
                                type="checkbox"
                                checked={salesData.recent_orders.length > 0 && selectedOrders.length === salesData.recent_orders.length}
                                onChange={toggleSelectAllOrders}
                                className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                                title="Select all"
                              />
                            </th>
                            <th className="text-left py-3 px-4 font-medium text-slate-600">Order ID</th>
                            <th className="text-left py-3 px-4 font-medium text-slate-600">Payment ID</th>
                            <th className="text-left py-3 px-4 font-medium text-slate-600">Customer</th>
                            <th className="text-left py-3 px-4 font-medium text-slate-600">Items</th>
                            <th className="text-left py-3 px-4 font-medium text-slate-600">Payment</th>
                            <th className="text-left py-3 px-4 font-medium text-slate-600">Status</th>
                            <th className="text-right py-3 px-4 font-medium text-slate-600">Total</th>
                            <th className="text-left py-3 px-4 font-medium text-slate-600">Tracking</th>
                            <th className="text-center py-3 px-4 font-medium text-slate-600">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {salesData.recent_orders.length > 0 ? (
                            (showPreordersOnly 
                              ? salesData.recent_orders.filter(o => o.is_preorder)
                              : salesData.recent_orders
                            ).map((order) => (
                              <React.Fragment key={order.order_id}>
                              <tr 
                                className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${selectedOrders.includes(order.order_id) ? 'bg-teal-50' : ''} ${expandedOrder === order.order_id ? 'bg-slate-50' : ''} ${order.exclude_from_sales ? 'opacity-60 bg-red-50/30' : ''}`}
                                onClick={(e) => {
                                  // Don't expand if clicking on interactive elements
                                  if (e.target.closest('button') || e.target.closest('a') || e.target.closest('input') || e.target.closest('select')) return;
                                  setExpandedOrder(expandedOrder === order.order_id ? null : order.order_id);
                                }}
                              >
                                <td className="py-3 px-4">
                                  <input
                                    type="checkbox"
                                    checked={selectedOrders.includes(order.order_id)}
                                    onChange={() => toggleOrderSelection(order.order_id)}
                                    className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                                  />
                                </td>
                                <td className="py-3 px-4 font-mono text-xs">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    {order.order_id}
                                    {order.is_wholesale_order && (
                                      <span className="inline-flex items-center gap-1 bg-teal-100 text-teal-700 text-[10px] font-sans font-semibold px-1.5 py-0.5 rounded-full" data-testid={`wholesale-badge-${order.order_id}`}>
                                        <Store className="h-3 w-3" /> Wholesale
                                      </span>
                                    )}
                                  </div>
                                  {order.created_at && (
                                    <div className="text-[11px] text-slate-400 font-sans mt-1 whitespace-nowrap" data-testid={`order-date-${order.order_id}`}>
                                      {new Date(order.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                      {' · '}
                                      {new Date(order.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                    </div>
                                  )}
                                </td>
                                <td className="py-3 px-4 font-mono text-xs">
                                  {order.payment_method === 'square' && (order.square_payment_id || order.payment_session_id) ? (
                                    <span className="text-teal-700" title={order.square_payment_id || order.payment_session_id}>
                                      SQ: {(order.square_payment_id || order.payment_session_id).slice(0, 12)}...
                                    </span>
                                  ) : order.payment_session_id && order.payment_method === 'paypal' ? (
                                    <span className="text-amber-600" title={order.payment_session_id}>
                                      PP: {order.payment_session_id.slice(0, 12)}...
                                    </span>
                                  ) : (
                                    <span className="text-slate-400">-</span>
                                  )}
                                </td>
                                <td className="py-3 px-4">
                                  {/* Reserved orders store the user under
                                      `customer_name` / `customer_email` /
                                      `contact` rather than the top-level
                                      `email` that Square orders use, so we
                                      look across all of them before falling
                                      back to "Guest". */}
                                  {(() => {
                                    const displayName = order.customer_name
                                      || order.contact?.name
                                      || order.shipping_address?.name
                                      || '';
                                    const displayEmail = order.email
                                      || order.customer_email
                                      || order.contact?.email
                                      || '';
                                    if (displayName && displayEmail) {
                                      return (
                                        <div>
                                          <div className="font-medium text-slate-900">{displayName}</div>
                                          <div className="text-xs text-slate-500">{displayEmail}</div>
                                        </div>
                                      );
                                    }
                                    if (displayName || displayEmail) {
                                      return <div className="font-medium text-slate-900">{displayName || displayEmail}</div>;
                                    }
                                    return <div className="text-slate-500">{order.customer_id || 'Guest'}</div>;
                                  })()}
                                  {order.affiliate_reward_email && (
                                    <div className="text-xs text-purple-600 font-medium mt-0.5">
                                      <Award className="h-3 w-3 inline mr-0.5" />Reward: {order.affiliate_reward_email}
                                    </div>
                                  )}
                                  {order.shipping_address && order.shipping_address.address_line1 && (
                                    <div className="text-xs text-slate-500 mt-1" data-testid={`shipping-address-${order.order_id}`}>
                                      <span className="font-medium text-slate-600">Ship to:</span>{' '}
                                      {order.shipping_address.name && <>{order.shipping_address.name}, </>}
                                      {order.shipping_address.address_line1}
                                      {order.shipping_address.city && <>, {order.shipping_address.city}</>}
                                      {order.shipping_address.state && <> {order.shipping_address.state}</>}
                                      {order.shipping_address.zip_code && <> {order.shipping_address.zip_code}</>}
                                    </div>
                                  )}
                                </td>
                                <td className="py-3 px-4">
                                  <span className="flex items-center gap-1 text-sm">
                                    {order.items?.length || 0} items
                                    <Eye className={`h-3.5 w-3.5 ${expandedOrder === order.order_id ? 'text-teal-600' : 'text-slate-300'}`} />
                                  </span>
                                </td>
                                <td className="py-3 px-4">
                                  <div className="flex gap-1 flex-wrap">
                                    <Badge className={
                                      order.payment_method === 'cash' ? 'bg-green-100 text-green-700' : 
                                      order.payment_method === 'reserve_pay_later' ? 'bg-blue-100 text-blue-700' :
                                      order.payment_method === 'square' ? 'bg-teal-100 text-teal-700' :
                                      order.payment_method === 'free_order' ? 'bg-slate-100 text-slate-700' :
                                      'bg-amber-100 text-amber-700'
                                    }>
                                      {order.payment_method === 'cash' ? 'Cash' :
                                       order.payment_method === 'reserve_pay_later' ? 'Reserved' :
                                       order.payment_method === 'square' ? 'Square' :
                                       order.payment_method === 'free_order' ? 'Free' :
                                       order.payment_method === 'paypal' ? 'PayPal' :
                                       (order.payment_method || 'Unknown')}
                                    </Badge>
                                    {order.is_preorder && (
                                      <Badge className="bg-amber-100 text-amber-700" data-testid={`preorder-badge-${order.order_id}`}>
                                        Pre-Order
                                      </Badge>
                                    )}
                                    {order.exclude_from_sales && (
                                      <Badge className="bg-red-100 text-red-700" data-testid={`excluded-badge-${order.order_id}`}>
                                        Excluded
                                      </Badge>
                                    )}
                                  </div>
                                </td>
                                <td className="py-3 px-4">
                                  {order.is_preorder ? (
                                    <select
                                      value={order.preorder_status || 'pending'}
                                      onChange={async (e) => {
                                        const newStatus = e.target.value;
                                        let note = '';
                                        if (newStatus === 'credited') {
                                          note = prompt('Add a note for the credit (e.g., which product credit is applied to):') || '';
                                        }
                                        try {
                                          const res = await adminFetch(`${API_URL}/api/admin/orders/${order.order_id}/preorder-status`, {
                                            method: 'PATCH',
                                            body: JSON.stringify({ preorder_status: newStatus, note })
                                          });
                                          if (res.ok) {
                                            toast.success(`Pre-order status updated to ${newStatus}`);
                                            fetchSalesData();
                                          } else {
                                            const data = await res.json();
                                            toast.error(data.detail || 'Failed to update');
                                          }
                                        } catch { toast.error('Failed to update pre-order status'); }
                                      }}
                                      className={`text-xs px-2 py-1 rounded-full border font-medium cursor-pointer ${
                                        order.preorder_status === 'completed' ? 'bg-green-100 text-green-700 border-green-200' :
                                        order.preorder_status === 'credited' ? 'bg-purple-100 text-purple-700 border-purple-200' :
                                        'bg-amber-100 text-amber-700 border-amber-200'
                                      }`}
                                      data-testid={`preorder-status-${order.order_id}`}
                                    >
                                      <option value="pending">Pending</option>
                                      <option value="completed">Completed</option>
                                      <option value="credited">Credited</option>
                                    </select>
                                  ) : (
                                    <select
                                      value={order.order_status || order.status || 'order_placed'}
                                      onChange={(e) => {
                                        const newStatus = e.target.value;
                                        const currentStatus = order.order_status || order.status || 'order_placed';
                                        if (newStatus !== currentStatus) {
                                          setStatusModal({
                                            order_id: order.order_id,
                                            order_email: order.email,
                                            customer_name: order.customer_name || order.shipping_address?.name || order.email,
                                            current_status: currentStatus,
                                            new_status: newStatus
                                          });
                                        }
                                      }}
                                      className={`text-xs rounded-full px-2 py-1 font-medium border-0 cursor-pointer ${(STATUS_CONFIG[order.order_status || order.status] || STATUS_CONFIG.pending).color}`}
                                      data-testid={`status-select-${order.order_id}`}
                                    >
                                      <option value="order_placed">Order Placed</option>
                                      <option value="confirmed">Confirmed</option>
                                      <option value="delayed">Delayed</option>
                                      <option value="ready_for_pickup">Ready for Pickup</option>
                                      <option value="shipped">Shipped</option>
                                    </select>
                                  )}
                                  {order.preorder_note && (
                                    <p className="text-xs text-slate-400 mt-1" title={order.preorder_note}>
                                      Note: {order.preorder_note.slice(0, 30)}{order.preorder_note.length > 30 ? '...' : ''}
                                    </p>
                                  )}
                                </td>
                                <td className="py-3 px-4 text-right font-semibold">
                                  ${(order.total || 0).toFixed(2)}
                                  {order.payment_status === 'partially_paid' && (order.balance_due || 0) > 0 && (
                                    <span
                                      className="block mt-1 text-xs font-semibold text-amber-600 whitespace-nowrap"
                                      title={`Deposit paid $${(order.amount_paid || 0).toFixed(2)} · Balance still owed`}
                                      data-testid={`balance-due-${order.order_id}`}
                                    >
                                      Balance due ${(order.balance_due || 0).toFixed(2)}
                                    </span>
                                  )}
                                </td>
                                <td className="py-3 px-4">
                                  {order.tracking_number ? (
                                    <div className="text-xs">
                                      <a 
                                        href={
                                          order.carrier_code === 'ups' ? `https://www.ups.com/track?tracknum=${order.tracking_number}` :
                                          order.carrier_code === 'fedex' ? `https://www.fedex.com/fedextrack/?trknbr=${order.tracking_number}` :
                                          `https://tools.usps.com/go/TrackConfirmAction?tLabels=${order.tracking_number}`
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-teal-600 hover:underline font-medium"
                                        data-testid={`tracking-link-${order.order_id}`}
                                      >
                                        {order.tracking_number.slice(0, 15)}...
                                      </a>
                                      <p className="text-slate-400">{(order.carrier_code || 'usps').toUpperCase()}</p>
                                    </div>
                                  ) : (
                                    <span className="text-xs text-slate-400">No tracking</span>
                                  )}
                                </td>
                                <td className="py-3 px-4">
                                  <div className="flex items-center justify-center gap-1">
                                    <button
                                      onClick={() => {
                                        const tracking = prompt('Enter tracking number:');
                                        if (!tracking) return;
                                        const carrier = prompt('Carrier (usps, ups, fedex):', 'usps') || 'usps';
                                        adminFetch(`${API_URL}/api/admin/orders/${order.order_id}/tracking`, {
                                          method: 'PATCH',
                                          body: JSON.stringify({ tracking_number: tracking, carrier_code: carrier, notify_customer: true })
                                        }).then(r => {
                                          if (r.ok) { toast.success('Tracking added & customer notified'); fetchSalesData(); }
                                          else r.json().then(d => toast.error(d.detail || 'Failed'));
                                        });
                                      }}
                                      className="p-1.5 hover:bg-blue-100 rounded text-blue-600"
                                      title="Add/Update Tracking"
                                      data-testid={`add-tracking-${order.order_id}`}
                                    >
                                      <Truck className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => {
                                        setInvoiceOrder(order);
                                        setShowInvoiceModal(true);
                                      }}
                                      className="p-1.5 hover:bg-teal-100 rounded text-teal-600"
                                      title="Resend Invoice"
                                    >
                                      <Send className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => handleEmailOrderToAdmin(order)}
                                      className="p-1.5 hover:bg-indigo-100 rounded text-indigo-500"
                                      title="Email order copy to admin"
                                      data-testid={`email-order-${order.order_id}`}
                                    >
                                      <Mail className="h-4 w-4" />
                                    </button>
                                    {order.payment_status === 'partially_paid' && (Number(order.balance_due) || 0) > 0 && (
                                      <button
                                        onClick={() => handleSendBalanceReminder(order.order_id)}
                                        disabled={remindingId === order.order_id}
                                        className="p-1.5 hover:bg-amber-100 rounded text-amber-600 disabled:opacity-50"
                                        title={`Email balance-due reminder ($${(Number(order.balance_due) || 0).toFixed(2)})`}
                                        data-testid={`balance-reminder-${order.order_id}`}
                                      >
                                        <Bell className="h-4 w-4" />
                                      </button>
                                    )}
                                    {order.exclude_from_sales ? (
                                      <button
                                        onClick={() => handleUnmarkAdjustment(order.order_id)}
                                        className="p-1.5 hover:bg-teal-100 rounded text-teal-600"
                                        title="Include in Sales Metrics"
                                        data-testid={`include-in-sales-${order.order_id}`}
                                      >
                                        <Undo2 className="h-4 w-4" />
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => handleMarkAsAdjustment(order.order_id)}
                                        className="p-1.5 hover:bg-amber-100 rounded text-amber-600"
                                        title="Mark as Inventory Adjustment"
                                        data-testid={`mark-adjustment-${order.order_id}`}
                                      >
                                        <AlertTriangle className="h-4 w-4" />
                                      </button>
                                    )}
                                    <div className="relative">
                                      <button
                                        onClick={() => setAffiliateRewardMenu(affiliateRewardMenu === order.order_id ? null : order.order_id)}
                                        className={`p-1.5 rounded ${order.affiliate_reward_email ? 'bg-purple-100 text-purple-700' : 'hover:bg-purple-100 text-purple-400 hover:text-purple-600'}`}
                                        title={order.affiliate_reward_email ? `Rewarded to ${order.affiliate_reward_email}` : 'Assign to Affiliate Rewards'}
                                        data-testid={`affiliate-reward-btn-${order.order_id}`}
                                      >
                                        <Award className="h-4 w-4" />
                                      </button>
                                      {affiliateRewardMenu === order.order_id && (
                                        <div className="absolute right-0 top-8 z-50 bg-white border border-slate-200 rounded-lg shadow-lg p-2 w-56" data-testid={`affiliate-reward-menu-${order.order_id}`}>
                                          <p className="text-xs font-medium text-slate-500 px-2 py-1">Assign to affiliate:</p>
                                          {affiliateCodes.length > 0 ? affiliateCodes.map(ac => (
                                            <button
                                              key={ac.code_id}
                                              onClick={() => handleAssignAffiliateReward(order.order_id, ac.assigned_customer_email)}
                                              className={`w-full text-left px-2 py-1.5 text-sm rounded hover:bg-purple-50 flex justify-between items-center ${order.affiliate_reward_email?.toLowerCase() === ac.assigned_customer_email?.toLowerCase() ? 'bg-purple-50 text-purple-700 font-medium' : 'text-slate-700'}`}
                                              data-testid={`assign-reward-${ac.code}`}
                                            >
                                              <span className="truncate">{ac.assigned_customer_name || ac.assigned_customer_email}</span>
                                              <span className="text-xs text-slate-400 ml-1">{ac.code}</span>
                                            </button>
                                          )) : <p className="text-xs text-slate-400 px-2 py-1">No affiliates found</p>}
                                          {order.affiliate_reward_email && (
                                            <button
                                              onClick={() => handleAssignAffiliateReward(order.order_id, '')}
                                              className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-red-50 text-red-500 mt-1 border-t border-slate-100"
                                              data-testid="remove-affiliate-reward"
                                            >
                                              Remove reward
                                            </button>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    {/* Generic Cancel & Reverse — for
                                        already-confirmed / paid orders.
                                        Hidden when the order is still in
                                        'reserved' state because reservations
                                        use the dedicated reservation-cancel
                                        button below (different reversal:
                                        no inventory restore, releases the
                                        affiliate code, deletes ShipStation
                                        entry). Showing both confused admins. */}
                                    {order.order_status !== 'reserved' && (
                                      <button
                                        onClick={() => handleCancelOrder(order)}
                                        className={`p-1.5 rounded ${order.order_status === 'cancelled' || order.status === 'cancelled' ? 'bg-rose-100 text-rose-700 cursor-not-allowed' : 'hover:bg-rose-100 text-rose-500 hover:text-rose-700'}`}
                                        title={order.order_status === 'cancelled' || order.status === 'cancelled' ? 'Order already cancelled' : 'Cancel & reverse order (restocks inventory, refunds credit)'}
                                        data-testid={`cancel-order-${order.order_id}`}
                                        disabled={order.order_status === 'cancelled' || order.status === 'cancelled'}
                                      >
                                        <XCircle className="h-4 w-4" />
                                      </button>
                                    )}
                                    {/* Confirm & Invoice — only visible on
                                        Reserve & Pay Later orders that are
                                        still in the 'reserved' state. One
                                        click deducts inventory, marks the
                                        order awaiting_payment, and emails
                                        the customer "expect a payment link
                                        shortly" plus an audit copy to the
                                        team mailbox. */}
                                    {(order.payment_method === 'reserve_pay_later' || order.payment_method === 'cash_app' || order.payment_method === 'zelle') && order.order_status === 'reserved' && (
                                      <button
                                        onClick={() => handleConfirmReservation(order)}
                                        className="p-1.5 hover:bg-teal-100 rounded text-teal-700"
                                        title={
                                          order.payment_method === 'cash_app'
                                            ? "Cash App payment received — mark paid, deduct inventory, email customer"
                                            : order.payment_method === 'zelle'
                                              ? "Zelle payment received — mark paid, deduct inventory, email customer"
                                              : "Payment received — mark paid, deduct inventory, email customer"
                                        }
                                        data-testid={`confirm-reservation-${order.order_id}`}
                                      >
                                        <CheckCircle2 className="h-4 w-4" />
                                      </button>
                                    )}
                                    {/* Undo / Revert — only for a just-confirmed
                                        reservation (order_status === 'confirmed').
                                        Restocks inventory and sends the order
                                        back to 'reserved' so the green checkmark
                                        can be re-clicked once payment is truly
                                        verified. For accidental clicks. */}
                                    {(order.payment_method === 'reserve_pay_later' || order.payment_method === 'cash_app' || order.payment_method === 'zelle') && order.order_status === 'confirmed' && (
                                      <button
                                        onClick={() => handleRevertConfirmation(order)}
                                        className="p-1.5 hover:bg-amber-100 rounded text-amber-600"
                                        title="Undo accidental confirmation — restock inventory, revert to Reserved, remove from ShipStation, email customer"
                                        data-testid={`revert-confirmation-${order.order_id}`}
                                      >
                                        <RotateCcw className="h-4 w-4" />
                                      </button>
                                    )}
                                    {(order.payment_method === 'reserve_pay_later' || order.payment_method === 'cash_app' || order.payment_method === 'zelle') && order.order_status === 'reserved' && (
                                      <button
                                        onClick={() => handleCancelReservation(order)}
                                        className="p-1.5 hover:bg-red-100 rounded text-red-600"
                                        title={
                                          order.payment_method === 'cash_app'
                                            ? "Customer didn't send Cash App — refund credit, release code, delete from ShipStation, email customer"
                                            : order.payment_method === 'zelle'
                                              ? "Customer didn't send Zelle — refund credit, release code, delete from ShipStation, email customer"
                                              : "Customer didn't pay — refund credit, release code, delete from ShipStation, email customer"
                                        }
                                        data-testid={`cancel-reservation-${order.order_id}`}
                                      >
                                        <XCircle className="h-4 w-4" />
                                      </button>
                                    )}
                                    {order.order_status !== 'cancelled' && (
                                      <button
                                        onClick={() => handleResyncShipStation(order)}
                                        className="p-1.5 hover:bg-indigo-100 rounded text-indigo-600"
                                        title="Re-sync this order to ShipStation (use if it didn't appear in ShipStation, e.g. after a billing/outage issue)"
                                        data-testid={`resync-shipstation-${order.order_id}`}
                                      >
                                        <Truck className="h-4 w-4" />
                                      </button>
                                    )}
                                    <button
                                      onClick={() => handleDeleteOrder(order.order_id)}
                                      className="p-1.5 hover:bg-red-100 rounded text-red-600"
                                      title="Delete Order"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              {expandedOrder === order.order_id && (
                                <tr className="bg-slate-50 border-b border-slate-200">
                                  <td colSpan="8" className="p-4">
                                    <div className="space-y-3">
                                      <div className="flex items-center justify-between mb-2">
                                        <p className="text-sm font-semibold text-slate-700">Order Items</p>
                                        <div className="flex gap-2 text-xs text-slate-500">
                                          {order.delivery_method === 'local_pickup' && (
                                            <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                                              Pickup{order.pickup_date ? `: ${order.pickup_date}` : ''}{order.pickup_time ? ` at ${order.pickup_time}` : ''}
                                            </span>
                                          )}
                                          {(order.discount?.code || order.discount_code) && (
                                            <span className="bg-teal-50 text-teal-700 px-2 py-0.5 rounded-full" data-testid={`order-discount-code-${order.order_id}`}>
                                              Code: {order.discount?.code || order.discount_code}
                                              {order.discount?.value != null ? ` (${order.discount.value}${order.discount.type === 'percentage' ? '%' : '$'} off)` : ''}
                                            </span>
                                          )}
                                          {order.store_credit_used > 0 && (
                                            <span className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full">
                                              Credit: ${order.store_credit_used.toFixed(2)}
                                            </span>
                                          )}
                                        </div>
                                        {order.order_status !== 'cancelled' && (
                                          <button
                                            onClick={(e) => { e.stopPropagation(); editingOrder === order.order_id ? cancelEditOrder() : startEditOrder(order); }}
                                            className="text-xs px-3 py-1 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-100 font-medium whitespace-nowrap"
                                            data-testid={`edit-order-btn-${order.order_id}`}
                                          >
                                            {editingOrder === order.order_id ? 'Close editor' : 'Edit order'}
                                          </button>
                                        )}
                                      </div>
                                      {editingOrder === order.order_id && editForm ? (
                                        <div className="space-y-2" onClick={(e) => e.stopPropagation()} data-testid={`order-edit-form-${order.order_id}`}>
                                          {(order.fulfillment_status === 'shipped' || order.order_status === 'shipped' || order.tracking_number) && (
                                            <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2">⚠️ This order looks shipped — editing it won't change what's already gone out.</div>
                                          )}
                                          {editForm.items.map((line, li) => {
                                            const selProd = availableProducts.find((x) => x.product_id === line.product_id);
                                            return (
                                              <div key={li} className="flex flex-wrap items-end gap-2 bg-white border border-slate-200 rounded-lg p-2" data-testid={`edit-line-${li}`}>
                                                <div className="flex-1 min-w-[160px]">
                                                  <Label className="text-[10px] text-slate-400">Product</Label>
                                                  <select value={line.product_id || ''} onChange={(e) => editSelectProduct(li, e.target.value)} className="w-full mt-0.5 px-2 py-1.5 border border-slate-300 rounded text-xs" data-testid={`edit-line-product-${li}`}>
                                                    <option value="">{line.name ? `Keep: ${line.name}` : 'Select product…'}</option>
                                                    <optgroup label="— Peptides —">
                                                      {availableProducts.filter((p) => p.product_type === 'peptide').map((p) => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}
                                                    </optgroup>
                                                    <optgroup label="— Shop / Accessories —">
                                                      {availableProducts.filter((p) => p.product_type === 'shop').map((p) => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}
                                                    </optgroup>
                                                  </select>
                                                </div>
                                                <div className="w-28">
                                                  <Label className="text-[10px] text-slate-400">Variant</Label>
                                                  {selProd && selProd.product_type !== 'shop' && selProd.variants?.length ? (
                                                    <select value={line.variant_index ?? 0} onChange={(e) => editSelectVariant(li, parseInt(e.target.value))} className="w-full mt-0.5 px-2 py-1.5 border border-slate-300 rounded text-xs" data-testid={`edit-line-variant-${li}`}>
                                                      {selProd.variants.map((v, vi) => <option key={vi} value={vi}>{v.size} — ${v.price}</option>)}
                                                    </select>
                                                  ) : (
                                                    <Input value={line.variant_name} onChange={(e) => editUpdateLine(li, { variant_name: e.target.value })} className="mt-0.5 h-8 text-xs" data-testid={`edit-line-variant-text-${li}`} />
                                                  )}
                                                </div>
                                                <div className="w-20">
                                                  <Label className="text-[10px] text-slate-400">Price</Label>
                                                  <Input type="number" value={line.price} onChange={(e) => editUpdateLine(li, { price: e.target.value })} className="mt-0.5 h-8 text-xs" data-testid={`edit-line-price-${li}`} />
                                                </div>
                                                <div className="w-16">
                                                  <Label className="text-[10px] text-slate-400">Qty</Label>
                                                  <Input type="number" value={line.quantity} onChange={(e) => editUpdateLine(li, { quantity: e.target.value })} className="mt-0.5 h-8 text-xs" data-testid={`edit-line-qty-${li}`} />
                                                </div>
                                                <button onClick={() => editRemoveLine(li)} className="h-8 px-2 text-red-500 hover:bg-red-50 rounded" data-testid={`edit-line-remove-${li}`}><Trash2 className="h-4 w-4" /></button>
                                              </div>
                                            );
                                          })}
                                          <button onClick={editAddLine} className="text-xs text-teal-700 font-medium hover:underline" data-testid={`edit-add-line-${order.order_id}`}>+ Add item</button>
                                          <div className="flex flex-wrap gap-3 pt-2 border-t border-slate-200">
                                            <div className="w-40"><Label className="text-[10px] text-slate-400">Discount label</Label><Input value={editForm.discount_label} onChange={(e) => setEditForm({ ...editForm, discount_label: e.target.value })} className="mt-0.5 h-8 text-xs" data-testid={`edit-discount-label-${order.order_id}`} /></div>
                                            <div className="w-28"><Label className="text-[10px] text-slate-400">Discount ($)</Label><Input type="number" value={editForm.discount_amount} onChange={(e) => setEditForm({ ...editForm, discount_amount: e.target.value })} className="mt-0.5 h-8 text-xs" data-testid={`edit-discount-amount-${order.order_id}`} /></div>
                                            <div className="w-28"><Label className="text-[10px] text-slate-400">Shipping ($)</Label><Input type="number" value={editForm.shipping_cost} onChange={(e) => setEditForm({ ...editForm, shipping_cost: e.target.value })} className="mt-0.5 h-8 text-xs" data-testid={`edit-shipping-${order.order_id}`} /></div>
                                          </div>
                                          {(() => { const c = editComputed(); return (
                                            <div className="text-xs text-slate-600 flex flex-col items-end gap-0.5 pt-1">
                                              <span>Subtotal: <strong>${c.subtotal.toFixed(2)}</strong></span>
                                              {c.disc > 0 && <span className="text-teal-700">Discount: -${c.disc.toFixed(2)}</span>}
                                              {parseFloat(editForm.shipping_cost) > 0 && <span>Shipping: ${(parseFloat(editForm.shipping_cost) || 0).toFixed(2)}</span>}
                                              {order.store_credit_used > 0 && <span className="text-purple-700">Store credit: -${order.store_credit_used.toFixed(2)}</span>}
                                              <span className="text-sm text-slate-900">New total: <strong>${Math.max(0, c.total - (order.store_credit_used || 0)).toFixed(2)}</strong></span>
                                            </div>
                                          ); })()}
                                          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                                            <input type="checkbox" checked={editForm.adjust_inventory} onChange={(e) => setEditForm({ ...editForm, adjust_inventory: e.target.checked })} data-testid={`edit-adjust-inventory-${order.order_id}`} />
                                            Adjust catalog stock for these changes (restock removed items, deduct added items)
                                          </label>
                                          <div className="flex justify-end gap-2 pt-1">
                                            <Button variant="outline" size="sm" onClick={cancelEditOrder} data-testid={`edit-cancel-${order.order_id}`}>Cancel</Button>
                                            <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white" disabled={savingEdit} onClick={saveEditOrder} data-testid={`edit-save-${order.order_id}`}>{savingEdit ? 'Saving…' : 'Save changes'}</Button>
                                          </div>
                                        </div>
                                      ) : (
                                      <table className="w-full text-sm">
                                        <thead>
                                          <tr className="text-xs text-slate-500 border-b border-slate-200">
                                            <th className="text-left py-1.5 font-medium">Product</th>
                                            <th className="text-left py-1.5 font-medium">Variant</th>
                                            <th className="text-right py-1.5 font-medium">Qty</th>
                                            <th className="text-right py-1.5 font-medium">Price</th>
                                            <th className="text-right py-1.5 font-medium">Subtotal</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {(order.items || []).map((item, idx) => (
                                            <tr key={idx} className="border-b border-slate-100 last:border-0">
                                              <td className="py-2 text-slate-900 font-medium">{item.name || item.product_name || 'Unknown Product'}</td>
                                              <td className="py-2 text-slate-500">{item.variant_name || item.vial_size || '-'}</td>
                                              <td className="py-2 text-right">{item.quantity}</td>
                                              <td className="py-2 text-right">${parseFloat(item.price || 0).toFixed(2)}</td>
                                              <td className="py-2 text-right font-medium">${(parseFloat(item.price || 0) * (item.quantity || 1)).toFixed(2)}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                        <tfoot>
                                          {order.subtotal != null && (
                                            <tr className="text-slate-600">
                                              <td colSpan="4" className="py-1 text-right">Subtotal:</td>
                                              <td className="py-1 text-right">${(order.subtotal || 0).toFixed(2)}</td>
                                            </tr>
                                          )}
                                          {order.discount_amount > 0 && (
                                            <tr className="text-teal-700">
                                              <td colSpan="4" className="py-1 text-right">Discount{(order.discount?.code || order.discount_code) ? ` (${order.discount?.code || order.discount_code})` : ''}:</td>
                                              <td className="py-1 text-right">-${(order.discount_amount || 0).toFixed(2)}</td>
                                            </tr>
                                          )}
                                          {order.store_credit_used > 0 && (
                                            <tr className="text-purple-700">
                                              <td colSpan="4" className="py-1 text-right">Store Credit:</td>
                                              <td className="py-1 text-right">-${(order.store_credit_used || 0).toFixed(2)}</td>
                                            </tr>
                                          )}
                                          <tr className="border-t border-slate-300">
                                            <td colSpan="4" className="py-2 text-right font-semibold text-slate-700">Total:</td>
                                            <td className="py-2 text-right font-bold text-slate-900">${(order.total || 0).toFixed(2)}</td>
                                          </tr>
                                          {order.payment_status === 'partially_paid' && (order.balance_due || 0) > 0 && (
                                            <>
                                              <tr className="text-green-700">
                                                <td colSpan="4" className="py-1 text-right">Deposit paid:</td>
                                                <td className="py-1 text-right">-${(order.amount_paid || 0).toFixed(2)}</td>
                                              </tr>
                                              <tr className="text-amber-600 font-bold">
                                                <td colSpan="4" className="py-1 text-right">Balance due:</td>
                                                <td className="py-1 text-right">${(order.balance_due || 0).toFixed(2)}</td>
                                              </tr>
                                            </>
                                          )}
                                        </tfoot>
                                      </table>
                                      )}
                                      {order.shipping_address && (
                                        <div className="text-xs text-slate-500 mt-2 pt-2 border-t border-slate-200">
                                          <span className="font-medium">Ship to: </span>
                                          {order.shipping_address.address_line1}{order.shipping_address.city ? `, ${order.shipping_address.city}` : ''}{order.shipping_address.state ? `, ${order.shipping_address.state}` : ''} {order.shipping_address.zip_code || ''}
                                        </div>
                                      )}
                                      {order.payment_method === 'zelle' && order.zelle_request_contact && (
                                        <div className="mt-2 pt-2 border-t border-purple-200 bg-purple-50 rounded-lg p-2.5" data-testid={`zelle-request-info-${order.order_id}`}>
                                          <p className="text-xs font-semibold text-purple-800 mb-0.5">Request payment via Zelle from:</p>
                                          <p className="text-xs text-purple-900">
                                            <span className="font-medium">Name:</span> {order.zelle_request_contact.name || '—'}<br />
                                            <span className="font-medium">Zelle phone/email:</span> <span className="font-mono">{order.zelle_request_contact.handle || '—'}</span>
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                              </React.Fragment>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="8" className="text-center py-8 text-slate-500">
                                No orders yet
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    {/* Load More + Back to Top */}
                    <div className="flex items-center justify-between mt-4">
                      <div>
                        {salesData.pagination?.has_more && (
                          <button
                            onClick={handleLoadMore}
                            disabled={loadingMore}
                            className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors disabled:opacity-50"
                            data-testid="load-more-orders"
                          >
                            {loadingMore ? 'Loading...' : `Load More Orders`}
                          </button>
                        )}
                        {salesData.pagination && !salesData.pagination.has_more && salesData.recent_orders.length > 0 && (
                          <p className="text-xs text-slate-400">Showing all {salesData.pagination.total_orders} orders</p>
                        )}
                      </div>
                      <button
                        onClick={() => document.getElementById('recent-orders-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-full transition-colors"
                        data-testid="back-to-top-orders"
                      >
                        <ArrowUp className="h-4 w-4" />
                        Back to Top
                      </button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="text-center py-12">
                <BarChart3 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Unable to load sales data</p>
                <Button onClick={fetchSalesData} className="mt-4" variant="outline">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retry
                </Button>
              </div>
            )}
          </>
        ) : activeTab === 'inventory' ? (
          <>
            {/* Base64 catalog bloat cleanup — surfaces a one-click migration
                button when any product is still holding a base64 `data:` URI
                in its `image_url` field. Those huge inline images inflated
                /api/products from ~30 KB to 16 MB on mobile, causing the
                "Trouble loading products" timeout. Hidden when count == 0. */}
            <Base64ImageMigrationBanner API={API_URL} onMigrated={() => fetchProducts()} />

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Card className="border-slate-100">
                <CardContent className="p-4">
                  <p className="text-sm text-slate-500">Total Products</p>
                  <p className="text-2xl font-heading font-bold text-slate-900">{products.length}</p>
                </CardContent>
              </Card>
              <Card className="border-slate-100">
                <CardContent className="p-4">
                  <p className="text-sm text-slate-500">Total Variants</p>
                  <p className="text-2xl font-heading font-bold text-slate-900">
                    {products.reduce((acc, p) => acc + (p.variants?.length || 0), 0)}
                  </p>
                </CardContent>
              </Card>
              <Card className="border-slate-100">
                <CardContent className="p-4">
                  <p className="text-sm text-slate-500">In Stock</p>
                  <p className="text-2xl font-heading font-bold text-green-600">
                    {products.reduce((acc, p) => acc + (p.variants?.filter(v => v.in_stock).length || 0), 0)}
                  </p>
                </CardContent>
              </Card>
              <Card className="border-slate-100">
                <CardContent className="p-4">
                  <p className="text-sm text-slate-500">Low Stock (&lt;20)</p>
                  <p className="text-2xl font-heading font-bold text-amber-600">
                    {products.reduce((acc, p) => acc + (p.variants?.filter(v => v.in_stock && (v.stock_quantity || 0) < 20).length || 0), 0)}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Bulk Actions Toolbar */}
            {selectedProducts.length > 0 && (
              <div className="mb-4 p-3 bg-teal-50 border border-teal-200 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selectedProducts.length === filteredProducts.length}
                    onChange={toggleSelectAllProducts}
                    className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  />
                  <span className="text-sm font-medium text-teal-800">
                    {selectedProducts.length} product(s) selected
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleBulkSetInStock}
                    disabled={bulkProductActionLoading}
                    className="text-green-600 border-green-300 hover:bg-green-50 text-xs"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                    In Stock
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleBulkSetOutOfStock}
                    disabled={bulkProductActionLoading}
                    className="text-amber-600 border-amber-300 hover:bg-amber-50 text-xs"
                  >
                    <XCircle className="h-3.5 w-3.5 mr-1" />
                    Out of Stock
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleBulkDeleteProducts}
                    disabled={bulkProductActionLoading}
                    className="text-red-600 border-red-300 hover:bg-red-50 text-xs"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            )}

            {/* Select All when none selected */}
            {selectedProducts.length === 0 && filteredProducts.length > 0 && (
              <div className="mb-4 flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={false}
                  onChange={toggleSelectAllProducts}
                  className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                />
                <span className="text-sm text-slate-500">Select all ({filteredProducts.length})</span>
              </div>
            )}

            {/* Products List */}
            <div className="space-y-4">
              {filteredProducts.map((product) => (
                <Card 
                  key={product.product_id} 
                  className={`border-2 transition-colors ${
                    selectedProducts.includes(product.product_id) 
                      ? 'border-teal-500 bg-teal-50/30' 
                      : 'border-slate-100 hover:border-slate-200'
                  }`} 
                  data-testid={`admin-product-${product.slug}`}
                >
                  <CardContent className="p-3 sm:p-6">
                    <div className="flex items-start gap-2 sm:gap-4">
                      {/* Checkbox */}
                      <div className="pt-1 flex-shrink-0">
                        <input
                          type="checkbox"
                          checked={selectedProducts.includes(product.product_id)}
                          onChange={() => toggleProductSelection(product.product_id)}
                          className="rounded border-slate-300 text-teal-600 focus:ring-teal-500 h-4 w-4 sm:h-5 sm:w-5"
                        />
                      </div>
                      {/* Product Image */}
                      <div className="w-12 h-12 sm:w-20 sm:h-20 bg-slate-50 rounded-lg flex-shrink-0 overflow-hidden">
                        {product.image_url ? (
                          <img 
                            src={(product.image_url.startsWith('/uploads') || product.image_url.startsWith('/api/uploads')) ? `${API_URL}${product.image_url}` : product.image_url} 
                            alt={product.name}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon className="h-5 w-5 sm:h-8 sm:w-8 text-slate-300" />
                          </div>
                        )}
                      </div>

                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        {/* Mobile: stack name and actions vertically */}
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3 sm:mb-4">
                          <div className="min-w-0">
                            <h3 className="font-heading font-semibold text-sm sm:text-lg text-slate-900 truncate">{product.name}</h3>
                            <p className="text-xs sm:text-sm text-slate-500 truncate">{product.category} • {product.purity}</p>
                        {/* Tier Visibility Badge */}
                        {product.visible_to_tiers && product.visible_to_tiers.length > 0 && (
                          <div className="flex items-center gap-1 mt-1">
                            <Shield className="h-3 w-3 text-purple-500" />
                            <span className="text-xs text-purple-600 font-medium">
                              {getTierNamesForProduct(product)?.join(', ') || 'Restricted'}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                        <Badge className={`text-[10px] sm:text-xs whitespace-nowrap ${
                          product.variants?.every(v => v.in_stock)
                            ? 'bg-green-50 text-green-700'
                            : product.variants?.some(v => v.in_stock)
                              ? 'bg-amber-50 text-amber-700'
                              : 'bg-red-50 text-red-700'
                        }`}>
                          {product.variants?.every(v => v.in_stock)
                            ? 'In Stock'
                            : product.variants?.some(v => v.in_stock)
                              ? 'Partial'
                              : 'Out'
                          }
                        </Badge>
                        <button
                          onClick={() => openVisibilityModal(product)}
                          className={`p-1 sm:p-1.5 rounded-lg transition-colors ${
                            product.visible_to_tiers?.length 
                              ? 'text-purple-500 hover:text-purple-700 hover:bg-purple-50' 
                              : 'text-slate-400 hover:text-purple-600 hover:bg-purple-50'
                          }`}
                          title="Set tier visibility"
                        >
                          <Shield className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        </button>
                        <button
                          onClick={() => openEditProductModal(product)}
                          className="p-1 sm:p-1.5 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                          title="Edit product"
                          data-testid={`edit-product-${product.slug}`}
                        >
                          <Edit className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        </button>
                        <button
                          onClick={() => handleNotifyCustomersAboutProduct(product.product_id, product.name)}
                          className="p-1 sm:p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Notify all customers about this product"
                          data-testid={`notify-product-${product.slug}`}
                        >
                          <Mail className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteProduct(product.product_id, product.name)}
                          className="p-1 sm:p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete product"
                          data-testid={`delete-product-${product.slug}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        </button>
                      </div>
                    </div>

                    {/* Variants */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                      {product.variants?.map((variant, index) => {
                        const isLowStock = variant.in_stock && (variant.stock_quantity || 0) < 20;
                        return (
                          <div 
                            key={index}
                            className={`p-2 sm:p-3 rounded-lg border ${
                              !variant.in_stock 
                                ? 'bg-red-50/50 border-red-100'
                                : isLowStock
                                  ? 'bg-amber-50/50 border-amber-100'
                                  : 'bg-green-50/50 border-green-100'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2 sm:mb-3">
                              <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                                {!variant.in_stock ? (
                                  <XCircle className="h-4 w-4 sm:h-5 sm:w-5 text-red-500 flex-shrink-0" />
                                ) : isLowStock ? (
                                  <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-amber-500 flex-shrink-0" />
                                ) : (
                                  <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0" />
                                )}
                                <div className="min-w-0">
                                  <p className="font-medium text-sm sm:text-base text-slate-900 truncate">{variant.size}</p>
                                  <p className="text-xs sm:text-sm text-slate-500">${variant.price.toFixed(2)}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                                <Switch
                                  checked={variant.in_stock}
                                  onCheckedChange={() => handleToggleStock(product.product_id, index, variant.in_stock)}
                                  disabled={updatingVariant === `${product.product_id}_${index}`}
                                  data-testid={`stock-toggle-${product.slug}-${index}`}
                                />
                                <button
                                  onClick={() => handleDeleteVariant(product.product_id, index, variant.size)}
                                  disabled={updatingVariant === `${product.product_id}_${index}` || product.variants?.length <= 1}
                                  className="p-1 rounded text-red-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
                                  title={product.variants?.length <= 1 ? "Cannot delete last variant" : "Remove this size"}
                                  data-testid={`delete-variant-${product.slug}-${index}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </div>
                            
                            {/* Stock Quantity Control */}
                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-200">
                              <span className="text-xs font-medium text-slate-500">QTY:</span>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleUpdateQuantity(product.product_id, index, Math.max(0, (variant.stock_quantity || 0) - 1))}
                                  disabled={updatingVariant === `${product.product_id}_${index}`}
                                  className="p-1 rounded bg-slate-100 hover:bg-slate-200 disabled:opacity-50"
                                  data-testid={`qty-minus-${product.slug}-${index}`}
                                >
                                  <Minus className="h-3 w-3" />
                                </button>
                                <input
                                  type="number"
                                  value={variant.stock_quantity || 0}
                                  onChange={(e) => {
                                    const val = parseInt(e.target.value) || 0;
                                    handleUpdateQuantity(product.product_id, index, Math.max(0, val));
                                  }}
                                  className="w-14 text-center text-sm font-medium border rounded px-1 py-0.5"
                                  min="0"
                                  data-testid={`qty-input-${product.slug}-${index}`}
                                />
                                <button
                                  onClick={() => handleUpdateQuantity(product.product_id, index, (variant.stock_quantity || 0) + 1)}
                                  disabled={updatingVariant === `${product.product_id}_${index}`}
                                  className="p-1 rounded bg-slate-100 hover:bg-slate-200 disabled:opacity-50"
                                  data-testid={`qty-plus-${product.slug}-${index}`}
                                >
                                  <Plus className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* Add New Variant Button/Form */}
                      {addingVariant === product.product_id ? (
                        <div className="p-3 rounded-lg border-2 border-dashed border-teal-300 bg-teal-50/30">
                          <p className="text-sm font-medium text-teal-700 mb-3">Add New Size</p>
                          <div className="space-y-2">
                            <input
                              type="text"
                              placeholder="Size (e.g., 15mg)"
                              value={newVariant.size}
                              onChange={(e) => setNewVariant(prev => ({ ...prev, size: e.target.value }))}
                              className="w-full text-sm border rounded px-2 py-1.5"
                              data-testid={`new-variant-size-${product.slug}`}
                            />
                            <input
                              type="number"
                              placeholder="Price"
                              value={newVariant.price}
                              onChange={(e) => setNewVariant(prev => ({ ...prev, price: e.target.value }))}
                              className="w-full text-sm border rounded px-2 py-1.5"
                              step="0.01"
                              min="0"
                              data-testid={`new-variant-price-${product.slug}`}
                            />
                            <input
                              type="number"
                              placeholder="Stock Qty"
                              value={newVariant.stock_quantity}
                              onChange={(e) => setNewVariant(prev => ({ ...prev, stock_quantity: e.target.value }))}
                              className="w-full text-sm border rounded px-2 py-1.5"
                              min="0"
                              data-testid={`new-variant-qty-${product.slug}`}
                            />
                            <div className="flex gap-2 pt-1">
                              <Button
                                size="sm"
                                onClick={() => handleAddVariant(product.product_id)}
                                disabled={updatingVariant === `${product.product_id}_add`}
                                className="flex-1 bg-teal-600 hover:bg-teal-700 text-white text-xs"
                                data-testid={`add-variant-confirm-${product.slug}`}
                              >
                                Add
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setAddingVariant(null);
                                  setNewVariant({ size: '', price: '', stock_quantity: 50 });
                                }}
                                className="flex-1 text-xs"
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setAddingVariant(product.product_id)}
                          className="p-3 rounded-lg border-2 border-dashed border-slate-200 bg-slate-50/50 hover:border-teal-300 hover:bg-teal-50/30 transition-colors flex items-center justify-center gap-2 text-slate-500 hover:text-teal-600"
                          data-testid={`add-variant-btn-${product.slug}`}
                        >
                          <PlusCircle className="h-5 w-5" />
                          <span className="text-sm font-medium">Add Size</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredProducts.length === 0 && (
          <div className="text-center py-12">
            <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No products found</p>
          </div>
        )}

            {/* Staff Access Management */}
            <div className="mt-12 pt-8 border-t border-slate-200">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900">Staff Access</h3>
                  <p className="text-sm text-slate-500">Allow team members to manage inventory</p>
                </div>
                <Button 
                  onClick={() => openStaffModal()} 
                  className="bg-slate-800 hover:bg-slate-900 text-white"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add Staff
                </Button>
              </div>

              {staffMembers.length === 0 ? (
                <div className="text-center py-8 bg-slate-50 rounded-lg">
                  <Users className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-2">No staff members yet</p>
                  <p className="text-sm text-slate-400">Add team members to help manage inventory</p>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {staffMembers.map((staff) => (
                    <Card key={staff.staff_id} className={`border-slate-100 ${!staff.is_active ? 'opacity-60' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                              staff.role === 'full_access' ? 'bg-purple-500' :
                              staff.role === 'content_manager' ? 'bg-blue-500' :
                              'bg-teal-500'
                            }`}>
                              {staff.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{staff.name}</p>
                              <p className="text-xs text-slate-500">{staff.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => openStaffModal(staff)}
                              className="p-1.5 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded"
                              title="Edit staff"
                              data-testid={`edit-staff-${staff.staff_id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteStaff(staff.staff_id, staff.name)}
                              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                              title="Remove staff"
                              data-testid={`delete-staff-${staff.staff_id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        <div className="mt-3 flex items-center justify-between">
                          <Badge className={
                            staff.role === 'full_access' ? 'bg-purple-100 text-purple-700' :
                            staff.role === 'content_manager' ? 'bg-blue-100 text-blue-700' :
                            'bg-teal-100 text-teal-700'
                          }>
                            {staff.role === 'full_access' ? 'Full Access' :
                             staff.role === 'content_manager' ? 'Content' :
                             'Inventory'}
                          </Badge>
                          <button
                            onClick={() => handleToggleStaffActive(staff)}
                            className={`text-xs px-2 py-1 rounded ${
                              staff.is_active 
                                ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                                : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                            }`}
                          >
                            {staff.is_active ? 'Active' : 'Inactive'}
                          </button>
                        </div>
                        {staff.last_login && (
                          <p className="text-xs text-slate-400 mt-2">
                            Last login: {new Date(staff.last_login).toLocaleDateString()}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">
                  <strong>Staff Login URL:</strong>{' '}
                  <code className="bg-white px-2 py-1 rounded text-teal-700">
                    {window.location.origin}/staff
                  </code>
                </p>
              </div>
            </div>
          </>
        ) : activeTab === 'accessories' ? (
          <AdminAccessoriesTab adminFetch={adminFetch} />
        ) : activeTab === 'coas' ? (
          /* COAs Tab Content */
          <>
            {/* COA Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
              <Card className="border-slate-100">
                <CardContent className="p-4">
                  <p className="text-sm text-slate-500">Total Products</p>
                  <p className="text-2xl font-heading font-bold text-slate-900">{coas.length}</p>
                </CardContent>
              </Card>
              <Card className="border-slate-100">
                <CardContent className="p-4">
                  <p className="text-sm text-slate-500">COAs Uploaded</p>
                  <p className="text-2xl font-heading font-bold text-green-600">
                    {coas.filter(c => c.has_coa).length}
                  </p>
                </CardContent>
              </Card>
              <Card className="border-slate-100">
                <CardContent className="p-4">
                  <p className="text-sm text-slate-500">Pending</p>
                  <p className="text-2xl font-heading font-bold text-amber-600">
                    {coas.filter(c => !c.has_coa).length}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* COA List */}
            <Card className="border-slate-100">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50">
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Product</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Batch #</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Purity</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Test Date</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCoas.map((coa) => {
                        const product = products.find(p => p.product_id === coa.product_id);
                        return (
                          <tr key={coa.coa_id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`admin-coa-${coa.product_slug}`}>
                            <td className="py-4 px-4">
                              <span className="font-medium text-slate-900">{coa.product_name}</span>
                            </td>
                            <td className="py-4 px-4">
                              {coa.has_coa ? (
                                <Badge className="bg-green-50 text-green-700 border-green-200">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  Uploaded
                                </Badge>
                              ) : (
                                <Badge className="bg-amber-50 text-amber-700 border-amber-200">
                                  <Clock className="h-3 w-3 mr-1" />
                                  Pending
                                </Badge>
                              )}
                            </td>
                            <td className="py-4 px-4">
                              {coa.has_coa ? (
                                <code className="text-sm bg-slate-100 px-2 py-1 rounded font-mono">
                                  {coa.batch_number}
                                </code>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </td>
                            <td className="py-4 px-4 text-sm text-slate-600">
                              {coa.purity}
                            </td>
                            <td className="py-4 px-4 text-sm text-slate-500">
                              {coa.has_coa ? coa.test_date : '—'}
                            </td>
                            <td className="py-4 px-4 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => product && openCoaModal(product)}
                                  data-testid={`upload-coa-${coa.product_slug}`}
                                >
                                  <Upload className="h-4 w-4 mr-1" />
                                  {coa.has_coa ? 'Update' : 'Upload'}
                                </Button>
                                {coa.has_coa && (
                                  <button
                                    onClick={() => handleDeleteCoa(coa.product_id, coa.product_name)}
                                    className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                    title="Delete COA"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {filteredCoas.length === 0 && (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No COAs found</p>
              </div>
            )}
          </>
        ) : activeTab === 'articles' ? (
          /* Articles Tab Content */
          <>
            {/* Article Stats & Add Button */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-4">
                <Card className="border-slate-100">
                  <CardContent className="p-4">
                    <p className="text-sm text-slate-500">Total Articles</p>
                    <p className="text-2xl font-heading font-bold text-slate-900">{articles.length}</p>
                  </CardContent>
                </Card>
              </div>
              <Button 
                onClick={() => openArticleModal()}
                className="bg-teal-600 hover:bg-teal-700 text-white"
                data-testid="add-article-btn"
              >
                <PlusCircle className="h-4 w-4 mr-2" />
                New Article
              </Button>
            </div>

            {/* Articles List */}
            <div className="space-y-4">
              {filteredArticles.map((article) => (
                <Card key={article.post_id} className="border-slate-100" data-testid={`article-${article.slug}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      {/* Article Image */}
                      <div className="w-32 h-24 bg-slate-100 rounded-lg flex-shrink-0 overflow-hidden">
                        {article.image_url ? (
                          <img 
                            src={(article.image_url.startsWith('/uploads') || article.image_url.startsWith('/api/uploads')) ? `${API_URL}${article.image_url}` : article.image_url} 
                            alt={article.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <BookOpen className="h-8 w-8 text-slate-300" />
                          </div>
                        )}
                      </div>

                      {/* Article Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-heading font-semibold text-lg text-slate-900">
                                {article.title}
                              </h3>
                              {/* Status Badge */}
                              <Badge className={
                                article.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                                article.status === 'draft' ? 'bg-amber-100 text-amber-700' :
                                'bg-green-100 text-green-700'
                              }>
                                {article.status === 'scheduled' ? (
                                  <><Clock className="h-3 w-3 mr-1" />{new Date(article.scheduled_at).toLocaleDateString()}</>
                                ) : article.status === 'draft' ? 'Draft' : 'Published'}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-500">
                              By {article.author} • {article.status === 'scheduled' 
                                ? `Scheduled for ${new Date(article.scheduled_at).toLocaleString()}`
                                : new Date(article.published_at).toLocaleDateString()
                              }
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => openArticleModal(article)}
                              className="p-1.5 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                              title="Edit article"
                              data-testid={`edit-article-${article.slug}`}
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleNotifyCustomersAboutArticle(article.post_id, article.title)}
                              className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Email to all customers"
                              data-testid={`notify-article-${article.slug}`}
                            >
                              <Send className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteArticle(article.post_id, article.title)}
                              className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete article"
                              data-testid={`delete-article-${article.slug}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        <p className="text-sm text-slate-600 line-clamp-2">
                          {article.excerpt}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {filteredArticles.length === 0 && (
              <div className="text-center py-12">
                <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">No articles yet</p>
                <Button 
                  onClick={() => openArticleModal()}
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                >
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Create Your First Article
                </Button>
              </div>
            )}
          </>
        ) : activeTab === 'ai-content' ? (
          <AdminAIContentEngine adminFetch={adminFetch} />
        ) : activeTab === 'customers' ? (
          /* Customers Tab Content */
          <>
            {/* Customer Sub-tabs - Scrollable on mobile */}
            <div className="flex items-center gap-2 mb-6 border-b border-slate-200 pb-4 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
              <button
                onClick={() => setCustomerTab('customers')}
                className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                  customerTab === 'customers' 
                    ? 'bg-teal-100 text-teal-700' 
                    : 'text-slate-500 hover:bg-slate-100'
                }`}
              >
                <Users className="h-4 w-4 inline mr-1 sm:mr-2" />
                Customers ({customers.length})
              </button>
              <button
                onClick={() => setCustomerTab('preorders')}
                className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                  customerTab === 'preorders' 
                    ? 'bg-teal-100 text-teal-700' 
                    : 'text-slate-500 hover:bg-slate-100'
                }`}
              >
                <ShoppingBag className="h-4 w-4 inline mr-1 sm:mr-2" />
                Preorders ({preorders.length})
              </button>
              <button
                onClick={() => setCustomerTab('notifications')}
                className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                  customerTab === 'notifications' 
                    ? 'bg-teal-100 text-teal-700' 
                    : 'text-slate-500 hover:bg-slate-100'
                }`}
              >
                <Bell className="h-4 w-4 inline mr-1 sm:mr-2" />
                Stock Alerts ({stockNotifications.length})
              </button>
              <button
                onClick={() => setCustomerTab('tiers')}
                className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                  customerTab === 'tiers' 
                    ? 'bg-teal-100 text-teal-700' 
                    : 'text-slate-500 hover:bg-slate-100'
                }`}
              >
                <Tag className="h-4 w-4 inline mr-1 sm:mr-2" />
                Tiers ({pricingTiers.length})
              </button>
              <button
                onClick={() => setCustomerTab('codes')}
                className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                  customerTab === 'codes' 
                    ? 'bg-teal-100 text-teal-700' 
                    : 'text-slate-500 hover:bg-slate-100'
                }`}
              >
                <Ticket className="h-4 w-4 inline mr-1 sm:mr-2" />
                Codes ({discountCodes.length})
              </button>
              <button
                onClick={() => setCustomerTab('blocklist')}
                className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                  customerTab === 'blocklist'
                    ? 'bg-teal-100 text-teal-700'
                    : 'text-slate-500 hover:bg-slate-100'
                }`}
                data-testid="customer-tab-blocklist"
              >
                <Ban className="h-4 w-4 inline mr-1 sm:mr-2" />
                Blocklist
              </button>
            </div>

            {/* Customers Sub-tab */}
            {customerTab === 'customers' && (
              <CustomersListPanel
                filteredCustomers={filteredCustomers}
                customerSearch={customerSearch}
                setCustomerSearch={setCustomerSearch}
                selectedCustomerIds={selectedCustomerIds}
                isBulkDeleting={isBulkDeleting}
                isExporting={isExporting}
                expandedCustomer={expandedCustomer}
                setExpandedCustomer={setExpandedCustomer}
                customerConsentForms={customerConsentForms}
                setCustomerConsentForms={setCustomerConsentForms}
                pricingTiers={pricingTiers}
                discountCodes={discountCodes}
                adminFetch={adminFetch}
                API_URL={API_URL}
                openCustomerModal={openCustomerModal}
                handleDeleteCustomer={handleDeleteCustomer}
                handleBanCustomer={handleBanCustomer}
                handleUnbanCustomer={handleUnbanCustomer}
                toggleCustomerSelection={toggleCustomerSelection}
                toggleSelectAllFiltered={toggleSelectAllFiltered}
                handleAutoFlagSuspicious={handleAutoFlagSuspicious}
                handleBulkDeleteCustomers={handleBulkDeleteCustomers}
                handleExportCustomers={handleExportCustomers}
                openNewsletterModal={openNewsletterModal}
                openCashPaymentModal={openCashPaymentModal}
                openPreorderModal={openPreorderModal}
                setPasswordResetCustomer={setPasswordResetCustomer}
                setShowPasswordResetModal={setShowPasswordResetModal}
                fetchCustomers={fetchCustomers}
              />
            )}

            {/* Preorders Sub-tab */}
            {customerTab === 'preorders' && (
              <>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between mb-6">
                  <p className="text-sm text-slate-500">Manage preorders for out-of-stock items</p>
                  <Button onClick={() => openPreorderModal()} className="bg-teal-600 hover:bg-teal-700 text-white">
                    <PlusCircle className="h-4 w-4 mr-2" />
                    New Preorder
                  </Button>
                </div>

                {preorders.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <ShoppingBag className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No preorders yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {preorders.map((preorder) => (
                      <Card key={preorder.preorder_id} className="border-slate-100">
                        <CardContent className="p-4">
                          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-medium text-slate-900">
                                  {preorder.customer_name || preorder.customer_email || 'Walk-in Customer'}
                                </span>
                                <Badge className={
                                  preorder.status === 'fulfilled' ? 'bg-green-100 text-green-700' :
                                  preorder.status === 'ready' ? 'bg-blue-100 text-blue-700' :
                                  preorder.status === 'confirmed' ? 'bg-teal-100 text-teal-700' :
                                  preorder.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                  'bg-amber-100 text-amber-700'
                                }>
                                  {preorder.status}
                                </Badge>
                              </div>
                              <p className="text-xs text-slate-500 mb-2">
                                #{preorder.preorder_id} • {new Date(preorder.created_at).toLocaleDateString()}
                              </p>
                              <div className="text-sm text-slate-600 mb-2">
                                {preorder.items?.map((item, idx) => (
                                  <span key={idx}>
                                    {item.quantity}x {item.product_name} ({item.variant_size})
                                    {idx < preorder.items.length - 1 ? ', ' : ''}
                                  </span>
                                ))}
                              </div>
                              <div className="flex gap-4 text-sm">
                                <span>Total: <strong className="text-teal-700">${preorder.estimated_total?.toFixed(2)}</strong></span>
                                {preorder.deposit_amount > 0 && (
                                  <span>Deposit: <strong className="text-green-600">${preorder.deposit_amount?.toFixed(2)}</strong></span>
                                )}
                                {preorder.balance_due > 0 && (
                                  <span>Balance: <strong className="text-amber-600">${preorder.balance_due?.toFixed(2)}</strong></span>
                                )}
                              </div>
                              {preorder.note && (
                                <p className="text-xs text-slate-500 mt-2 italic">Note: {preorder.note}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {preorder.status !== 'fulfilled' && preorder.status !== 'cancelled' && (
                                <>
                                  <select
                                    value={preorder.status}
                                    onChange={(e) => handleUpdatePreorderStatus(preorder.preorder_id, e.target.value)}
                                    className="text-xs border rounded px-2 py-1"
                                  >
                                    <option value="pending">Pending</option>
                                    <option value="confirmed">Confirmed</option>
                                    <option value="ready">Ready</option>
                                    <option value="cancelled">Cancelled</option>
                                  </select>
                                  <Button
                                    size="sm"
                                    onClick={() => handleFulfillPreorder(preorder.preorder_id)}
                                    className="bg-green-600 hover:bg-green-700 text-white text-xs"
                                  >
                                    <Check className="h-3 w-3 mr-1" />
                                    Fulfill
                                  </Button>
                                </>
                              )}
                              <button
                                onClick={() => handleDeletePreorder(preorder.preorder_id)}
                                className="p-2 text-slate-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Stock Notifications Sub-tab */}
            {customerTab === 'notifications' && (
              <>
                <div className="mb-6">
                  <p className="text-sm text-slate-500">Customers requesting notifications when items are back in stock</p>
                </div>

                {stockNotifications.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <Bell className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No stock notification requests</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {stockNotifications.map((notif) => (
                      <Card key={notif.notification_id} className="border-slate-100">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <Mail className="h-4 w-4 text-slate-400" />
                                <span className="font-medium text-slate-900">{notif.email}</span>
                              </div>
                              <p className="text-sm text-slate-600">
                                Wants: <strong>{notif.product_name}</strong> ({notif.variant_size})
                              </p>
                              <p className="text-xs text-slate-400 mt-1">
                                Requested: {new Date(notif.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={notif.notified ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                                {notif.notified ? 'Notified' : 'Pending'}
                              </Badge>
                              <button
                                onClick={() => handleDeleteNotification(notif.notification_id)}
                                className="p-2 text-slate-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Pricing Tiers Sub-tab */}
            {customerTab === 'tiers' && (
              <>
                <div className="flex items-center justify-between mb-6">
                  <p className="text-sm text-slate-500">Manage special pricing tiers for your customers</p>
                  <Button onClick={() => openTierModal()} className="bg-teal-600 hover:bg-teal-700 text-white">
                    <PlusCircle className="h-4 w-4 mr-2" />
                    New Tier
                  </Button>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  {pricingTiers.map((tier) => (
                    <Card key={tier.tier_id} className={`border-slate-100 ${!tier.is_active && 'opacity-60'}`}>
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-slate-900">{tier.name}</h3>
                              {!tier.is_active && <Badge variant="outline" className="text-xs">Inactive</Badge>}
                            </div>
                            <p className="text-sm text-slate-500 mt-1">{tier.description}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => openTierModal(tier)}
                              className="p-2 text-slate-400 hover:text-teal-600 rounded-lg hover:bg-teal-50"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteTier(tier.tier_id)}
                              className="p-2 text-slate-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                          <div className="flex items-center gap-2">
                            {tier.discount_type === 'percentage' ? (
                              <Percent className="h-5 w-5 text-teal-600" />
                            ) : (
                              <DollarSign className="h-5 w-5 text-teal-600" />
                            )}
                            <span className="text-2xl font-bold text-teal-600">
                              {tier.discount_type === 'percentage' ? `${tier.discount_value}%` : `$${tier.discount_value}`}
                            </span>
                            <span className="text-sm text-slate-500">off</span>
                          </div>
                          <Badge className="bg-slate-100 text-slate-600">
                            {tier.customer_count || 0} customers
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {pricingTiers.length === 0 && (
                  <div className="text-center py-12">
                    <Tag className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 mb-4">No pricing tiers yet</p>
                    <Button onClick={() => openTierModal()} className="bg-teal-600 hover:bg-teal-700 text-white">
                      <PlusCircle className="h-4 w-4 mr-2" />
                      Create First Tier
                    </Button>
                  </div>
                )}
              </>
            )}

            {/* Discount Codes Sub-tab */}
            {customerTab === 'codes' && (
              <>
                <div className="flex items-center justify-between mb-6">
                  <p className="text-sm text-slate-500">Generate discount codes for one-time users or special promotions</p>
                  <Button onClick={() => setShowCodeModal(true)} className="bg-teal-600 hover:bg-teal-700 text-white">
                    <PlusCircle className="h-4 w-4 mr-2" />
                    Generate Code
                  </Button>
                </div>

                <div className="space-y-3">
                  {discountCodes.map((code) => (
                    <Card key={code.code_id} className={`border-slate-100 ${!code.is_active && 'opacity-60'}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="bg-slate-900 text-white px-3 py-2 rounded-lg font-mono text-lg">
                              {code.code}
                            </div>
                            <button
                              onClick={() => copyToClipboard(code.code)}
                              className="p-2 text-slate-400 hover:text-teal-600 rounded-lg hover:bg-teal-50"
                              title="Copy code"
                            >
                              <Copy className="h-4 w-4" />
                            </button>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-teal-600">
                                  {code.discount_type === 'percentage' ? `${code.discount_value}% off` : `$${code.discount_value} off`}
                                </span>
                                {!code.is_active && <Badge variant="outline" className="text-xs">Inactive</Badge>}
                                <button
                                  type="button"
                                  onClick={() => handleToggleOncePerCustomer(code)}
                                  className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${code.once_per_customer ? 'bg-teal-50 border-teal-300 text-teal-700' : 'border-slate-200 text-slate-400 hover:border-teal-300 hover:text-teal-700'}`}
                                  title="Toggle: each customer account can use this code only once"
                                  data-testid={`discount-once-toggle-${code.code_id}`}
                                >
                                  {code.once_per_customer ? '1 use per customer' : 'Reusable · click to limit 1/customer'}
                                </button>
                              </div>
                              <p className="text-sm text-slate-500">
                                {code.description || code.created_for || 'No description'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right text-sm">
                              <p className="text-slate-900">
                                {code.usage_count || 0}{code.usage_limit ? `/${code.usage_limit}` : ''} uses
                              </p>
                              {code.expires_at && (
                                <p className="text-slate-500">
                                  Expires {new Date(code.expires_at).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                            <button
                              onClick={() => handleDeleteCode(code.code_id)}
                              className="p-2 text-slate-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {discountCodes.length === 0 && (
                  <div className="text-center py-12">
                    <Ticket className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 mb-4">No discount codes yet</p>
                    <Button onClick={() => setShowCodeModal(true)} className="bg-teal-600 hover:bg-teal-700 text-white">
                      <PlusCircle className="h-4 w-4 mr-2" />
                      Generate First Code
                    </Button>
                  </div>
                )}
              </>
            )}
            {customerTab === 'blocklist' && (
              <BlocklistPanel adminFetch={adminFetch} API_URL={API_URL} active={customerTab === 'blocklist'} />
            )}
          </>
        ) : activeTab === 'affiliates' ? (
          <AffiliatesTab adminFetch={adminFetch} />
        ) : activeTab === 'custom-orders' ? (
          <AdminCustomOrdersTab adminFetch={adminFetch} />
        ) : activeTab === 'wholesale-catalog' ? (
          <AdminWholesaleCatalogTab adminFetch={adminFetch} />
        ) : activeTab === 'lifecycle' ? (
          <AdminLifecycleTab adminFetch={adminFetch} />
        ) : activeTab === 'broadcast' ? (
          <AdminBroadcastTab adminFetch={adminFetch} />
        ) : activeTab === 'resources' ? (
          <AdminResourcesTab adminFetch={adminFetch} />
        ) : activeTab === 'settings' ? (
          /* Settings Tab Content */
          <div className="max-w-xl space-y-6">
            {/* Site status — maintenance + payments toggles. Top of the
                Settings tab so it's the first thing the admin sees during
                an outage. */}
            <SiteSettingsCard />

            {/* Reset User Password by Email */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-amber-600" />
                  Reset User Password
                </CardTitle>
                <p className="text-sm text-slate-500">Reset password for any registered user by their email address</p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleResetPasswordByEmail} className="space-y-4">
                  <div>
                    <Label htmlFor="reset-email">User Email Address</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      placeholder="user@example.com"
                      required
                      data-testid="reset-user-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="reset-new-password">New Password</Label>
                    <Input
                      id="reset-new-password"
                      type="password"
                      value={resetNewPassword}
                      onChange={(e) => setResetNewPassword(e.target.value)}
                      placeholder="Enter new password (min 6 characters)"
                      required
                      minLength={6}
                      data-testid="reset-user-new-password"
                    />
                  </div>
                  <div>
                    <Label htmlFor="reset-confirm-password">Confirm New Password</Label>
                    <Input
                      id="reset-confirm-password"
                      type="password"
                      value={resetConfirmPassword}
                      onChange={(e) => setResetConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      required
                      data-testid="reset-user-confirm-password"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-amber-600 hover:bg-amber-700"
                    disabled={resettingByEmail || !resetEmail || resetNewPassword.length < 6 || resetNewPassword !== resetConfirmPassword}
                    data-testid="reset-user-password-btn"
                  >
                    {resettingByEmail ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Resetting Password...
                      </>
                    ) : (
                      <>
                        <Key className="h-4 w-4 mr-2" />
                        Reset User Password
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Google Calendar Connection for Pickup Scheduling */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-teal-600" />
                  Google Calendar (Pickup Scheduling)
                </CardTitle>
                <p className="text-sm text-slate-500">
                  Connect your Google Calendar to manage pickup availability. Blocked days on your calendar will not be available for customer pickups.
                </p>
              </CardHeader>
              <CardContent>
                <CalendarConnectionManager adminFetch={adminFetch} />
              </CardContent>
            </Card>

            {/* Pickup Calendar - Block Dates */}
            <PickupCalendarManager adminFetch={adminFetch} />

            {/* Test Email Configuration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-blue-600" />
                  Test Email Configuration
                </CardTitle>
                <p className="text-sm text-slate-500">Send a test email to verify your email setup is working</p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSendTestEmail} className="space-y-4">
                  <div>
                    <Label htmlFor="test-email">Email Address</Label>
                    <Input
                      id="test-email"
                      type="email"
                      value={testEmailAddress}
                      onChange={(e) => setTestEmailAddress(e.target.value)}
                      placeholder="your@email.com"
                      required
                      data-testid="test-email-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    disabled={sendingTestEmail || !testEmailAddress}
                    data-testid="send-test-email-btn"
                  >
                    {sendingTestEmail ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="h-4 w-4 mr-2" />
                        Send Test Email
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Change Admin Password */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="h-5 w-5 text-teal-600" />
                  Change Admin Password
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div>
                    <Label htmlFor="current-password">Current Password</Label>
                    <Input
                      id="current-password"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                      required
                      data-testid="current-password"
                    />
                  </div>
                  <div>
                    <Label htmlFor="new-password">New Password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password (min 6 characters)"
                      required
                      minLength={6}
                      data-testid="new-password"
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirm-password">Confirm New Password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      required
                      data-testid="confirm-password"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-teal-600 hover:bg-teal-700"
                    disabled={changingPassword}
                    data-testid="change-password-btn"
                  >
                    {changingPassword ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Changing Password...
                      </>
                    ) : (
                      <>
                        <Key className="h-4 w-4 mr-2" />
                        Change Password
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        ) : null}

        {/* Newsletter Broadcast Modal (Apr 22, 2026 — extracted to NewsletterBroadcastModal.jsx) */}
        <NewsletterBroadcastModal
          open={showNewsletterModal}
          onClose={() => setShowNewsletterModal(false)}
          articles={articles}
          subscriberCount={newsletterSubscriberCount}
          mode={newsletterMode}
          setMode={setNewsletterMode}
          subject={newsletterSubject}
          setSubject={setNewsletterSubject}
          body={newsletterBody}
          setBody={setNewsletterBody}
          articleId={newsletterArticleId}
          setArticleId={setNewsletterArticleId}
          testEmail={newsletterTestEmail}
          setTestEmail={setNewsletterTestEmail}
          isSending={isSendingNewsletter}
          onSend={sendNewsletter}
        />

        {/* Cash Payment Modal */}
        {showCashPaymentModal && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
                <div>
                  <h2 className="font-heading text-lg font-bold text-slate-900">
                    Record {cashPaymentMethod === 'card' ? 'Card' : 'Cash'} Payment
                  </h2>
                  {cashPaymentCustomer && (
                    <p className="text-sm text-slate-500">Customer: {cashPaymentCustomer.name || cashPaymentCustomer.email}</p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setShowCashPaymentModal(false);
                    setCashPaymentMethod('cash');
                    setAdminSquareToken(null);
                    setAdminSquareVerification(null);
                  }}
                  className="p-2 hover:bg-slate-100 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="p-4 sm:p-6 space-y-4">
                {/* Items List */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">Order Items</Label>
                    <Button 
                      type="button" 
                      size="sm" 
                      onClick={addCashPaymentItem}
                      className="bg-teal-600 hover:bg-teal-700 text-white"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Item
                    </Button>
                  </div>
                  
                  {cashPaymentItems.length === 0 ? (
                    <div className="text-center py-8 text-slate-400 border-2 border-dashed rounded-lg">
                      <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No items added yet</p>
                      <p className="text-sm">Click "Add Item" to start</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {cashPaymentItems.map((item, index) => (
                        <div key={index} className="border border-slate-200 rounded-lg p-3 sm:p-4">
                          <div className="flex flex-col sm:flex-row gap-3">
                            {/* Product Select */}
                            <div className="flex-1">
                              <Label className="text-xs text-slate-500">Product</Label>
                              <select
                                value={item.product_id}
                                onChange={(e) => updateCashPaymentItem(index, 'product_id', e.target.value)}
                                className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                                data-testid={`cash-item-product-${index}`}
                              >
                                <option value="">Select product...</option>
                                <optgroup label="— Peptides —">
                                  {availableProducts.filter(p => p.product_type === 'peptide').map(p => (
                                    <option key={p.product_id} value={p.product_id}>{p.name}</option>
                                  ))}
                                </optgroup>
                                <optgroup label="— Shop / Accessories —">
                                  {availableProducts.filter(p => p.product_type === 'shop').map(p => (
                                    <option key={p.product_id} value={p.product_id}>{p.name}</option>
                                  ))}
                                </optgroup>
                              </select>
                              {item.product?.product_type === 'shop' && (
                                <span className="inline-block mt-1 text-[10px] px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full font-medium">
                                  Shop item
                                </span>
                              )}
                            </div>
                            
                            {/* Variant Select */}
                            {item.product && item.product.product_type !== 'shop' && (
                              <div className="w-full sm:w-32">
                                <Label className="text-xs text-slate-500">Size</Label>
                                <select
                                  value={item.variant_index}
                                  onChange={(e) => updateCashPaymentItem(index, 'variant_index', parseInt(e.target.value))}
                                  className={`w-full mt-1 px-3 py-2 border rounded-lg text-sm ${
                                    getCashItemAvailableStock(item) <= 0
                                      ? 'border-red-400 bg-red-50 text-red-700'
                                      : 'border-slate-300'
                                  }`}
                                  data-testid={`cash-item-variant-${index}`}
                                >
                                  {item.product.variants?.map((v, vIdx) => (
                                    <option 
                                      key={vIdx} 
                                      value={vIdx}
                                      disabled={!v.in_stock || v.stock_quantity <= 0}
                                    >
                                      {v.size} - ${v.price} {v.stock_quantity <= 0 ? '(Out)' : `(${v.stock_quantity})`}
                                    </option>
                                  ))}
                                </select>
                                {getCashItemAvailableStock(item) <= 0 && (
                                  <p className="text-[11px] text-red-600 mt-1" data-testid={`cash-item-oos-${index}`}>
                                    Out of stock — pick another size.
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Shop: pack size */}
                            {item.product?.product_type === 'shop' && item.product.has_pack_sizes && (
                              <div className="w-full sm:w-40">
                                <Label className="text-xs text-slate-500">Pack Size</Label>
                                <select
                                  value={item.pack_size_index}
                                  onChange={(e) => updateCashPaymentItem(index, 'pack_size_index', parseInt(e.target.value))}
                                  className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                                  data-testid={`cash-item-pack-${index}`}
                                >
                                  {(item.product.pack_sizes || []).map((ps, psIdx) => (
                                    <option
                                      key={psIdx}
                                      value={psIdx}
                                      disabled={(ps.stock_quantity || 0) <= 0}
                                    >
                                      {ps.name || `Pack ${psIdx + 1}`} - ${ps.price} {(ps.stock_quantity || 0) <= 0 ? '(Out)' : `(${ps.stock_quantity})`}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}

                            {/* Shop: color */}
                            {item.product?.product_type === 'shop' && item.product.has_colors && (
                              <div className="w-full sm:w-32">
                                <Label className="text-xs text-slate-500">Color</Label>
                                <select
                                  value={item.color_index}
                                  onChange={(e) => updateCashPaymentItem(index, 'color_index', parseInt(e.target.value))}
                                  className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                                  data-testid={`cash-item-color-${index}`}
                                >
                                  {(item.product.colors || []).map((c, cIdx) => (
                                    <option
                                      key={cIdx}
                                      value={cIdx}
                                      disabled={(c.stock_quantity || 0) <= 0}
                                    >
                                      {c.name || `Color ${cIdx + 1}`} {(c.stock_quantity || 0) <= 0 ? '(Out)' : `(${c.stock_quantity})`}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                            
                            {/* Quantity */}
                            <div className="w-full sm:w-24">
                              <Label className="text-xs text-slate-500">Qty</Label>
                              <Input
                                type="number"
                                min="1"
                                max={getCashItemAvailableStock(item) || 99}
                                value={item.quantity}
                                onChange={(e) => updateCashPaymentItem(index, 'quantity', parseInt(e.target.value) || 1)}
                                className="mt-1"
                                data-testid={`cash-item-qty-${index}`}
                              />
                              {item.product?.product_type === 'shop' && item.product.max_per_order && (
                                <p className="text-[10px] text-amber-600 mt-1">Max {item.product.max_per_order} per order</p>
                              )}
                            </div>
                            
                            {/* Price Display & Remove */}
                            <div className="flex items-end justify-between sm:justify-start gap-3">
                              <div className="text-right">
                                <Label className="text-xs text-slate-500">Total</Label>
                                <p className="font-semibold text-teal-700 mt-1">
                                  ${(getCashItemUnitPrice(item) * (item.quantity || 0)).toFixed(2)}
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeCashPaymentItem(index)}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg mb-1"
                                data-testid={`cash-item-remove-${index}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Discount Section */}
                <div className="border border-slate-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="font-semibold text-slate-700">Discount</Label>
                    {cashPaymentCustomer?.pricing_tier_id && (
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                        {pricingTiers.find(t => t.tier_id === cashPaymentCustomer.pricing_tier_id)?.name || 'Special Tier'} applied
                      </span>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={cashPaymentDiscount}
                        onChange={(e) => setCashPaymentDiscount(parseFloat(e.target.value) || 0)}
                        placeholder="Discount value"
                        data-testid="cash-payment-discount"
                      />
                    </div>
                    <select
                      value={cashPaymentDiscountType}
                      onChange={(e) => setCashPaymentDiscountType(e.target.value)}
                      className="border border-slate-200 rounded-md px-3 py-2 text-sm"
                      data-testid="cash-payment-discount-type"
                    >
                      <option value="percentage">%</option>
                      <option value="fixed">$ Fixed</option>
                    </select>
                  </div>
                  {cashPaymentDiscount > 0 && (
                    <p className="text-sm text-green-600">
                      Discount: -${calculateCashPaymentDiscountAmount().toFixed(2)}
                      {cashPaymentDiscountType === 'percentage' && ` (${cashPaymentDiscount}%)`}
                    </p>
                  )}
                </div>
                
                {/* Store Credit Section — appears only when the customer
                    actually has a positive store-credit balance (typically
                    affiliates with accrued rewards). The admin enters how
                    much of that credit to apply; backend re-caps to the
                    smaller of the balance and the post-discount subtotal. */}
                {Number(cashPaymentCustomer?.store_credit) > 0 && (
                  <div className="border border-amber-200 bg-amber-50/50 rounded-lg p-4 space-y-3" data-testid="cash-payment-store-credit-section">
                    <div className="flex items-center justify-between">
                      <Label className="font-semibold text-slate-700">Apply Store Credit</Label>
                      <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full" data-testid="cash-payment-available-credit">
                        Available: ${Number(cashPaymentCustomer.store_credit).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={cashPaymentStoreCreditUsed}
                          onChange={(e) => setCashPaymentStoreCreditUsed(parseFloat(e.target.value) || 0)}
                          placeholder="0.00"
                          data-testid="cash-payment-store-credit-input"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          const post = calculateCashPaymentSubtotal() - calculateCashPaymentDiscountAmount();
                          const max = Math.min(
                            Number(cashPaymentCustomer.store_credit) || 0,
                            post,
                          );
                          setCashPaymentStoreCreditUsed(Math.max(0, Math.round(max * 100) / 100));
                        }}
                        className="text-amber-700 border-amber-300 hover:bg-amber-100"
                        data-testid="cash-payment-store-credit-max"
                      >
                        Use Max
                      </Button>
                    </div>
                    {effectiveCashPaymentStoreCredit() > 0 && (
                      <p className="text-sm text-amber-700" data-testid="cash-payment-store-credit-applied">
                        Applying -${effectiveCashPaymentStoreCredit().toFixed(2)} of store credit
                      </p>
                    )}
                  </div>
                )}
                
                {/* Note */}
                <div>
                  <Label>Note (optional)</Label>
                  <Input
                    placeholder="Payment note or reference..."
                    value={cashPaymentNote}
                    onChange={(e) => setCashPaymentNote(e.target.value)}
                    className="mt-1"
                  />
                </div>
                
                {/* Total Summary */}
                <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between text-sm text-slate-600">
                    <span>Subtotal:</span>
                    <span>${calculateCashPaymentSubtotal().toFixed(2)}</span>
                  </div>
                  {cashPaymentDiscount > 0 && (
                    <div className="flex items-center justify-between text-sm text-green-600">
                      <span>Discount ({cashPaymentDiscountType === 'percentage' ? `${cashPaymentDiscount}%` : 'Fixed'}):</span>
                      <span>-${calculateCashPaymentDiscountAmount().toFixed(2)}</span>
                    </div>
                  )}
                  {effectiveCashPaymentStoreCredit() > 0 && (
                    <div className="flex items-center justify-between text-sm text-amber-700" data-testid="cash-payment-summary-credit">
                      <span>Store Credit Applied:</span>
                      <span>-${effectiveCashPaymentStoreCredit().toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                    <span className="font-semibold text-slate-700">Final Balance Due:</span>
                    <span className="text-2xl font-bold text-teal-700" data-testid="cash-payment-final-total">
                      ${calculateCashPaymentTotal().toFixed(2)}
                    </span>
                  </div>
                </div>
                
                {/* Payment Method Selector — Cash or Card. Cash falls through
                    the original flow; Card renders the Square Web Payments
                    card form and tokenizes when the admin taps Pay. */}
                <div className="border border-slate-200 rounded-lg p-4 space-y-3" data-testid="admin-payment-method">
                  <Label className="text-base font-semibold">Payment Method</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => { setCashPaymentMethod('cash'); setAdminSquareToken(null); setAdminSquareVerification(null); }}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        cashPaymentMethod === 'cash'
                          ? 'border-green-500 bg-green-50 text-green-900'
                          : 'border-slate-200 hover:border-slate-300 text-slate-600'
                      }`}
                      data-testid="admin-pay-method-cash"
                    >
                      <span className="block font-semibold">Cash</span>
                      <span className="block text-xs mt-0.5">In-person cash payment</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setCashPaymentMethod('card')}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        cashPaymentMethod === 'card'
                          ? 'border-teal-500 bg-teal-50 text-teal-900'
                          : 'border-slate-200 hover:border-slate-300 text-slate-600'
                      }`}
                      data-testid="admin-pay-method-card"
                    >
                      <span className="block font-semibold">Credit Card</span>
                      <span className="block text-xs mt-0.5">Charge via Square (in-person)</span>
                    </button>
                  </div>
                  {cashPaymentMethod === 'card' && calculateCashPaymentTotal() > 0 && (
                    <div className="pt-3" data-testid="admin-square-form-wrapper">
                      {adminSquareToken ? (
                        <div className="rounded-lg border border-green-300 bg-green-50 p-3 text-sm text-green-900" data-testid="admin-card-tokenized">
                          ✓ Card details captured. Click <strong>Charge Card</strong> below to complete.
                          <button
                            type="button"
                            onClick={() => { setAdminSquareToken(null); setAdminSquareVerification(null); }}
                            className="ml-2 underline text-green-700 hover:text-green-800"
                          >
                            Re-enter card
                          </button>
                        </div>
                      ) : (
                        <SquareCardForm
                          applicationId={process.env.REACT_APP_SQUARE_APPLICATION_ID}
                          locationId={process.env.REACT_APP_SQUARE_LOCATION_ID}
                          environment={process.env.REACT_APP_SQUARE_ENVIRONMENT || 'production'}
                          amount={calculateCashPaymentTotal()}
                          buyerEmail={cashPaymentCustomer?.email}
                          disabled={loading}
                          onBeforeTokenize={() => {
                            if (cashPaymentItems.length === 0) {
                              toast.error('Please add at least one item before charging the card.');
                              return false;
                            }
                            return true;
                          }}
                          onPaymentToken={async (tok, vtok) => {
                            setAdminSquareToken(tok);
                            setAdminSquareVerification(vtok || null);
                            toast.success('Card captured. Click "Charge Card" to complete.');
                          }}
                        />
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowCashPaymentModal(false);
                      setCashPaymentMethod('cash');
                      setAdminSquareToken(null);
                      setAdminSquareVerification(null);
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleRecordCashPayment}
                    disabled={loading || cashPaymentItems.length === 0 || (cashPaymentMethod === 'card' && calculateCashPaymentTotal() > 0 && !adminSquareToken)}
                    className={`flex-1 ${cashPaymentMethod === 'card' ? 'bg-teal-600 hover:bg-teal-700' : 'bg-green-600 hover:bg-green-700'} text-white`}
                    data-testid="admin-record-payment-btn"
                  >
                    {loading
                      ? 'Processing...'
                      : cashPaymentMethod === 'card'
                        ? (calculateCashPaymentTotal() <= 0 ? 'Record Payment' : 'Charge Card')
                        : 'Record Payment'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Product Visibility Modal */}
        {showVisibilityModal && visibilityProduct && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-md w-full">
              <div className="p-4 sm:p-6 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h2 className="font-heading text-lg font-bold text-slate-900">Product Visibility</h2>
                  <p className="text-sm text-slate-500">{visibilityProduct.name}</p>
                </div>
                <button onClick={() => setShowVisibilityModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="p-4 sm:p-6">
                <p className="text-sm text-slate-600 mb-4">
                  Select which customer tiers can see this product. Leave all unchecked to make it visible to everyone.
                </p>
                
                <div className="space-y-2 mb-6">
                  {pricingTiers.map((tier) => (
                    <label 
                      key={tier.tier_id} 
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedTiers.includes(tier.tier_id)
                          ? 'border-purple-300 bg-purple-50'
                          : 'border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedTiers.includes(tier.tier_id)}
                        onChange={() => handleToggleTierVisibility(tier.tier_id)}
                        className="w-4 h-4 text-purple-600 rounded"
                      />
                      <div className="flex-1">
                        <span className="font-medium text-slate-900">{tier.name}</span>
                        {tier.description && (
                          <p className="text-xs text-slate-500">{tier.description}</p>
                        )}
                      </div>
                      {tier.discount_value > 0 && (
                        <Badge className="bg-green-100 text-green-700 text-xs">
                          {tier.discount_type === 'percentage' ? `${tier.discount_value}% off` : `$${tier.discount_value} off`}
                        </Badge>
                      )}
                    </label>
                  ))}
                  
                  {pricingTiers.length === 0 && (
                    <div className="text-center py-6 text-slate-400">
                      <Tag className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No pricing tiers created yet</p>
                      <p className="text-sm">Create tiers in Customers tab first</p>
                    </div>
                  )}
                </div>
                
                <div className="bg-slate-50 rounded-lg p-3 mb-4">
                  <p className="text-sm text-slate-600">
                    <strong>Current visibility: </strong>
                    {selectedTiers.length === 0 
                      ? <span className="text-green-600">All customers</span>
                      : <span className="text-purple-600">
                          {pricingTiers.filter(t => selectedTiers.includes(t.tier_id)).map(t => t.name).join(', ')}
                        </span>
                    }
                  </p>
                </div>
                
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowVisibilityModal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveVisibility}
                    disabled={loading}
                    className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    {loading ? 'Saving...' : 'Save Visibility'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Staff Modal */}
        {showStaffModal && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
                <h2 className="font-heading text-lg font-bold text-slate-900">
                  {editingStaff ? 'Edit Staff Member' : 'Add Staff Member'}
                </h2>
                <button onClick={() => setShowStaffModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="p-4 sm:p-6 space-y-4">
                {/* Select from existing customers option (only for new staff) */}
                {!editingStaff && (
                  <div className="pb-4 border-b border-slate-200">
                    <Label className="text-sm font-medium">Add from existing customer</Label>
                    <p className="text-xs text-slate-500 mb-2">Select a customer to grant staff access</p>
                    <select
                      value={staffForm.customerId}
                      onChange={(e) => {
                        const customerId = e.target.value;
                        if (customerId) {
                          const customer = customers.find(c => c.customer_id === customerId);
                          if (customer) {
                            handleSelectCustomerForStaff(customer);
                          }
                        } else {
                          setStaffForm(prev => ({ ...prev, fromCustomer: false, customerId: '', name: '', email: '' }));
                        }
                      }}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    >
                      <option value="">-- Create new staff account --</option>
                      {customers.map(customer => (
                        <option key={customer.customer_id} value={customer.customer_id}>
                          {customer.name || 'Unnamed'} ({customer.email})
                        </option>
                      ))}
                    </select>
                    {staffForm.fromCustomer && (
                      <p className="text-xs text-teal-600 mt-1 flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        Customer selected - just set a password and role
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <Label>Full Name</Label>
                  <Input
                    placeholder="John Smith"
                    value={staffForm.name}
                    onChange={(e) => setStaffForm(prev => ({ ...prev, name: e.target.value }))}
                    disabled={staffForm.fromCustomer}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    placeholder="john@example.com"
                    value={staffForm.email}
                    onChange={(e) => setStaffForm(prev => ({ ...prev, email: e.target.value }))}
                    disabled={!!editingStaff || staffForm.fromCustomer}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>{editingStaff ? 'New Password (leave blank to keep current)' : 'Staff Password'}</Label>
                  <Input
                    type="password"
                    placeholder={editingStaff ? '••••••••' : 'Create password for staff login'}
                    value={staffForm.password}
                    onChange={(e) => setStaffForm(prev => ({ ...prev, password: e.target.value }))}
                    className="mt-1"
                  />
                  {!editingStaff && (
                    <p className="text-xs text-slate-500 mt-1">This password is for staff panel login only</p>
                  )}
                </div>
                
                <div>
                  <Label>Access Level</Label>
                  <select
                    value={staffForm.role}
                    onChange={(e) => setStaffForm(prev => ({ ...prev, role: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg"
                  >
                    <option value="inventory_manager">Inventory Manager - Manage products & stock</option>
                    <option value="content_manager">Content Manager - Manage articles & COAs</option>
                    <option value="full_access">Full Access - All admin features</option>
                  </select>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowStaffModal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveStaff}
                    disabled={loading}
                    className="flex-1 bg-slate-800 hover:bg-slate-900 text-white"
                  >
                    {loading ? 'Saving...' : (editingStaff ? 'Update Staff' : 'Add Staff')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Preorder Modal */}
        {showPreorderModal && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
                <div>
                  <h2 className="font-heading text-lg font-bold text-slate-900">Create Preorder</h2>
                  {preorderCustomer && (
                    <p className="text-sm text-slate-500">Customer: {preorderCustomer.name || preorderCustomer.email}</p>
                  )}
                </div>
                <button onClick={() => setShowPreorderModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="p-4 sm:p-6 space-y-4">
                {/* Contact Info */}
                {!preorderCustomer && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Customer Name</Label>
                      <Input placeholder="Name" className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs">Email</Label>
                      <Input placeholder="Email" className="mt-1" />
                    </div>
                  </div>
                )}
                
                <div>
                  <Label className="text-xs">Phone Number</Label>
                  <Input 
                    placeholder="Phone for pickup notification"
                    value={preorderPhone}
                    onChange={(e) => setPreorderPhone(e.target.value)}
                    className="mt-1"
                  />
                </div>

                {/* Items List */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">Preorder Items</Label>
                    <Button 
                      type="button" 
                      size="sm" 
                      onClick={addPreorderItem}
                      className="bg-teal-600 hover:bg-teal-700 text-white"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Item
                    </Button>
                  </div>
                  
                  {preorderItems.length === 0 ? (
                    <div className="text-center py-8 text-slate-400 border-2 border-dashed rounded-lg">
                      <ShoppingBag className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No items added yet</p>
                      <p className="text-sm">Add products to preorder (including out-of-stock items)</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {preorderItems.map((item, index) => (
                        <div key={index} className="border border-slate-200 rounded-lg p-3 sm:p-4">
                          <div className="flex flex-col sm:flex-row gap-3">
                            {/* Product Select */}
                            <div className="flex-1">
                              <Label className="text-xs text-slate-500">Product</Label>
                              <select
                                value={item.product_id}
                                onChange={(e) => updatePreorderItem(index, 'product_id', e.target.value)}
                                className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                              >
                                <option value="">Select product...</option>
                                {products.map(p => (
                                  <option key={p.product_id} value={p.product_id}>{p.name}</option>
                                ))}
                              </select>
                            </div>
                            
                            {/* Variant Select */}
                            {item.product && (
                              <div className="w-full sm:w-40">
                                <Label className="text-xs text-slate-500">Size</Label>
                                <select
                                  value={item.variant_index}
                                  onChange={(e) => updatePreorderItem(index, 'variant_index', parseInt(e.target.value))}
                                  className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                                >
                                  {item.product.variants?.map((v, vIdx) => (
                                    <option key={vIdx} value={vIdx}>
                                      {v.size} - ${v.price} {!v.in_stock || v.stock_quantity <= 0 ? '(Out of Stock)' : `(${v.stock_quantity})`}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                            
                            {/* Quantity */}
                            <div className="w-full sm:w-24">
                              <Label className="text-xs text-slate-500">Qty</Label>
                              <Input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => updatePreorderItem(index, 'quantity', parseInt(e.target.value) || 1)}
                                className="mt-1"
                              />
                            </div>
                            
                            {/* Price Display & Remove */}
                            <div className="flex items-end justify-between sm:justify-start gap-3">
                              <div className="text-right">
                                <Label className="text-xs text-slate-500">Total</Label>
                                <p className="font-semibold text-teal-700 mt-1">
                                  ${((item.product?.variants?.[item.variant_index]?.price || 0) * item.quantity).toFixed(2)}
                                </p>
                              </div>
                              <button
                                type="button"
                                onClick={() => removePreorderItem(index)}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg mb-1"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Deposit */}
                <div>
                  <Label>Deposit Amount (optional)</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={preorderDeposit}
                    onChange={(e) => setPreorderDeposit(e.target.value)}
                    className="mt-1"
                  />
                </div>
                
                {/* Note */}
                <div>
                  <Label>Note (optional)</Label>
                  <Input
                    placeholder="Special instructions or notes..."
                    value={preorderNote}
                    onChange={(e) => setPreorderNote(e.target.value)}
                    className="mt-1"
                  />
                </div>
                
                {/* Total */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-slate-600">Estimated Total:</span>
                    <span className="text-xl font-bold text-teal-700">
                      ${calculatePreorderTotal().toFixed(2)}
                    </span>
                  </div>
                  {preorderDeposit && parseFloat(preorderDeposit) > 0 && (
                    <>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">Deposit:</span>
                        <span className="text-green-600">-${parseFloat(preorderDeposit).toFixed(2)}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm font-medium border-t mt-2 pt-2">
                        <span className="text-slate-700">Balance Due:</span>
                        <span className="text-amber-600">
                          ${(calculatePreorderTotal() - parseFloat(preorderDeposit || 0)).toFixed(2)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
                
                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowPreorderModal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreatePreorder}
                    disabled={loading || preorderItems.length === 0}
                    className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
                  >
                    {loading ? 'Creating...' : 'Create Preorder'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Customer Modal */}
        {showCustomerModal && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
                <h2 className="font-heading text-lg font-bold text-slate-900">
                  {editingCustomer ? 'Edit Customer' : 'Add Customer'}
                </h2>
                <button onClick={() => setShowCustomerModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <form onSubmit={handleSaveCustomer} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Name *</Label>
                    <Input
                      placeholder="Customer name"
                      value={customerForm.name}
                      onChange={(e) => setCustomerForm(prev => ({ ...prev, name: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      placeholder="email@example.com"
                      value={customerForm.email}
                      onChange={(e) => setCustomerForm(prev => ({ ...prev, email: e.target.value }))}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      placeholder="+1 (555) 123-4567"
                      value={customerForm.phone}
                      onChange={(e) => setCustomerForm(prev => ({ ...prev, phone: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Company</Label>
                    <Input
                      placeholder="Company name"
                      value={customerForm.company}
                      onChange={(e) => setCustomerForm(prev => ({ ...prev, company: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Pricing Tier</Label>
                    <select
                      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                      value={customerForm.pricing_tier_id}
                      onChange={(e) => setCustomerForm(prev => ({ ...prev, pricing_tier_id: e.target.value }))}
                      data-testid="customer-pricing-tier"
                    >
                      <option value="">No special pricing</option>
                      {pricingTiers.filter(t => t.is_active).map(tier => (
                        <option key={tier.tier_id} value={tier.tier_id}>
                          {tier.name} ({tier.discount_value}% off)
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Affiliate Code</Label>
                    <select
                      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                      value={customerForm.affiliate_code_id}
                      onChange={(e) => setCustomerForm(prev => ({ ...prev, affiliate_code_id: e.target.value }))}
                      data-testid="customer-affiliate-code"
                    >
                      <option value="">No affiliate code</option>
                      {discountCodes.map(code => {
                        const isAssignedElsewhere = code.assigned_customer_email && code.assigned_customer_email.toLowerCase() !== (customerForm.email || '').toLowerCase();
                        return (
                          <option key={code.code_id} value={code.code_id} disabled={isAssignedElsewhere}>
                            {code.code} ({code.discount_value}{code.discount_type === 'percentage' ? '%' : '$'} off){isAssignedElsewhere ? ` — assigned to ${code.assigned_customer_email}` : ''}
                          </option>
                        );
                      })}
                    </select>
                    {customerForm.affiliate_code_id && (
                      <p className="text-xs text-teal-600 font-medium">This customer will be marked as an affiliate</p>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <select
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                    value={customerForm.status}
                    onChange={(e) => setCustomerForm(prev => ({ ...prev, status: e.target.value }))}
                    data-testid="customer-status"
                  >
                    <option value="active">Active</option>
                    <option value="vip">VIP</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                
                {/* Address Fields */}
                <div className="space-y-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <Label className="text-sm font-semibold text-slate-700">Shipping Address</Label>
                  <div className="space-y-2">
                    <Input
                      placeholder="Street address"
                      value={customerForm.address_line1}
                      onChange={(e) => setCustomerForm(prev => ({ ...prev, address_line1: e.target.value }))}
                      data-testid="customer-address-line1"
                    />
                    <Input
                      placeholder="Apt, suite, unit (optional)"
                      value={customerForm.address_line2}
                      onChange={(e) => setCustomerForm(prev => ({ ...prev, address_line2: e.target.value }))}
                      data-testid="customer-address-line2"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      placeholder="City"
                      value={customerForm.city}
                      onChange={(e) => setCustomerForm(prev => ({ ...prev, city: e.target.value }))}
                      data-testid="customer-city"
                    />
                    <Input
                      placeholder="State"
                      value={customerForm.state}
                      onChange={(e) => setCustomerForm(prev => ({ ...prev, state: e.target.value }))}
                      data-testid="customer-state"
                    />
                    <Input
                      placeholder="ZIP Code"
                      value={customerForm.zip_code}
                      onChange={(e) => setCustomerForm(prev => ({ ...prev, zip_code: e.target.value }))}
                      data-testid="customer-zip-code"
                    />
                  </div>
                  <Input
                    placeholder="Country (e.g., US)"
                    value={customerForm.country}
                    onChange={(e) => setCustomerForm(prev => ({ ...prev, country: e.target.value }))}
                    data-testid="customer-country"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Tags (comma-separated)</Label>
                  <Input
                    placeholder="researcher, bulk-buyer, affiliate"
                    value={customerForm.tags}
                    onChange={(e) => setCustomerForm(prev => ({ ...prev, tags: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <textarea
                    placeholder="Internal notes about this customer..."
                    value={customerForm.notes}
                    onChange={(e) => setCustomerForm(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm resize-none"
                  />
                </div>
                
                {/* Local Pickup Option */}
                <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={customerForm.allow_local_pickup}
                      onChange={(e) => setCustomerForm(prev => ({ ...prev, allow_local_pickup: e.target.checked }))}
                      className="w-4 h-4 text-teal-600 rounded"
                    />
                    <div>
                      <span className="font-medium text-teal-800">Allow Local Pickup</span>
                      <p className="text-xs text-teal-600">Customer can choose free local pickup at checkout</p>
                    </div>
                  </label>
                </div>
                
                {/* Cash Payment Option */}
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={customerForm.allow_cash_payment}
                      onChange={(e) => setCustomerForm(prev => ({ ...prev, allow_cash_payment: e.target.checked }))}
                      className="w-4 h-4 text-amber-600 rounded"
                      data-testid="allow-cash-payment-checkbox"
                    />
                    <div>
                      <span className="font-medium text-amber-800">Allow Cash Payment</span>
                      <p className="text-xs text-amber-600">Customer can choose to pay with cash / in-person at checkout</p>
                    </div>
                  </label>
                </div>

                {/* Allow Consent Send to Third Party */}
                <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={customerForm.allow_consent_send}
                      onChange={(e) => setCustomerForm(prev => ({ ...prev, allow_consent_send: e.target.checked }))}
                      className="w-4 h-4 text-indigo-600 rounded"
                      data-testid="allow-consent-send-checkbox"
                    />
                    <div>
                      <span className="font-medium text-indigo-800">Allow Sending Consent Forms</span>
                      <p className="text-xs text-indigo-600">Customer can send consent forms to third-party recipients from their account</p>
                    </div>
                  </label>
                </div>

                {/* Research Consent */}
                {editingCustomer && (
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200" data-testid="consent-section">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-slate-800">Research Consent</span>
                      {editingCustomer.research_consent?.status === 'signed' ? (
                        <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full font-medium">Signed</span>
                      ) : editingCustomer.research_consent?.status === 'pending' ? (
                        <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full font-medium">Pending</span>
                      ) : (
                        <span className="text-xs px-2 py-1 bg-slate-200 text-slate-600 rounded-full font-medium">Not Sent</span>
                      )}
                    </div>
                    {editingCustomer.research_consent?.status === 'signed' && (
                      <p className="text-xs text-green-600 mb-2">
                        Signed by {editingCustomer.research_consent.signer_name || 'customer'} on {new Date(editingCustomer.research_consent.signed_at).toLocaleDateString()}
                      </p>
                    )}
                    {editingCustomer.research_consent?.status === 'pending' && (
                      <p className="text-xs text-yellow-600 mb-2">
                        Sent on {new Date(editingCustomer.research_consent.sent_at).toLocaleDateString()} — awaiting signature
                      </p>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        try {
                          const res = await adminFetch(`${API_URL}/api/admin/customers/${editingCustomer.customer_id}/send-consent`, {
                            method: 'POST'
                          });
                          const data = await res.json();
                          if (res.ok) {
                            toast.success(`Consent form sent to ${editingCustomer.email}`);
                            fetchCustomers();
                          } else {
                            toast.error(data.detail || 'Failed to send');
                          }
                        } catch (e) { toast.error(e.message); }
                      }}
                      data-testid="send-consent-btn"
                    >
                      <Send className="h-3 w-3 mr-1" />
                      {editingCustomer.research_consent?.status === 'pending' ? 'Resend Consent Form' : 'Send Consent Form'}
                    </Button>
                  </div>
                )}
                
                {/* Default CC Emails */}
                <div className="space-y-2">
                  <Label>Default CC Emails (for invoices)</Label>
                  <Input
                    placeholder="email1@example.com, email2@example.com"
                    value={customerForm.default_cc_emails}
                    onChange={(e) => setCustomerForm(prev => ({ ...prev, default_cc_emails: e.target.value }))}
                  />
                  <p className="text-xs text-slate-500">Comma-separated emails that will be CC'd on invoices</p>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowCustomerModal(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white">
                    {loading ? 'Saving...' : (editingCustomer ? 'Update Customer' : 'Add Customer')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Pricing Tier Modal */}
        {showTierModal && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-md w-full">
              <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                <h2 className="font-heading text-lg font-bold text-slate-900">
                  {editingTier ? 'Edit Pricing Tier' : 'New Pricing Tier'}
                </h2>
                <button onClick={() => setShowTierModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <form onSubmit={handleSaveTier} className="p-6 space-y-4">
                <div className="space-y-2">
                  <Label>Tier Name *</Label>
                  <Input
                    placeholder="e.g., Friends & Family"
                    value={tierForm.name}
                    onChange={(e) => setTierForm(prev => ({ ...prev, name: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    placeholder="Brief description of this tier"
                    value={tierForm.description}
                    onChange={(e) => setTierForm(prev => ({ ...prev, description: e.target.value }))}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Discount Type</Label>
                    <select
                      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                      value={tierForm.discount_type}
                      onChange={(e) => setTierForm(prev => ({ ...prev, discount_type: e.target.value }))}
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount ($)</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Discount Value *</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder={tierForm.discount_type === 'percentage' ? '25' : '10.00'}
                      value={tierForm.discount_value}
                      onChange={(e) => setTierForm(prev => ({ ...prev, discount_value: e.target.value }))}
                      required
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={tierForm.is_active}
                    onCheckedChange={(checked) => setTierForm(prev => ({ ...prev, is_active: checked }))}
                  />
                  <Label>Active</Label>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowTierModal(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white">
                    {loading ? 'Saving...' : (editingTier ? 'Update Tier' : 'Create Tier')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Discount Code Modal */}
        {showCodeModal && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-md w-full">
              <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                <h2 className="font-heading text-lg font-bold text-slate-900">Generate Discount Code</h2>
                <button onClick={() => setShowCodeModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <form onSubmit={handleSaveCode} className="p-6 space-y-4">
                <div className="space-y-2">
                  <Label>Code (leave blank to auto-generate)</Label>
                  <Input
                    placeholder="e.g., FRIEND25"
                    value={codeForm.code}
                    onChange={(e) => setCodeForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Discount Type *</Label>
                    <select
                      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
                      value={codeForm.discount_type}
                      onChange={(e) => setCodeForm(prev => ({ ...prev, discount_type: e.target.value }))}
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount ($)</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Discount Value *</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="25"
                      value={codeForm.discount_value}
                      onChange={(e) => setCodeForm(prev => ({ ...prev, discount_value: e.target.value }))}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Usage Limit</Label>
                    <Input
                      type="number"
                      min="1"
                      placeholder="Unlimited"
                      value={codeForm.usage_limit}
                      onChange={(e) => setCodeForm(prev => ({ ...prev, usage_limit: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Expires On</Label>
                    <Input
                      type="date"
                      value={codeForm.expires_at}
                      onChange={(e) => setCodeForm(prev => ({ ...prev, expires_at: e.target.value }))}
                    />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!codeForm.once_per_customer}
                    onChange={(e) => setCodeForm(prev => ({ ...prev, once_per_customer: e.target.checked }))}
                    className="h-4 w-4 accent-teal-600"
                    data-testid="discount-code-once-per-customer"
                  />
                  One use per customer account
                </label>
                <div className="space-y-2">
                  <Label>Created For (note)</Label>
                  <Input
                    placeholder="e.g., John Doe - Birthday discount"
                    value={codeForm.created_for}
                    onChange={(e) => setCodeForm(prev => ({ ...prev, created_for: e.target.value }))}
                  />
                </div>
                <div className="p-3 bg-teal-50 rounded-lg border border-teal-200 space-y-2">
                  <p className="text-sm font-medium text-teal-800 flex items-center gap-1">
                    <Award className="h-4 w-4" /> Affiliate Tracking (optional)
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Affiliate Customer Name</Label>
                      <Input
                        placeholder="e.g., John Smith"
                        value={codeForm.assigned_customer_name || ''}
                        onChange={(e) => setCodeForm(prev => ({ ...prev, assigned_customer_name: e.target.value }))}
                        data-testid="code-affiliate-name"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Affiliate Customer Email</Label>
                      <Input
                        placeholder="e.g., john@email.com"
                        value={codeForm.assigned_customer_email || ''}
                        onChange={(e) => setCodeForm(prev => ({ ...prev, assigned_customer_email: e.target.value }))}
                        data-testid="code-affiliate-email"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-teal-600">Assign to track sales and earn points when customers use this code.</p>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowCodeModal(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white">
                    {loading ? 'Generating...' : 'Generate Code'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Invoice Resend Modal */}
        {showInvoiceModal && invoiceOrder && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-md w-full">
              <div className="p-4 sm:p-6 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h2 className="font-heading text-lg font-bold text-slate-900">Resend Invoice</h2>
                  <p className="text-sm text-slate-500">Order #{invoiceOrder.order_id}</p>
                </div>
                <button onClick={() => setShowInvoiceModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-4 sm:p-6 space-y-4">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-sm text-slate-600">Sending to:</p>
                  <p className="font-medium">{invoiceOrder.email || invoiceOrder.customer_email || 'Customer Email'}</p>
                </div>
                <div className="space-y-2">
                  <Label>CC Emails (comma-separated)</Label>
                  <Input
                    placeholder="email1@example.com, email2@example.com"
                    value={invoiceEmails.cc}
                    onChange={(e) => setInvoiceEmails(prev => ({ ...prev, cc: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>BCC Emails (comma-separated)</Label>
                  <Input
                    placeholder="email@example.com"
                    value={invoiceEmails.bcc}
                    onChange={(e) => setInvoiceEmails(prev => ({ ...prev, bcc: e.target.value }))}
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowInvoiceModal(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleResendInvoice} 
                    disabled={sendingInvoice} 
                    className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
                  >
                    {sendingInvoice ? 'Sending...' : 'Send Invoice'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Password Reset Modal */}
        {showPasswordResetModal && passwordResetCustomer && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-md w-full">
              <div className="p-4 sm:p-6 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h2 className="font-heading text-lg font-bold text-slate-900">Reset Customer Password</h2>
                  <p className="text-sm text-slate-500">{passwordResetCustomer.email}</p>
                </div>
                <button 
                  onClick={() => {
                    setShowPasswordResetModal(false);
                    setPasswordResetCustomer(null);
                    setNewCustomerPassword('');
                    setConfirmCustomerPassword('');
                  }} 
                  className="p-2 hover:bg-slate-100 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <form onSubmit={handleResetCustomerPassword} className="p-4 sm:p-6 space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                    <p className="text-sm text-amber-800">
                      {passwordResetCustomer.user_id 
                        ? <>This will change the password for <strong>{passwordResetCustomer.name || passwordResetCustomer.email}</strong>.</>
                        : <>No login account exists for <strong>{passwordResetCustomer.name || passwordResetCustomer.email}</strong>. This will create one and set the password.</>
                      }
                      {' '}Make sure to communicate the new password to the customer securely.
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="Enter new password"
                    value={newCustomerPassword}
                    onChange={(e) => setNewCustomerPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                  <p className="text-xs text-slate-500">Minimum 6 characters</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-new-password">Confirm New Password</Label>
                  <Input
                    id="confirm-new-password"
                    type="password"
                    placeholder="Confirm new password"
                    value={confirmCustomerPassword}
                    onChange={(e) => setConfirmCustomerPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => {
                      setShowPasswordResetModal(false);
                      setPasswordResetCustomer(null);
                      setNewCustomerPassword('');
                      setConfirmCustomerPassword('');
                    }} 
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit"
                    disabled={resettingPassword || newCustomerPassword.length < 6 || newCustomerPassword !== confirmCustomerPassword}
                    className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    {resettingPassword ? 'Processing...' : (passwordResetCustomer.user_id ? 'Reset Password' : 'Create Account & Set Password')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Status Change Confirmation Modal */}
        {statusModal && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="status-confirmation-modal">
            <div className="bg-white rounded-xl max-w-md w-full p-6">
              <h3 className="font-heading font-bold text-lg text-slate-900 mb-4">Confirm Status Change</h3>
              
              <div className="bg-slate-50 rounded-lg p-4 mb-4 space-y-2">
                <p className="text-sm text-slate-600">
                  <span className="font-medium">Order:</span> {statusModal.order_id}
                </p>
                <p className="text-sm text-slate-600">
                  <span className="font-medium">Customer:</span> {statusModal.customer_name}
                </p>
                <div className="flex items-center gap-2 text-sm">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${(STATUS_CONFIG[statusModal.current_status] || STATUS_CONFIG.pending).color}`}>
                    {(STATUS_CONFIG[statusModal.current_status] || STATUS_CONFIG.pending).label}
                  </span>
                  <ArrowUp className="h-3 w-3 text-slate-400 rotate-90" />
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${(STATUS_CONFIG[statusModal.new_status] || STATUS_CONFIG.pending).color}`}>
                    {(STATUS_CONFIG[statusModal.new_status] || STATUS_CONFIG.pending).label}
                  </span>
                </div>
              </div>
              
              <p className="text-sm text-slate-500 mb-4">
                The customer will be notified via email about this status change.
              </p>
              
              <div className="flex gap-3">
                <Button
                  onClick={() => setStatusModal(null)}
                  variant="outline"
                  className="flex-1"
                  disabled={updatingStatus}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleStatusChange}
                  disabled={updatingStatus}
                  className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
                  data-testid="confirm-status-change"
                >
                  {updatingStatus ? 'Updating...' : 'Confirm & Notify'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Sales Report Modal */}
        {showReportModal && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-xl max-w-4xl w-full my-8">
              <div className="p-4 sm:p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
                <div>
                  <h2 className="font-heading text-lg font-bold text-slate-900">Sales Report</h2>
                  <p className="text-sm text-slate-500">Generate and print sales data</p>
                </div>
                <button onClick={() => { setShowReportModal(false); setReportData(null); }} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-4 sm:p-6">
                {/* Date Range Selection */}
                <div className="flex flex-wrap gap-4 mb-6 print:hidden">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={reportDateRange.start}
                      onChange={(e) => setReportDateRange(prev => ({ ...prev, start: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={reportDateRange.end}
                      onChange={(e) => setReportDateRange(prev => ({ ...prev, end: e.target.value }))}
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <Button onClick={handleGenerateReport} disabled={loadingReport}>
                      {loadingReport ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Generate Report'}
                    </Button>
                    {reportData && (
                      <Button variant="outline" onClick={handlePrintReport}>
                        <Printer className="h-4 w-4 mr-2" />
                        Print
                      </Button>
                    )}
                  </div>
                </div>

                {/* Report Content */}
                {loadingReport ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="h-8 w-8 text-teal-600 animate-spin" />
                  </div>
                ) : reportData ? (
                  <div className="space-y-6 print:space-y-4" id="printable-report">
                    {/* Report Header */}
                    <div className="text-center border-b pb-4 print:pb-2">
                      <h1 className="text-2xl font-bold text-slate-900">Genora Peptides - Sales Report</h1>
                      <p className="text-slate-500">
                        {reportData.date_range.start} to {reportData.date_range.end}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">Generated: {new Date(reportData.generated_at).toLocaleString()}</p>
                    </div>

                    {/* Summary */}
                    <div className="grid grid-cols-3 gap-4 print:gap-2">
                      <div className="bg-teal-50 p-4 rounded-lg print:p-2">
                        <p className="text-sm text-teal-600">Total Revenue</p>
                        <p className="text-2xl font-bold text-teal-800">${reportData.summary.total_revenue.toLocaleString()}</p>
                      </div>
                      <div className="bg-blue-50 p-4 rounded-lg print:p-2">
                        <p className="text-sm text-blue-600">Total Orders</p>
                        <p className="text-2xl font-bold text-blue-800">{reportData.summary.total_orders}</p>
                      </div>
                      <div className="bg-purple-50 p-4 rounded-lg print:p-2">
                        <p className="text-sm text-purple-600">Avg Order Value</p>
                        <p className="text-2xl font-bold text-purple-800">${reportData.summary.average_order_value.toFixed(2)}</p>
                      </div>
                    </div>

                    {/* Payment Methods */}
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-2">Payment Methods</h3>
                      <div className="grid grid-cols-2 gap-4">
                        {Object.entries(reportData.payment_methods).map(([method, data]) => (
                          <div key={method} className="flex justify-between p-3 bg-slate-50 rounded">
                            <span className="capitalize">{method}</span>
                            <span className="font-semibold">{data.count} orders - ${data.revenue.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Top Products */}
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-2">Top Products</h3>
                      <table className="w-full text-sm">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="text-left py-2 px-3">#</th>
                            <th className="text-left py-2 px-3">Product</th>
                            <th className="text-right py-2 px-3">Qty Sold</th>
                            <th className="text-right py-2 px-3">Revenue</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.top_products.map((product, i) => (
                            <tr key={i} className="border-b">
                              <td className="py-2 px-3">{i + 1}</td>
                              <td className="py-2 px-3">{product.name}</td>
                              <td className="py-2 px-3 text-right">{product.quantity}</td>
                              <td className="py-2 px-3 text-right font-semibold">${product.revenue.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Order Details */}
                    {reportData.orders && reportData.orders.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-2">Order Details</h3>
                        <table className="w-full text-xs">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="text-left py-2 px-2">Order ID</th>
                              <th className="text-left py-2 px-2">Date</th>
                              <th className="text-left py-2 px-2">Customer</th>
                              <th className="text-center py-2 px-2">Items</th>
                              <th className="text-left py-2 px-2">Payment</th>
                              <th className="text-right py-2 px-2">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.orders.map((order) => (
                              <tr key={order.order_id} className="border-b">
                                <td className="py-1 px-2 font-mono">{order.order_id}</td>
                                <td className="py-1 px-2">{new Date(order.created_at).toLocaleDateString()}</td>
                                <td className="py-1 px-2">{order.customer_name}</td>
                                <td className="py-1 px-2 text-center">{order.items_count}</td>
                                <td className="py-1 px-2 capitalize">{order.payment_method}</td>
                                <td className="py-1 px-2 text-right font-semibold">${order.total.toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <Calendar className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                    <p>Select a date range and click "Generate Report"</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
